import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  FiLayout,
  FiArrowDownCircle,
  FiArrowUpCircle,
  FiUsers,
  FiUser,
  FiGrid,
  FiClipboard,
  FiLogOut,
  FiRefreshCw,
  FiPlus,
  FiCalendar,
  FiInbox,
  FiMenu,
  FiDownload,
  FiFileText,
  FiShield,
  FiUserCheck,
  FiDollarSign,
  FiChevronDown,
  FiCheckSquare,
  FiPercent,
  FiZap,
  FiSlash,
} from 'react-icons/fi';
import {
  useAuth,
  normalizedRoleId,
  ROLE_ADMIN,
  ROLE_OWNER,
  ROLE_FRONT_DESK,
  roleIdSetForUser,
} from '../../context/AuthContext';
import { api, downloadFrontDeskReceipt, formatApiErrorUserMessage, openPdfBlobInNewTab } from '../../api/client';
import { frontDeskApi, downloadFrontDeskReport, downloadFrontDeskPayrollSlip } from '../../api/frontDesk';
import { unwrapEntity } from '../../utils/apiPayload';
import {
  toISODate,
  fromISODate,
  stayNightsOverlapBlocked,
  isNightBlockedAsCheckIn,
  blockedIntervalsForPicker,
} from '../../utils/bookingCalendar';
import { formatLongDate } from '../../utils/formatLongDate';
import {
  getPricingBreakdown,
  cautionToneClassName,
  bookingHasCautionLine,
  applyFrontDeskCreatePricingOverrides,
} from '../../utils/bookingPricing';
import {
  formatNairaDecimalFromNumber,
  formatNairaDecimalInputChange,
  formatWholeNairaFromNumber,
  formatWholeNairaInputChange,
  parseNairaDecimalToNumber,
  parseWholeNairaToNumber,
} from '../../utils/moneyInputFormat';
import NotificationBell from '../../component/NotificationBell/NotificationBell';
import { BRAND_NAME, TAGLINE } from '../../constants/brand';
import { formatDisplayName } from '../../utils/formatDisplayName';
import { luxisttConfirm } from '../../utils/luxisttSwal';
import { FrontDeskUsersPanel } from './FrontDeskUsersPanel';
import { FrontDeskPermissionRolesPanel } from './FrontDeskPermissionRolesPanel';
import { FrontDeskCatalogPanel } from './FrontDeskCatalogPanel';
import { FdPerPageField, FdPaginationNav, normalizeFdPerPage } from '../../components/FdDataTableControls';
import { LightboxImage } from '../../components/LightboxImage';
import '../Dashboard/Dashboard.css';
import './FrontDesk.css';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/** Normalize API / form date to YYYY-MM-DD for calendar comparisons. */
function toCalendarIso(value) {
  if (value == null || value === '') return '';
  const s = typeof value === 'string' ? value : String(value);
  return s.slice(0, 10);
}

function isArrivalDayForOps(booking, opsDateIso) {
  return toCalendarIso(booking?.start_date) === toCalendarIso(opsDateIso);
}

function isDepartureDayForOps(booking, opsDateIso) {
  return toCalendarIso(booking?.end_date) === toCalendarIso(opsDateIso);
}

/** Show checkout when guest departs today and is confirmed or already checked in (API may implicit check-in on checkout). */
function canShowCheckOutOnOps(booking, opsDateIso) {
  if (!isDepartureDayForOps(booking, opsDateIso)) return false;
  return ['checked_in', 'confirmed'].includes(booking?.status?.value);
}

/** Check-in only after staff has confirmed the booking and recorded payment. */
function canShowCheckInOnOps(booking, opsDateIso) {
  return (
    isArrivalDayForOps(booking, opsDateIso) &&
    booking?.status?.value === 'confirmed' &&
    booking?.payment_status?.value === 'paid'
  );
}

function formatNgn(n) {
  if (n == null || n === '') return '—';
  try {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      maximumFractionDigits: 0,
    }).format(Number(n));
  } catch {
    return `₦${n}`;
  }
}

function renderFdBookingPricingCells(b) {
  const pb = getPricingBreakdown(b);
  const cn = cautionToneClassName(pb.tone, 'fd-booking-caution');
  const titleParts = [];
  if (pb.caution_original > 0) {
    titleParts.push(`Original caution ${formatNgn(pb.caution_original)}`);
  }
  if (pb.caution_refunded > 0) {
    titleParts.push(`Refunded ${formatNgn(pb.caution_refunded)}`);
  }
  if (pb.caution_deposit_forfeited > 0) {
    titleParts.push(`Deposit retained ${formatNgn(pb.caution_deposit_forfeited)}`);
  }
  if (pb.caution_damage_extra > 0) {
    titleParts.push(`Damage / extra ${formatNgn(pb.caution_damage_extra)}`);
  }
  const title = titleParts.length > 0 ? titleParts.join(' · ') : undefined;
  return (
    <>
      <td className="fd-col-right fd-td-subtotal">{formatNgn(pb.stay_subtotal)}</td>
      <td className={`fd-col-right ${cn}`} title={title}>
        {formatNgn(pb.caution_display)}
        {pb.caution_damage_extra > 0 ? <span className="fd-booking-caution-suffix"> · damage</span> : null}
      </td>
      <td className="fd-col-center fd-td-total">{formatNgn(pb.total)}</td>
    </>
  );
}

function formatDateTime(iso) {
  if (iso == null || iso === '') return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

function canFrontDeskEditBooking(b) {
  const st = b?.status?.value;
  return st && !['cancelled', 'checked_out'].includes(st);
}

function canFrontDeskCancelBooking(b) {
  const st = b?.status?.value;
  if (!st || st === 'cancelled') return false;
  if (['checked_in', 'checked_out'].includes(st)) return false;
  if (b?.payment_status?.value === 'paid') return false;
  return true;
}

/** Property id string for staff-ability checks (matches /me staff_abilities_by_property keys). */
function fdBookingPropertyKey(b, fallbackPropertyId) {
  const raw = b?.apartment?.property_id ?? b?.apartment?.property?.id ?? fallbackPropertyId;
  if (raw == null || raw === '') return null;
  return String(raw);
}

function canFdCancelBookingStaff(canStaffAbilityForFrontDesk, b, fallbackPropertyId) {
  if (!canFrontDeskCancelBooking(b)) return false;
  const key = fdBookingPropertyKey(b, fallbackPropertyId);
  return (
    canStaffAbilityForFrontDesk(key, 'booking.delete') || canStaffAbilityForFrontDesk(key, 'booking.cancel')
  );
}

function formatSurchargeFeeLabel(s) {
  if (!s) return '';
  if (s.billing_kind === 'fixed' && s.amount != null) return formatNgn(s.amount);
  if (s.billing_kind === 'percent' && s.percent != null) return `${s.percent}% of room`;
  return '';
}

function surchargeWaivesGuestCapacity(s) {
  if (!s) return false;
  if (s.waives_guest_capacity) return true;
  const n = String(s.name || '').toLowerCase();
  const t = String(s.type_name || '').toLowerCase();
  return n.includes('party') || t.includes('party');
}

function StatusBadge({ ui }) {
  if (!ui) return null;
  const c = ui.color || 'gray';
  return <span className={`fd-status fd-status--${c}`}>{ui.label}</span>;
}

function StatTile({ value, label, icon: Icon, hint, variant }) {
  let display = value ?? '—';
  if (variant === 'money') {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      display = formatNgn(value);
    } else {
      display = '—';
    }
  }
  return (
    <div className={`fd-stat-card${variant === 'money' ? ' fd-stat-card--money' : ''}`}>
      {Icon ? (
        <div className="fd-stat-card-icon" aria-hidden>
          <Icon />
        </div>
      ) : null}
      <strong>{display}</strong>
      <span className="fd-stat-card-label">{label}</span>
      {hint ? <span className="fd-stat-card-hint">{hint}</span> : null}
    </div>
  );
}

function EmptyTableRow({ colSpan, message }) {
  return (
    <tr>
      <td colSpan={colSpan} className="fd-table-empty">
        <FiInbox className="fd-empty-icon" aria-hidden />
        <span>{message}</span>
      </td>
    </tr>
  );
}

function guestLabel(b) {
  return b.guest_name || b.registered_user?.name || '—';
}

function unitLabel(b) {
  const apt = b.apartment;
  if (!apt) return '—';
  const p = apt.property?.name;
  return p ? `${p} · ${apt.name}` : apt.name;
}

function canOfferGuestRefund(b) {
  return (
    b &&
    b.status?.value === 'confirmed' &&
    b.payment_status?.value === 'paid' &&
    !b.guest_refunded_at
  );
}

function canFrontDeskAdjustCautionFees(b) {
  if (!b || b.guest_refunded_at) return false;
  if (b.status?.value === 'cancelled') return false;
  if (b.payment_status?.value === 'paid') return true;
  const cash = String(b.payment_method || '').toLowerCase() === 'cash';
  const st = b.status?.value;
  return cash && (st === 'confirmed' || st === 'checked_in' || st === 'checked_out');
}

function canStaffMarkPaidOrCaution(bookingKey, canStaffFn) {
  return canStaffFn(bookingKey, 'booking.mark_paid') || canStaffFn(bookingKey, 'booking.caution_adjust');
}

/**
 * Room rack: extra line only when it adds detail the status badge does not cover
 * (e.g. Confirmed + “Not checked in”). For checked_out / checked_in / cancelled the badge already matches.
 */
function rackCheckInSummary(booking) {
  if (!booking) return '';
  const st = booking.status?.value;
  if (st === 'checked_out' || st === 'checked_in' || st === 'cancelled') return '';
  if (booking.checked_in_at) return 'In house';
  if (st === 'confirmed' || st === 'pending') return 'Not checked in';
  return '—';
}

const BOOKING_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'checked_in', label: 'In house' },
  { value: 'checked_out', label: 'Checked out' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PAYMENT_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All payments' },
  { value: 'pending', label: 'Pending' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'refunded', label: 'Refunded' },
];

/** New booking modal — matches Filament defaults (confirmed + pending) with sensible desk-only status choices. */
const CREATE_BOOKING_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'checked_in', label: 'Checked in' },
];

const CREATE_PAYMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
];

const FINANCE_NAV_ABILITIES = [
  'hr.payroll_view',
  'hr.payroll_create',
  'hr.payroll_delete',
  'hr.payroll_manage',
  'finance.expenses_view',
  'finance.expenses_create',
  'finance.expenses_update',
  'finance.expenses_delete',
  'finance.expenses_manage',
  'finance.purchases_view',
  'finance.purchases_create',
  'finance.purchases_update',
  'finance.purchases_delete',
  'finance.purchases_manage',
];

/** Property staff roster / PII: HR abilities only (not desk.team). */
const STAFF_ROSTER_DETAIL_ABILITIES = [
  'hr.staff_view',
  'hr.staff_user_create',
  'hr.staff_record_create',
  'hr.staff_create',
  'hr.staff_manage',
  'hr.staff_update',
  'hr.staff_delete',
  'hr.kyc_review',
  'hr.property_owner_assign',
];

/** Users nav — same as roster detail (who may see staff directory). */
const STAFF_ROSTER_NAV_ABILITIES = STAFF_ROSTER_DETAIL_ABILITIES;

/** Roles & permissions screen — anyone who can view or change assignments. */
const STAFF_ROLES_NAV_ABILITIES = [
  'hr.staff_view',
  'hr.staff_user_create',
  'hr.staff_record_create',
  'hr.staff_update',
  'hr.staff_create',
  'hr.staff_manage',
  'hr.staff_delete',
  'hr.property_owner_assign',
];

const CATALOG_DISCOUNT_NAV_ABILITIES = ['catalog.discounts_view', 'catalog.discounts_manage'];

const CATALOG_SURCHARGE_NAV_ABILITIES = ['catalog.surcharges_view', 'catalog.surcharges_manage'];

const CATALOG_BLOCKED_NAV_ABILITIES = ['catalog.blocked_dates_view', 'catalog.blocked_dates_manage'];

/** Mirrors API {@see \App\Services\StaffPropertyAccess::mayManageStaffPermissionRoleTemplates} for SPA controls. */
function canManageStaffPermissionTemplates(user, hasPermission, canStaffAbilityForFrontDeskFn) {
  if (!user) return false;
  const rid = normalizedRoleId(user);
  if (rid === ROLE_ADMIN || user?.role === 'Admin') return true;
  if (hasPermission('staff-manage') || hasPermission('manage-users')) return true;
  if (hasPermission('create_staff::permission::role')) return true;
  const map = user.staff_abilities_by_property;
  if (map && typeof map === 'object' && !Array.isArray(map)) {
    const grant = ['hr.staff_update', 'hr.staff_create', 'hr.staff_delete'];
    return Object.values(map).some((list) => Array.isArray(list) && grant.some((g) => list.includes(g)));
  }
  if (
    user?.role === 'Front Desk' ||
    rid === ROLE_FRONT_DESK ||
    rid === ROLE_OWNER ||
    user?.role === 'Owner'
  ) {
    return true;
  }
  return false;
}

function canFdStaffRosterDetail(pid, canStaffAbilityForFrontDeskFn) {
  return STAFF_ROSTER_DETAIL_ABILITIES.some((a) => canStaffAbilityForFrontDeskFn(pid, a));
}

const REPORTS_NAV_ABILITIES = ['desk.reports_view', 'desk.reports_export', 'desk.reports'];

const PAYROLL_MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

function formatPayrollPeriodLabel(row) {
  const m = Number(row?.period_month);
  const y = row?.period_year ?? '';
  if (m >= 1 && m <= 12) {
    return `${PAYROLL_MONTH_OPTIONS[m - 1].label} ${y}`;
  }
  return y !== '' && y != null ? `${m} / ${y}` : '—';
}

const FD_KYC_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'verified', label: 'Verified' },
  { value: 'rejected', label: 'Rejected' },
];

const VIEWS_WITH_NEW_BOOKING_TOOLBAR = new Set([
  'overview',
  'arrivals',
  'departures',
  'inhouse',
  'rack',
  'bookings',
]);

const FRONT_DESK_NAV = [
  { id: 'overview', label: 'Overview', icon: FiLayout, ability: 'desk.overview' },
  { id: 'arrivals', label: 'Arrivals', icon: FiArrowDownCircle, ability: 'desk.overview' },
  { id: 'departures', label: 'Departures', icon: FiArrowUpCircle, ability: 'desk.overview' },
  { id: 'inhouse', label: 'In house', icon: FiUsers, ability: 'desk.overview' },
  { id: 'rack', label: 'Room rack', icon: FiGrid, ability: 'desk.rack' },
  { id: 'bookings', label: 'All bookings', icon: FiClipboard, ability: 'booking.view' },
  { id: 'users', label: 'Users', icon: FiUserCheck, anyOf: STAFF_ROSTER_NAV_ABILITIES },
  {
    id: 'staff-roles',
    label: 'Roles & permissions',
    icon: FiCheckSquare,
    anyOf: STAFF_ROLES_NAV_ABILITIES,
  },
  {
    id: 'catalog-discounts',
    label: 'Discounts',
    icon: FiPercent,
    anyOf: CATALOG_DISCOUNT_NAV_ABILITIES,
  },
  {
    id: 'catalog-surcharges',
    label: 'Surcharges',
    icon: FiZap,
    anyOf: CATALOG_SURCHARGE_NAV_ABILITIES,
  },
  {
    id: 'catalog-blocked-dates',
    label: 'Block dates',
    icon: FiSlash,
    anyOf: CATALOG_BLOCKED_NAV_ABILITIES,
  },
  {
    id: 'finance',
    label: 'Finance & HR',
    icon: FiDollarSign,
    anyOf: FINANCE_NAV_ABILITIES,
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: FiFileText,
    anyOf: REPORTS_NAV_ABILITIES,
  },
  { id: 'audit', label: 'Audit', icon: FiShield, ability: 'desk.audit' },
];

function frontDeskNavVisible(canStaffAbilityForFrontDesk, propertyId, item) {
  const pid = propertyId || null;
  if (item.anyOf) {
    return item.anyOf.some((a) => canStaffAbilityForFrontDesk(pid, a));
  }
  return canStaffAbilityForFrontDesk(pid, item.ability);
}

export default function FrontDesk() {
  const { user, logout, canStaffAbilityForFrontDesk, canAccessProfilePage, refreshUser, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('overview');
  const [propertyId, setPropertyId] = useState('');
  const [date, setDate] = useState(todayISO);
  const [properties, setProperties] = useState([]);
  const [bookingPlatforms, setBookingPlatforms] = useState([]);
  const [propertiesReady, setPropertiesReady] = useState(false);
  const [summary, setSummary] = useState(null);
  const [arrivals, setArrivals] = useState([]);
  const [departures, setDepartures] = useState([]);
  const [inHouse, setInHouse] = useState([]);
  const [rack, setRack] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [bookingsMeta, setBookingsMeta] = useState(null);
  const [bookingsPage, setBookingsPage] = useState(1);
  const [bookingsQDraft, setBookingsQDraft] = useState('');
  const [bookingsQ, setBookingsQ] = useState('');
  const [bookingsStatusFilter, setBookingsStatusFilter] = useState('');
  const [bookingsPaymentFilter, setBookingsPaymentFilter] = useState('');
  const [bookingsApartmentId, setBookingsApartmentId] = useState('');
  const [bookingsOnDate, setBookingsOnDate] = useState('');
  const [bookingsPerPage, setBookingsPerPage] = useState(20);
  const [bookingsApartmentOptions, setBookingsApartmentOptions] = useState([]);
  const [catalogRefreshTick, setCatalogRefreshTick] = useState(0);
  const [reportsMeta, setReportsMeta] = useState(null);
  const [reportType, setReportType] = useState('occupancy');
  const [reportFrom, setReportFrom] = useState(todayISO);
  const [reportTo, setReportTo] = useState(todayISO);
  const [reportApartmentId, setReportApartmentId] = useState('');
  const [reportPreviewLoading, setReportPreviewLoading] = useState(false);
  const [reportPreview, setReportPreview] = useState(null);
  const [activityRows, setActivityRows] = useState([]);
  const [activityMeta, setActivityMeta] = useState(null);
  const [activityPage, setActivityPage] = useState(1);
  const [activityPerPage, setActivityPerPage] = useState(20);
  const [activityQDraft, setActivityQDraft] = useState('');
  const [activityQ, setActivityQ] = useState('');
  const [activityActorDraft, setActivityActorDraft] = useState('');
  const [activityActor, setActivityActor] = useState('');
  const [activitySubjectType, setActivitySubjectType] = useState('');
  const [activitySubjectId, setActivitySubjectId] = useState('');
  const [activityDateFrom, setActivityDateFrom] = useState('');
  const [activityDateTo, setActivityDateTo] = useState('');
  const [activityLoading, setActivityLoading] = useState(false);
  const [teamRows, setTeamRows] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  /** Full active staff list for payroll assignment dropdown (users table stays paginated). */
  const [payrollStaffPickerRows, setPayrollStaffPickerRows] = useState([]);
  const [financeTab, setFinanceTab] = useState('payroll');
  const [fdPayrollRows, setFdPayrollRows] = useState([]);
  const [fdPayrollMeta, setFdPayrollMeta] = useState(null);
  const [fdPayrollPage, setFdPayrollPage] = useState(1);
  const [fdFinancePerPage, setFdFinancePerPage] = useState(25);
  const [fdExpenseRows, setFdExpenseRows] = useState([]);
  const [fdExpenseMeta, setFdExpenseMeta] = useState(null);
  const [fdExpensePage, setFdExpensePage] = useState(1);
  const [fdPurchaseRows, setFdPurchaseRows] = useState([]);
  const [fdPurchaseMeta, setFdPurchaseMeta] = useState(null);
  const [fdPurchasePage, setFdPurchasePage] = useState(1);
  const [fdPayrollFilterQDraft, setFdPayrollFilterQDraft] = useState('');
  const [fdPayrollFilterQ, setFdPayrollFilterQ] = useState('');
  const [fdPayrollFilterYear, setFdPayrollFilterYear] = useState('');
  const [fdPayrollFilterMonth, setFdPayrollFilterMonth] = useState('');
  const [fdPayrollFilterAssignmentId, setFdPayrollFilterAssignmentId] = useState('');
  const [fdExpenseFilterQDraft, setFdExpenseFilterQDraft] = useState('');
  const [fdExpenseFilterQ, setFdExpenseFilterQ] = useState('');
  const [fdExpenseFilterTypeId, setFdExpenseFilterTypeId] = useState('');
  const [fdExpenseFilterDateFrom, setFdExpenseFilterDateFrom] = useState('');
  const [fdExpenseFilterDateTo, setFdExpenseFilterDateTo] = useState('');
  const [fdPurchaseFilterQDraft, setFdPurchaseFilterQDraft] = useState('');
  const [fdPurchaseFilterQ, setFdPurchaseFilterQ] = useState('');
  const [fdPurchaseFilterTypeId, setFdPurchaseFilterTypeId] = useState('');
  const [fdPurchaseFilterDateFrom, setFdPurchaseFilterDateFrom] = useState('');
  const [fdPurchaseFilterDateTo, setFdPurchaseFilterDateTo] = useState('');
  const [fdExpenseTypes, setFdExpenseTypes] = useState([]);
  const [fdPurchaseTypes, setFdPurchaseTypes] = useState([]);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [fdSaving, setFdSaving] = useState(false);
  const [fdPayrollSlipLoadingId, setFdPayrollSlipLoadingId] = useState(null);
  const [payrollFormFd, setPayrollFormFd] = useState({
    property_staff_assignment_id: '',
    period_year: new Date().getFullYear(),
    period_month: new Date().getMonth() + 1,
    gross_amount_ngn: '',
    deductions_ngn: '0',
    paid_at: '',
    payment_reference: '',
    slip_notes: '',
  });
  const [expenseFormFd, setExpenseFormFd] = useState({
    amount: '',
    expense_type_id: '',
    expense_date: todayISO(),
    description: '',
    apartment_id: '',
  });
  const [purchaseFormFd, setPurchaseFormFd] = useState({
    item_name: '',
    amount: '',
    quantity: '1',
    purchase_date: todayISO(),
    purchase_type_id: '',
    vendor: '',
    description: '',
    apartment_id: '',
  });
  const [expenseEditDraft, setExpenseEditDraft] = useState(null);
  const [purchaseEditDraft, setPurchaseEditDraft] = useState(null);
  const [staffMeta, setStaffMeta] = useState(null);
  const [staffPage, setStaffPage] = useState(1);
  const [staffPerPage, setStaffPerPage] = useState(20);
  const [staffQDraft, setStaffQDraft] = useState('');
  const [staffQ, setStaffQ] = useState('');
  const [staffKycFilter, setStaffKycFilter] = useState('');
  const [staffIncludeInactive, setStaffIncludeInactive] = useState(false);
  const [kycEditAssignment, setKycEditAssignment] = useState(null);
  const [kycFormFd, setKycFormFd] = useState({
    kyc_status: 'pending',
    kyc_notes: '',
    government_id_number: '',
    next_of_kin_name: '',
    next_of_kin_phone: '',
    next_of_kin_relationship: '',
    father_name: '',
    mother_name: '',
  });
  const [kycGovIdFileFd, setKycGovIdFileFd] = useState(null);
  const [kycSaveBusy, setKycSaveBusy] = useState(false);
  const [headerProfileOpen, setHeaderProfileOpen] = useState(false);
  const desktopHeaderToolsRef = useRef(null);
  const mobileHeaderToolsRef = useRef(null);
  const [reportExportBusy, setReportExportBusy] = useState(null);
  const [modalEditing, setModalEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  /** Booking id while a row action (mark paid, checkout, PDF) is in flight */
  const [rowActionBusy, setRowActionBusy] = useState(null);
  /**
   * Row fetch for details / edit / check-in — separate `kind` so Edit loading does not spin Details.
   * @type {React.State<{ id: number, kind: 'details'|'edit'|'checkin' } | null>}
   */
  const [rowPrefetchBusy, setRowPrefetchBusy] = useState(null);
  const [modal, setModal] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBlockedDates, setCreateBlockedDates] = useState([]);
  const [createPriceQuote, setCreatePriceQuote] = useState(null);
  const [createSelectedSurchargeIds, setCreateSelectedSurchargeIds] = useState([]);
  const [createSurchargeOverrides, setCreateSurchargeOverrides] = useState({});
  const [editPriceQuote, setEditPriceQuote] = useState(null);
  const [editSelectedSurchargeIds, setEditSelectedSurchargeIds] = useState([]);
  const [editSurchargeOverrides, setEditSurchargeOverrides] = useState({});
  const [createGuestIdFile, setCreateGuestIdFile] = useState(null);
  /** Saved on guest profile (from autocomplete). */
  const [createIdProfilePreview, setCreateIdProfilePreview] = useState(null);
  /** Local file preview (blob URL; revoke on replace / close). */
  const [createIdFilePreview, setCreateIdFilePreview] = useState(null);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkInIdNumber, setCheckInIdNumber] = useState('');
  const [checkInIdFile, setCheckInIdFile] = useState(null);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [checkOutBooking, setCheckOutBooking] = useState(null);
  const [checkOutNote, setCheckOutNote] = useState('');
  const [guestRefundOpen, setGuestRefundOpen] = useState(false);
  const [guestRefundAmount, setGuestRefundAmount] = useState('');
  const [guestRefundNote, setGuestRefundNote] = useState('');
  const [cautionModalBooking, setCautionModalBooking] = useState(null);
  const [cautionModalKind, setCautionModalKind] = useState('refund');
  const [cautionModalAmount, setCautionModalAmount] = useState('');
  const [cautionModalNote, setCautionModalNote] = useState('');
  const [createDateWarning, setCreateDateWarning] = useState('');
  const [createCheckoutWarning, setCreateCheckoutWarning] = useState('');
  const [createBookingPropertyId, setCreateBookingPropertyId] = useState('');
  const [createApartments, setCreateApartments] = useState([]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    apartment_id: '',
    start_date: '',
    end_date: '',
    guests_adults: 2,
    guests_children: 0,
    guest_user_id: '',
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    guest_id_number: '',
    status: 'confirmed',
    payment_status: 'pending',
    notes: '',
    payment_method: 'cash',
    booking_platform_id: '',
    total_price_manual: '',
    discount_amount_manual: '',
  });
  const [guestSuggestions, setGuestSuggestions] = useState([]);
  /** When set, email field still matches this guest from the picker (lowercase). */
  const [guestLinkedEmailNorm, setGuestLinkedEmailNorm] = useState('');
  const guestSearchTimerRef = useRef(null);
  const guestSearchAbortRef = useRef(null);

  const defaultBookingPlatformId = useMemo(() => {
    const w = bookingPlatforms.find((p) => p.slug === 'walk_in');
    if (w) return String(w.id);
    return bookingPlatforms[0] ? String(bookingPlatforms[0].id) : '';
  }, [bookingPlatforms]);

  const isFdNavItemAllowed = useCallback(
    (item) => frontDeskNavVisible(canStaffAbilityForFrontDesk, propertyId || null, item),
    [canStaffAbilityForFrontDesk, propertyId],
  );

  /** Sidebar shows only items the user may open for the current property scope (no disabled/teaser links). */
  const fdNavItemsVisible = useMemo(
    () => FRONT_DESK_NAV.filter((item) => isFdNavItemAllowed(item)),
    [isFdNavItemAllowed],
  );

  const canManagePermissionTemplates = useMemo(
    () => canManageStaffPermissionTemplates(user, hasPermission, canStaffAbilityForFrontDesk),
    [user, hasPermission, canStaffAbilityForFrontDesk],
  );

  /** Matches API {@see \App\Support\PlatformAdmin::is} for template scope (global vs per-property). */
  const isPlatformAdminUser = useMemo(() => {
    if (!user) return false;
    if (roleIdSetForUser(user).has(ROLE_ADMIN) || user?.role === 'Admin') return true;
    const roles = Array.isArray(user.roles) ? user.roles : [];
    return roles.some((r) => String(r).toLowerCase() === 'super_admin');
  }, [user]);

  const allowedFinanceTabs = useMemo(() => {
    const pid = propertyId || null;
    const tabs = [];
    if (
      canStaffAbilityForFrontDesk(pid, 'hr.payroll_view') ||
      canStaffAbilityForFrontDesk(pid, 'hr.payroll_create') ||
      canStaffAbilityForFrontDesk(pid, 'hr.payroll_delete') ||
      canStaffAbilityForFrontDesk(pid, 'hr.payroll_manage')
    ) {
      tabs.push('payroll');
    }
    if (
      canStaffAbilityForFrontDesk(pid, 'finance.expenses_view') ||
      canStaffAbilityForFrontDesk(pid, 'finance.expenses_create') ||
      canStaffAbilityForFrontDesk(pid, 'finance.expenses_update') ||
      canStaffAbilityForFrontDesk(pid, 'finance.expenses_delete') ||
      canStaffAbilityForFrontDesk(pid, 'finance.expenses_manage')
    ) {
      tabs.push('expenses');
    }
    if (
      canStaffAbilityForFrontDesk(pid, 'finance.purchases_view') ||
      canStaffAbilityForFrontDesk(pid, 'finance.purchases_create') ||
      canStaffAbilityForFrontDesk(pid, 'finance.purchases_update') ||
      canStaffAbilityForFrontDesk(pid, 'finance.purchases_delete') ||
      canStaffAbilityForFrontDesk(pid, 'finance.purchases_manage')
    ) {
      tabs.push('purchases');
    }
    return tabs;
  }, [propertyId, canStaffAbilityForFrontDesk]);

  const canCreateBookingFd = canStaffAbilityForFrontDesk(propertyId || null, 'booking.create');
  const showNewBookingInToolbar = VIEWS_WITH_NEW_BOOKING_TOOLBAR.has(view);
  const pidFd = propertyId || null;
  const canPayrollCreateFd = Boolean(propertyId) && canStaffAbilityForFrontDesk(pidFd, 'hr.payroll_create');
  const canPayrollDeleteFd = Boolean(propertyId) && canStaffAbilityForFrontDesk(pidFd, 'hr.payroll_delete');
  const canPayrollViewFd =
    Boolean(propertyId) &&
    (canStaffAbilityForFrontDesk(pidFd, 'hr.payroll_view') ||
      canStaffAbilityForFrontDesk(pidFd, 'hr.payroll_manage'));
  const canPayrollSlipFd =
    Boolean(propertyId) &&
    (canPayrollViewFd ||
      canPayrollCreateFd ||
      canPayrollDeleteFd ||
      canStaffAbilityForFrontDesk(pidFd, 'hr.payroll_manage'));
  const showPayrollRowActionsFd = canPayrollSlipFd;
  const canExpenseCreateFd = Boolean(propertyId) && canStaffAbilityForFrontDesk(pidFd, 'finance.expenses_create');
  const canExpenseUpdateFd = Boolean(propertyId) && canStaffAbilityForFrontDesk(pidFd, 'finance.expenses_update');
  const canExpenseDeleteFd = Boolean(propertyId) && canStaffAbilityForFrontDesk(pidFd, 'finance.expenses_delete');
  const canPurchaseCreateFd = Boolean(propertyId) && canStaffAbilityForFrontDesk(pidFd, 'finance.purchases_create');
  const canPurchaseUpdateFd = Boolean(propertyId) && canStaffAbilityForFrontDesk(pidFd, 'finance.purchases_update');
  const canPurchaseDeleteFd = Boolean(propertyId) && canStaffAbilityForFrontDesk(pidFd, 'finance.purchases_delete');
  const canStaffUserCreateFd =
    Boolean(propertyId) &&
    (canStaffAbilityForFrontDesk(pidFd, 'hr.staff_user_create') ||
      canStaffAbilityForFrontDesk(pidFd, 'hr.staff_create') ||
      canStaffAbilityForFrontDesk(pidFd, 'hr.staff_manage'));
  const canStaffRecordCreateFd =
    Boolean(propertyId) &&
    (canStaffAbilityForFrontDesk(pidFd, 'hr.staff_record_create') ||
      canStaffAbilityForFrontDesk(pidFd, 'hr.staff_create') ||
      canStaffAbilityForFrontDesk(pidFd, 'hr.staff_manage'));
  const canStaffCreateFd = canStaffUserCreateFd || canStaffRecordCreateFd;
  const canStaffUpdateFd = Boolean(propertyId) && canStaffAbilityForFrontDesk(pidFd, 'hr.staff_update');
  const canStaffDeleteFd = Boolean(propertyId) && canStaffAbilityForFrontDesk(pidFd, 'hr.staff_delete');
  const showKycReviewFd = Boolean(propertyId) && canStaffAbilityForFrontDesk(pidFd, 'hr.kyc_review');
  const canPropertyOwnerAssignFd =
    Boolean(propertyId) && canStaffAbilityForFrontDesk(pidFd, 'hr.property_owner_assign');
  const canReportsViewFd =
    canStaffAbilityForFrontDesk(pidFd, 'desk.reports_view') ||
    canStaffAbilityForFrontDesk(pidFd, 'desk.reports');
  const canReportsExportFd =
    canStaffAbilityForFrontDesk(pidFd, 'desk.reports_export') ||
    canStaffAbilityForFrontDesk(pidFd, 'desk.reports');
  const showExpenseRowActionsFd = canExpenseUpdateFd || canExpenseDeleteFd;
  const showPurchaseRowActionsFd = canPurchaseUpdateFd || canPurchaseDeleteFd;

  const todayStart = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
  }, []);

  const minCheckoutCreate = useMemo(() => {
    if (!createForm.start_date) return todayStart;
    const s = fromISODate(createForm.start_date);
    if (!s) return todayStart;
    return new Date(s.getFullYear(), s.getMonth(), s.getDate() + 1, 0, 0, 0);
  }, [createForm.start_date, todayStart]);

  const createUnavailableIntervals = useMemo(
    () => blockedIntervalsForPicker(createBlockedDates),
    [createBlockedDates],
  );

  const isDateBlockedCreate = useCallback(
    (date) => isNightBlockedAsCheckIn(date, createBlockedDates, todayStart),
    [createBlockedDates, todayStart],
  );

  const canSelectStartCreate = useCallback(
    (date) => {
      const day0 = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
      return day0 >= todayStart && !isNightBlockedAsCheckIn(date, createBlockedDates, todayStart);
    },
    [todayStart, createBlockedDates],
  );

  const canSelectEndCreate = useCallback(
    (date) => {
      if (!createForm.start_date) return false;
      const start = fromISODate(createForm.start_date);
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
      if (!start || d <= start) return false;
      return !stayNightsOverlapBlocked(createForm.start_date, toISODate(date), createBlockedDates);
    },
    [createForm.start_date, createBlockedDates],
  );

  const renderDayContentsCreate = useCallback(
    (dayOfMonth, date) => {
      if (!date || !isDateBlockedCreate(date)) return dayOfMonth;
      return (
        <span className="book-day-content book-day-content-blocked">
          <span>{dayOfMonth}</span>
          <span className="book-day-slash" aria-hidden="true" />
        </span>
      );
    },
    [isDateBlockedCreate],
  );

  const createNightCount = useMemo(() => {
    if (!createForm.start_date || !createForm.end_date || createForm.end_date <= createForm.start_date) return 0;
    const s = fromISODate(createForm.start_date);
    const e = fromISODate(createForm.end_date);
    if (!s || !e) return 0;
    return Math.max(0, Math.round((e.getTime() - s.getTime()) / 86400000));
  }, [createForm.start_date, createForm.end_date]);

  const createDiscountOverrideParsed = useMemo(() => {
    const m = createForm.discount_amount_manual;
    if (m === '' || m == null) return null;
    const p = parseWholeNairaToNumber(m);
    return Number.isFinite(p) && p >= 0 ? p : null;
  }, [createForm.discount_amount_manual]);

  const createSurchargeOverrideAmountMap = useMemo(() => {
    const out = {};
    Object.entries(createSurchargeOverrides).forEach(([id, value]) => {
      if (value === '' || value == null) return;
      const p = parseWholeNairaToNumber(value);
      if (Number.isFinite(p) && p >= 0) {
        out[id] = p;
      }
    });
    return out;
  }, [createSurchargeOverrides]);

  const createPricingOverridePreview = useMemo(() => {
    if (!createPriceQuote) return null;
    const hasDisc = createDiscountOverrideParsed !== null;
    const hasSurchargeOverride = Object.keys(createSurchargeOverrideAmountMap).length > 0;
    if (!hasDisc && !hasSurchargeOverride) return null;
    return applyFrontDeskCreatePricingOverrides(createPriceQuote, {
      discountAmountOverride: hasDisc ? createDiscountOverrideParsed : undefined,
      surchargeAmountOverrides: createSurchargeOverrideAmountMap,
    });
  }, [createPriceQuote, createDiscountOverrideParsed, createSurchargeOverrideAmountMap]);

  const createOverridePreviewInvalid = Boolean(
    createPricingOverridePreview && createPricingOverridePreview.ok === false,
  );

  const createDisplayTotal = useMemo(() => {
    const m = createForm.total_price_manual;
    const parsed = parseWholeNairaToNumber(m);
    if (m !== '' && m != null && Number.isFinite(parsed) && parsed >= 0) return parsed;
    if (createPricingOverridePreview?.ok) return createPricingOverridePreview.total;
    if (createPriceQuote?.total != null && Number(createPriceQuote.total) >= 0) return Number(createPriceQuote.total);
    return 0;
  }, [createForm.total_price_manual, createPriceQuote, createPricingOverridePreview]);

  const createDisplaySubtotal = useMemo(() => {
    if (createPricingOverridePreview?.ok) return createPricingOverridePreview.subtotal;
    return createPriceQuote?.subtotal != null ? Number(createPriceQuote.subtotal) : null;
  }, [createPriceQuote, createPricingOverridePreview]);

  const createStayAfterDiscount = useMemo(() => {
    if (createPricingOverridePreview?.ok) return createPricingOverridePreview.stay_total_after_discount;
    return createPriceQuote?.stay_total_after_discount != null
      ? Number(createPriceQuote.stay_total_after_discount)
      : null;
  }, [createPriceQuote, createPricingOverridePreview]);

  const createDisplaySurchargeLines = useMemo(() => {
    if (createPricingOverridePreview?.ok) return createPricingOverridePreview.surcharge_lines;
    return Array.isArray(createPriceQuote?.surcharge_lines) ? createPriceQuote.surcharge_lines : [];
  }, [createPriceQuote, createPricingOverridePreview]);

  const createHasDiscount = useMemo(() => {
    if (createPricingOverridePreview?.ok) {
      return (
        createPricingOverridePreview.discount_amount > 0 &&
        createPricingOverridePreview.subtotal > createPricingOverridePreview.stay_total_after_discount
      );
    }
    return (
      Boolean(createPriceQuote?.applied_discount) &&
      createDisplaySubtotal != null &&
      createStayAfterDiscount != null &&
      createDisplaySubtotal > createStayAfterDiscount
    );
  }, [createPriceQuote, createPricingOverridePreview, createDisplaySubtotal, createStayAfterDiscount]);

  const createAppliedDiscountLabel = useMemo(() => {
    if (createPricingOverridePreview?.ok && createPricingOverridePreview.applied_discount?.name) {
      return createPricingOverridePreview.applied_discount.name;
    }
    return createPriceQuote?.applied_discount?.name || null;
  }, [createPriceQuote, createPricingOverridePreview]);

  const editDiscountOverrideParsed = useMemo(() => {
    const m = editForm?.discount_amount_manual;
    if (m === '' || m == null) return null;
    const p = parseWholeNairaToNumber(m);
    return Number.isFinite(p) && p >= 0 ? p : null;
  }, [editForm?.discount_amount_manual]);

  const editSurchargeOverrideAmountMap = useMemo(() => {
    const out = {};
    Object.entries(editSurchargeOverrides).forEach(([id, value]) => {
      if (value === '' || value == null) return;
      const p = parseWholeNairaToNumber(value);
      if (Number.isFinite(p) && p >= 0) {
        out[id] = p;
      }
    });
    return out;
  }, [editSurchargeOverrides]);

  const editPricingOverridePreview = useMemo(() => {
    if (!editPriceQuote) return null;
    const hasDisc = editDiscountOverrideParsed !== null;
    const hasSurchargeOverride = Object.keys(editSurchargeOverrideAmountMap).length > 0;
    if (!hasDisc && !hasSurchargeOverride) return null;
    return applyFrontDeskCreatePricingOverrides(editPriceQuote, {
      discountAmountOverride: hasDisc ? editDiscountOverrideParsed : undefined,
      surchargeAmountOverrides: editSurchargeOverrideAmountMap,
    });
  }, [editPriceQuote, editDiscountOverrideParsed, editSurchargeOverrideAmountMap]);

  const editOverridePreviewInvalid = Boolean(
    editPricingOverridePreview && editPricingOverridePreview.ok === false,
  );

  const editDisplayTotal = useMemo(() => {
    const m = editForm?.total_price_manual;
    const parsed = parseWholeNairaToNumber(m);
    if (m !== '' && m != null && Number.isFinite(parsed) && parsed >= 0) return parsed;
    if (editPricingOverridePreview?.ok) return editPricingOverridePreview.total;
    if (editPriceQuote?.total != null && Number(editPriceQuote.total) >= 0) return Number(editPriceQuote.total);
    return 0;
  }, [editForm?.total_price_manual, editPriceQuote, editPricingOverridePreview]);

  const editDisplaySubtotal = useMemo(() => {
    if (editPricingOverridePreview?.ok) return editPricingOverridePreview.subtotal;
    return editPriceQuote?.subtotal != null ? Number(editPriceQuote.subtotal) : null;
  }, [editPriceQuote, editPricingOverridePreview]);

  const editStayAfterDiscount = useMemo(() => {
    if (editPricingOverridePreview?.ok) return editPricingOverridePreview.stay_total_after_discount;
    return editPriceQuote?.stay_total_after_discount != null
      ? Number(editPriceQuote.stay_total_after_discount)
      : null;
  }, [editPriceQuote, editPricingOverridePreview]);

  const editDisplaySurchargeLines = useMemo(() => {
    if (editPricingOverridePreview?.ok) return editPricingOverridePreview.surcharge_lines;
    return Array.isArray(editPriceQuote?.surcharge_lines) ? editPriceQuote.surcharge_lines : [];
  }, [editPriceQuote, editPricingOverridePreview]);

  const editHasDiscount = useMemo(() => {
    if (editPricingOverridePreview?.ok) {
      return (
        editPricingOverridePreview.discount_amount > 0 &&
        editPricingOverridePreview.subtotal > editPricingOverridePreview.stay_total_after_discount
      );
    }
    return (
      Boolean(editPriceQuote?.applied_discount) &&
      editDisplaySubtotal != null &&
      editStayAfterDiscount != null &&
      editDisplaySubtotal > editStayAfterDiscount
    );
  }, [editPriceQuote, editPricingOverridePreview, editDisplaySubtotal, editStayAfterDiscount]);

  const editAppliedDiscountLabel = useMemo(() => {
    if (editPricingOverridePreview?.ok && editPricingOverridePreview.applied_discount?.name) {
      return editPricingOverridePreview.applied_discount.name;
    }
    return editPriceQuote?.applied_discount?.name || null;
  }, [editPriceQuote, editPricingOverridePreview]);

  const editMandatorySurcharges = useMemo(
    () =>
      Array.isArray(editPriceQuote?.available_surcharges)
        ? editPriceQuote.available_surcharges.filter((s) => s.is_mandatory)
        : [],
    [editPriceQuote],
  );
  const editOptionalSurcharges = useMemo(
    () =>
      Array.isArray(editPriceQuote?.available_surcharges)
        ? editPriceQuote.available_surcharges.filter((s) => !s.is_mandatory)
        : [],
    [editPriceQuote],
  );

  const editGuestCapacityWaivedByPartyFee = useMemo(() => {
    const list = editPriceQuote?.available_surcharges;
    if (!Array.isArray(list)) return false;
    return editSelectedSurchargeIds.some((id) => {
      const s = list.find((x) => Number(x.id) === Number(id));
      return Boolean(s && surchargeWaivesGuestCapacity(s));
    });
  }, [editPriceQuote, editSelectedSurchargeIds]);

  const editMaxAdultsInput = editGuestCapacityWaivedByPartyFee
    ? 50
    : Math.max(1, Number(modal?.apartment?.capacity_adults ?? 20));
  const editMaxChildrenInput = editGuestCapacityWaivedByPartyFee
    ? 50
    : Math.max(0, Number(modal?.apartment?.capacity_children ?? 20));

  const createMandatorySurcharges = useMemo(
    () =>
      Array.isArray(createPriceQuote?.available_surcharges)
        ? createPriceQuote.available_surcharges.filter((s) => s.is_mandatory)
        : [],
    [createPriceQuote],
  );
  const createOptionalSurcharges = useMemo(
    () =>
      Array.isArray(createPriceQuote?.available_surcharges)
        ? createPriceQuote.available_surcharges.filter((s) => !s.is_mandatory)
        : [],
    [createPriceQuote],
  );

  const createSelectedApartment = useMemo(
    () => createApartments.find((a) => String(a.id) === String(createForm.apartment_id)),
    [createApartments, createForm.apartment_id],
  );

  const createGuestCapacityWaivedByPartyFee = useMemo(() => {
    const list = createPriceQuote?.available_surcharges;
    if (!Array.isArray(list)) return false;
    return createSelectedSurchargeIds.some((id) => {
      const s = list.find((x) => Number(x.id) === Number(id));
      return Boolean(s && surchargeWaivesGuestCapacity(s));
    });
  }, [createPriceQuote, createSelectedSurchargeIds]);

  const createMaxAdultsInput = createGuestCapacityWaivedByPartyFee
    ? 50
    : Math.max(1, Number(createSelectedApartment?.capacity_adults ?? 20));
  const createMaxChildrenInput = createGuestCapacityWaivedByPartyFee
    ? 50
    : Math.max(0, Number(createSelectedApartment?.capacity_children ?? 20));

  const toggleCreateSurcharge = useCallback((id) => {
    setCreateSelectedSurchargeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const toggleEditSurcharge = useCallback((id) => {
    setEditSelectedSurchargeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  useEffect(() => {
    if (!createOpen || !createForm.apartment_id) {
      setCreateBlockedDates([]);
      return;
    }
    let cancelled = false;
    api
      .get(`/apartments/${createForm.apartment_id}/blocked-dates`, undefined)
      .then((r) => {
        if (!cancelled) setCreateBlockedDates(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (!cancelled) setCreateBlockedDates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [createOpen, createForm.apartment_id]);

  useEffect(() => {
    if (!createOpen || createBlockedDates.length === 0) return;
    setCreateForm((prev) => {
      if (!prev.start_date) return prev;
      const startDate = fromISODate(prev.start_date);
      if (!startDate) return prev;
      if (isNightBlockedAsCheckIn(startDate, createBlockedDates, todayStart)) {
        setCreateDateWarning('That check-in date is unavailable — please pick another.');
        return { ...prev, start_date: '', end_date: '' };
      }
      return prev;
    });
  }, [createOpen, createBlockedDates, todayStart]);

  useEffect(() => {
    if (!createOpen || !createBookingPropertyId) {
      setCreateApartments([]);
      return;
    }
    let cancelled = false;
    frontDeskApi
      .apartments(createBookingPropertyId)
      .then((res) => {
        if (!cancelled) setCreateApartments(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setCreateApartments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [createOpen, createBookingPropertyId]);

  useEffect(() => {
    if (
      !createOpen ||
      !createForm.apartment_id ||
      !createForm.start_date ||
      !createForm.end_date ||
      createForm.end_date <= createForm.start_date
    ) {
      setCreatePriceQuote(null);
      return;
    }
    let cancelled = false;
    const params = new URLSearchParams({
      start_date: createForm.start_date,
      end_date: createForm.end_date,
    });
    createSelectedSurchargeIds.forEach((id) => params.append('surcharge_ids[]', String(id)));
    const qs = `?${params.toString()}`;
    api
      .get(`/apartments/${createForm.apartment_id}${qs}`)
      .then((res) => {
        const apt = unwrapEntity(res) || res.data || res;
        if (!cancelled) setCreatePriceQuote(apt?.price_quote || null);
      })
      .catch(() => {
        if (!cancelled) setCreatePriceQuote(null);
      });
    return () => {
      cancelled = true;
    };
  }, [createOpen, createForm.apartment_id, createForm.start_date, createForm.end_date, createSelectedSurchargeIds]);

  useEffect(() => {
    const available = createPriceQuote?.available_surcharges;
    if (!Array.isArray(available) || available.length === 0) {
      return;
    }
    const allowed = new Set(available.filter((s) => !s.is_mandatory).map((s) => s.id));
    setCreateSelectedSurchargeIds((prev) => prev.filter((id) => allowed.has(id)));
  }, [createPriceQuote]);

  useEffect(() => {
    const lines = Array.isArray(createPriceQuote?.surcharge_lines) ? createPriceQuote.surcharge_lines : [];
    const allowed = new Set(lines.map((line) => String(line.id)));
    setCreateSurchargeOverrides((prev) => {
      const next = {};
      Object.entries(prev).forEach(([id, value]) => {
        if (allowed.has(String(id))) {
          next[id] = value;
        }
      });
      return next;
    });
  }, [createPriceQuote]);

  useEffect(() => {
    if (!modalEditing || !modal?.apartment?.id) {
      setEditPriceQuote(null);
      return;
    }
    const start = editForm?.start_date;
    const end = editForm?.end_date;
    if (!start || !end || end <= start) {
      setEditPriceQuote(null);
      return;
    }
    let cancelled = false;
    const params = new URLSearchParams({
      start_date: start,
      end_date: end,
    });
    editSelectedSurchargeIds.forEach((id) => params.append('surcharge_ids[]', String(id)));
    api
      .get(`/apartments/${modal.apartment.id}?${params.toString()}`)
      .then((res) => {
        const apt = unwrapEntity(res) || res.data || res;
        if (!cancelled) setEditPriceQuote(apt?.price_quote || null);
      })
      .catch(() => {
        if (!cancelled) setEditPriceQuote(null);
      });
    return () => {
      cancelled = true;
    };
  }, [modalEditing, modal?.apartment?.id, editForm?.start_date, editForm?.end_date, editSelectedSurchargeIds]);

  useEffect(() => {
    const lines = Array.isArray(editPriceQuote?.surcharge_lines) ? editPriceQuote.surcharge_lines : [];
    const allowed = new Set(lines.map((line) => String(line.id)));
    setEditSurchargeOverrides((prev) => {
      const next = {};
      Object.entries(prev).forEach(([id, value]) => {
        if (allowed.has(String(id))) {
          next[id] = value;
        }
      });
      return next;
    });
  }, [editPriceQuote]);

  useEffect(() => {
    if (createForm.start_date && createForm.end_date && createForm.end_date <= createForm.start_date) {
      setCreateCheckoutWarning('Check-out must be after check-in.');
    } else {
      setCreateCheckoutWarning('');
    }
  }, [createForm.start_date, createForm.end_date]);

  useEffect(() => {
    if (
      !createForm.start_date ||
      !createForm.end_date ||
      createForm.end_date <= createForm.start_date ||
      createBlockedDates.length === 0
    ) {
      setCreateDateWarning('');
      return;
    }
    if (stayNightsOverlapBlocked(createForm.start_date, createForm.end_date, createBlockedDates)) {
      setCreateDateWarning('This stay overlaps a booked or blocked period.');
    } else {
      setCreateDateWarning('');
    }
  }, [createForm.start_date, createForm.end_date, createBlockedDates]);

  const handleCreateStartChange = (date) => {
    if (date && isNightBlockedAsCheckIn(date, createBlockedDates, todayStart)) {
      setCreateDateWarning('That check-in date is unavailable.');
      return;
    }
    setCreateDateWarning('');
    const start = toISODate(date);
    setCreateForm((prev) => {
      const next = { ...prev, start_date: start };
      if (next.end_date && next.end_date <= start) next.end_date = '';
      return next;
    });
  };

  const handleCreateEndChange = (date) => {
    if (!date || !createForm.start_date) {
      setCreateForm((prev) => ({ ...prev, end_date: date ? toISODate(date) : '' }));
      return;
    }
    const checkoutISO = toISODate(date);
    if (stayNightsOverlapBlocked(createForm.start_date, checkoutISO, createBlockedDates)) {
      setCreateDateWarning('That stay overlaps a booked or blocked period.');
      return;
    }
    setCreateDateWarning('');
    setCreateForm((prev) => ({ ...prev, end_date: checkoutISO }));
  };

  const dateParams = useCallback(
    () => ({
      date,
      ...(propertyId ? { property_id: propertyId } : {}),
    }),
    [date, propertyId],
  );

  const loadProperties = useCallback(async () => {
    try {
      const [res, bpRaw] = await Promise.all([
        frontDeskApi.properties(),
        frontDeskApi.bookingPlatforms().catch(() => null),
      ]);
      const fromApi = bpRaw && Array.isArray(bpRaw.data) ? bpRaw.data : [];
      const fromMe = Array.isArray(user?.booking_platforms) ? user.booking_platforms : [];
      setBookingPlatforms(fromApi.length > 0 ? fromApi : fromMe);
      const list = Array.isArray(res?.data) ? res.data : [];
      if (list.length > 0) {
        setProperties(list);
        return;
      }
      const fallback = user?.front_desk_properties;
      if (Array.isArray(fallback) && fallback.length > 0) {
        setProperties(fallback);
        return;
      }
      setProperties([]);
    } catch (e) {
      const fallback = user?.front_desk_properties;
      if (Array.isArray(fallback) && fallback.length > 0) {
        setProperties(fallback);
        return;
      }
      setProperties([]);
      setError((prev) => (prev ? prev : e.message || 'Could not load property list'));
    } finally {
      setPropertiesReady(true);
    }
  }, [user?.front_desk_properties, user?.booking_platforms]);

  useEffect(() => {
    if (Array.isArray(user?.booking_platforms) && user.booking_platforms.length > 0) {
      setBookingPlatforms(user.booking_platforms);
    }
  }, [user?.booking_platforms]);

  const loadSummary = useCallback(async () => {
    try {
      const s = await frontDeskApi.summary(dateParams());
      setSummary(s.data);
    } catch {
      /* keep previous summary if refresh fails */
    }
  }, [dateParams]);

  const loadOperational = useCallback(async () => {
    const scopeKey = propertyId || null;
    if (!canStaffAbilityForFrontDesk(scopeKey, 'desk.overview')) {
      setError('');
      setSummary(null);
      setArrivals([]);
      setDepartures([]);
      setInHouse([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const p = dateParams();
      const [s, a, d, h] = await Promise.all([
        frontDeskApi.summary(p),
        frontDeskApi.arrivals(p),
        frontDeskApi.departures(p),
        frontDeskApi.inHouse(p),
      ]);
      setSummary(s.data);
      setArrivals(Array.isArray(a.data) ? a.data : []);
      setDepartures(Array.isArray(d.data) ? d.data : []);
      setInHouse(Array.isArray(h.data) ? h.data : []);
    } catch (e) {
      setError(e.message || 'Could not load data');
    } finally {
      setLoading(false);
    }
  }, [dateParams, propertyId, canStaffAbilityForFrontDesk]);

  const loadRack = useCallback(async () => {
    if (!propertyId) {
      setError('Choose a property to view the room rack.');
      setRack(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const from = date;
      const t = new Date(date);
      t.setDate(t.getDate() + 6);
      const to = t.toISOString().slice(0, 10);
      const res = await frontDeskApi.roomRack({ property_id: propertyId, from, to });
      setRack(res.data);
    } catch (e) {
      setError(e.message || 'Could not load room rack');
      setRack(null);
    } finally {
      setLoading(false);
    }
  }, [date, propertyId]);

  const loadBookings = useCallback(async () => {
    if (!propertyId) {
      setError('');
      setBookings([]);
      setBookingsMeta(null);
      setLoading(false);
      return;
    }
    if (!canStaffAbilityForFrontDesk(propertyId || null, 'booking.view')) {
      setError('');
      setBookings([]);
      setBookingsMeta(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const p = {
        page: bookingsPage,
        per_page: normalizeFdPerPage(bookingsPerPage, 100),
        ...(propertyId ? { property_id: propertyId } : {}),
        ...(bookingsQ ? { q: bookingsQ } : {}),
        ...(bookingsStatusFilter ? { status: bookingsStatusFilter } : {}),
        ...(bookingsPaymentFilter ? { payment_status: bookingsPaymentFilter } : {}),
        ...(bookingsApartmentId ? { apartment_id: Number(bookingsApartmentId) } : {}),
        ...(bookingsOnDate ? { on_date: bookingsOnDate } : {}),
      };
      const res = await frontDeskApi.bookings(p);
      setBookings(Array.isArray(res.data) ? res.data : []);
      const meta = res.meta || null;
      setBookingsMeta(meta);
      if (meta?.current_page != null) {
        setBookingsPage((prev) => (meta.current_page !== prev ? meta.current_page : prev));
      }
    } catch (e) {
      setError(e.message || 'Could not load bookings');
    } finally {
      setLoading(false);
    }
  }, [
    propertyId,
    bookingsPage,
    bookingsPerPage,
    bookingsQ,
    bookingsStatusFilter,
    bookingsPaymentFilter,
    bookingsApartmentId,
    bookingsOnDate,
    canStaffAbilityForFrontDesk,
  ]);

  const loadReportsMeta = useCallback(async () => {
    try {
      const res = await frontDeskApi.reportsMeta();
      setReportsMeta(res.data ?? null);
    } catch {
      /* keep prior meta */
    }
  }, []);

  const loadReportPreview = useCallback(async () => {
    setReportPreviewLoading(true);
    setError('');
    try {
      const reportFinanceParams = {
        date_from: reportFrom,
        date_to: reportTo,
        ...(reportApartmentId ? { apartment_id: Number(reportApartmentId) } : {}),
      };
      const fetchAllFinanceRows = async (fetchPage, params) => {
        const out = [];
        let page = 1;
        let lastPage = 1;
        do {
          // eslint-disable-next-line no-await-in-loop
          const res = await fetchPage({ ...params, page, per_page: 100 });
          out.push(...(Array.isArray(res.data) ? res.data : []));
          lastPage = Math.max(1, Number(res.meta?.last_page || 1));
          page += 1;
        } while (page <= lastPage);
        return out;
      };
      const summarizeExpensesRows = (rows) => {
        const bucket = new Map();
        rows.forEach((row) => {
          const type = row?.expense_type?.name || row?.category || row?.type || 'Other';
          const amount = Number(row?.amount || 0);
          const prev = bucket.get(type) || { expense_type: type, entry_count: 0, total_amount_ngn: 0 };
          prev.entry_count += 1;
          prev.total_amount_ngn += Number.isFinite(amount) ? amount : 0;
          bucket.set(type, prev);
        });
        return [...bucket.values()].map((row) => ({
          ...row,
          total_amount_ngn: Math.round((row.total_amount_ngn + Number.EPSILON) * 100) / 100,
        }));
      };
      const summarizePurchasesRows = (rows) => {
        const bucket = new Map();
        rows.forEach((row) => {
          const type = row?.purchase_type?.name || row?.type || 'Other';
          const amount = Number(row?.amount || 0);
          const quantity = Number(row?.quantity || 0);
          const lineTotal = (Number.isFinite(amount) ? amount : 0) * (Number.isFinite(quantity) ? quantity : 0);
          const prev = bucket.get(type) || { purchase_type: type, entry_count: 0, total_amount_ngn: 0 };
          prev.entry_count += 1;
          prev.total_amount_ngn += lineTotal;
          bucket.set(type, prev);
        });
        return [...bucket.values()].map((row) => ({
          ...row,
          total_amount_ngn: Math.round((row.total_amount_ngn + Number.EPSILON) * 100) / 100,
        }));
      };
      const p = {
        from: reportFrom,
        to: reportTo,
        ...(propertyId ? { property_id: propertyId } : {}),
        ...(reportApartmentId ? { apartment_id: reportApartmentId } : {}),
      };
      if (propertyId && reportType === 'expenses_summary') {
        const rows = await fetchAllFinanceRows((params) => frontDeskApi.expenses(propertyId, params), reportFinanceParams);
        setReportPreview({
          from: reportFrom,
          to: reportTo,
          rows: summarizeExpensesRows(rows),
        });
      } else if (propertyId && reportType === 'purchases_summary') {
        const rows = await fetchAllFinanceRows((params) => frontDeskApi.purchases(propertyId, params), reportFinanceParams);
        setReportPreview({
          from: reportFrom,
          to: reportTo,
          rows: summarizePurchasesRows(rows),
        });
      } else if (propertyId && reportType === 'profit_and_loss') {
        const [baseRes, expenseRows, purchaseRows] = await Promise.all([
          frontDeskApi.reportJson(reportType, p),
          fetchAllFinanceRows((params) => frontDeskApi.expenses(propertyId, params), reportFinanceParams),
          fetchAllFinanceRows((params) => frontDeskApi.purchases(propertyId, params), reportFinanceParams),
        ]);
        const base = baseRes?.data ?? null;
        const expensesByType = summarizeExpensesRows(expenseRows);
        const purchasesByType = summarizePurchasesRows(purchaseRows);
        const expensesTotal = expensesByType.reduce((sum, row) => sum + Number(row.total_amount_ngn || 0), 0);
        const purchasesTotal = purchasesByType.reduce((sum, row) => sum + Number(row.total_amount_ngn || 0), 0);
        const totalCosts = expensesTotal + purchasesTotal;
        const revenue = Number(base?.revenue_ngn || 0);
        setReportPreview({
          ...(base || {}),
          from: reportFrom,
          to: reportTo,
          expenses_total_ngn: Math.round((expensesTotal + Number.EPSILON) * 100) / 100,
          purchases_total_ngn: Math.round((purchasesTotal + Number.EPSILON) * 100) / 100,
          total_costs_ngn: Math.round((totalCosts + Number.EPSILON) * 100) / 100,
          net_income_ngn: Math.round((revenue - totalCosts + Number.EPSILON) * 100) / 100,
          expenses_by_type: expensesByType,
          purchases_by_type: purchasesByType,
        });
      } else {
        const res = await frontDeskApi.reportJson(reportType, p);
        setReportPreview(res.data ?? null);
      }
    } catch (e) {
      setReportPreview(null);
      setError(e.message || 'Could not load report');
    } finally {
      setReportPreviewLoading(false);
    }
  }, [reportType, reportFrom, reportTo, propertyId, reportApartmentId]);

  const loadActivity = useCallback(async () => {
    setActivityLoading(true);
    setError('');
    try {
      const params = {
        page: activityPage,
        per_page: normalizeFdPerPage(activityPerPage, 100),
        ...(activityQ ? { q: activityQ } : {}),
        ...(activityActor ? { actor: activityActor } : {}),
        ...(activitySubjectType.trim() ? { subject_type: activitySubjectType.trim() } : {}),
        ...(activitySubjectId.trim() !== '' && !Number.isNaN(Number(activitySubjectId))
          ? { subject_id: Number(activitySubjectId) }
          : {}),
        ...(activityDateFrom ? { created_from: activityDateFrom } : {}),
        ...(activityDateTo ? { created_to: activityDateTo } : {}),
      };
      const res = await frontDeskApi.activityLog(params);
      setActivityRows(Array.isArray(res.data) ? res.data : []);
      setActivityMeta(res.meta || null);
    } catch (e) {
      setError(e.message || 'Could not load activity log');
    } finally {
      setActivityLoading(false);
    }
  }, [
    activityPage,
    activityPerPage,
    activityQ,
    activityActor,
    activitySubjectType,
    activitySubjectId,
    activityDateFrom,
    activityDateTo,
  ]);

  const loadTeamList = useCallback(async () => {
    if (!propertyId) {
      setTeamRows([]);
      setStaffMeta(null);
      return;
    }
    const pid = propertyId || null;
    const rosterAllowed = canFdStaffRosterDetail(pid, canStaffAbilityForFrontDesk);
    if (!rosterAllowed) {
      setTeamRows([]);
      setStaffMeta(null);
      return;
    }
    setTeamLoading(true);
    setError('');
    try {
      const res = await frontDeskApi.team(propertyId, {
        page: staffPage,
        per_page: normalizeFdPerPage(staffPerPage, 250),
        ...(staffQ ? { q: staffQ } : {}),
        ...(staffKycFilter ? { kyc_status: staffKycFilter } : {}),
        ...(staffIncludeInactive ? { all: 1 } : {}),
      });
      setTeamRows(Array.isArray(res.data) ? res.data : []);
      setStaffMeta(res.meta || null);
    } catch (e) {
      setError(e.message || 'Could not load staff');
      setTeamRows([]);
      setStaffMeta(null);
    } finally {
      setTeamLoading(false);
    }
  }, [
    propertyId,
    staffPage,
    staffPerPage,
    staffQ,
    staffKycFilter,
    staffIncludeInactive,
    canStaffAbilityForFrontDesk,
  ]);

  const loadPayrollStaffPicker = useCallback(async () => {
    if (!propertyId) {
      setPayrollStaffPickerRows([]);
      return;
    }
    const pid = propertyId;
    const payrollTabAllowed =
      canStaffAbilityForFrontDesk(pid, 'hr.payroll_view') ||
      canStaffAbilityForFrontDesk(pid, 'hr.payroll_create') ||
      canStaffAbilityForFrontDesk(pid, 'hr.payroll_delete') ||
      canStaffAbilityForFrontDesk(pid, 'hr.payroll_manage');
    if (!payrollTabAllowed) {
      setPayrollStaffPickerRows([]);
      return;
    }
    const rosterAllowed = canFdStaffRosterDetail(pid, canStaffAbilityForFrontDesk);
    if (!rosterAllowed) {
      setPayrollStaffPickerRows([]);
      return;
    }
    try {
      const res = await frontDeskApi.team(propertyId, { page: 1, per_page: 250 });
      setPayrollStaffPickerRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      setPayrollStaffPickerRows([]);
    }
  }, [propertyId, canStaffAbilityForFrontDesk]);

  useEffect(() => {
    if (view === 'users' || view === 'finance') {
      loadTeamList();
    }
  }, [view, loadTeamList]);

  useEffect(() => {
    if (view !== 'finance' || financeTab !== 'payroll' || !propertyId) return;
    loadPayrollStaffPicker();
  }, [view, financeTab, propertyId, loadPayrollStaffPicker]);

  useEffect(() => {
    const t = setTimeout(() => setStaffQ(staffQDraft.trim()), 400);
    return () => clearTimeout(t);
  }, [staffQDraft]);

  useEffect(() => {
    const t = setTimeout(() => setActivityQ(activityQDraft.trim()), 400);
    return () => clearTimeout(t);
  }, [activityQDraft]);

  useEffect(() => {
    const t = setTimeout(() => setActivityActor(activityActorDraft.trim()), 400);
    return () => clearTimeout(t);
  }, [activityActorDraft]);

  useEffect(() => {
    setActivityPage(1);
  }, [
    activityPerPage,
    activityQ,
    activityActor,
    activitySubjectType,
    activitySubjectId,
    activityDateFrom,
    activityDateTo,
  ]);

  useEffect(() => {
    setStaffPage(1);
  }, [propertyId, staffQ, staffKycFilter, staffIncludeInactive, staffPerPage]);

  useEffect(() => {
    setBookingsPage(1);
  }, [bookingsPerPage]);

  useLayoutEffect(() => {
    if (fdNavItemsVisible.length === 0) return;
    const current = FRONT_DESK_NAV.find((n) => n.id === view);
    if (!current || !isFdNavItemAllowed(current)) {
      setView(fdNavItemsVisible[0].id);
    }
  }, [view, fdNavItemsVisible, isFdNavItemAllowed]);

  useEffect(() => {
    if (view !== 'finance') return;
    if (allowedFinanceTabs.length && !allowedFinanceTabs.includes(financeTab)) {
      setFinanceTab(allowedFinanceTabs[0]);
    }
  }, [view, financeTab, allowedFinanceTabs]);

  const fdPayrollListParams = useMemo(() => {
    const p = { page: fdPayrollPage, per_page: normalizeFdPerPage(fdFinancePerPage, 100) };
    const q = fdPayrollFilterQ.trim();
    if (q) p.q = q;
    if (fdPayrollFilterYear !== '' && fdPayrollFilterYear != null) {
      const y = Number(fdPayrollFilterYear);
      if (!Number.isNaN(y)) p.period_year = y;
    }
    if (fdPayrollFilterMonth !== '' && fdPayrollFilterMonth != null) {
      const m = Number(fdPayrollFilterMonth);
      if (!Number.isNaN(m)) p.period_month = m;
    }
    if (fdPayrollFilterAssignmentId) {
      p.assignment_id = Number(fdPayrollFilterAssignmentId);
    }
    return p;
  }, [
    fdPayrollPage,
    fdFinancePerPage,
    fdPayrollFilterQ,
    fdPayrollFilterYear,
    fdPayrollFilterMonth,
    fdPayrollFilterAssignmentId,
  ]);

  const fdExpenseListParams = useMemo(() => {
    const p = { page: fdExpensePage, per_page: normalizeFdPerPage(fdFinancePerPage, 100) };
    const q = fdExpenseFilterQ.trim();
    if (q) p.q = q;
    if (fdExpenseFilterTypeId) p.expense_type_id = Number(fdExpenseFilterTypeId);
    if (fdExpenseFilterDateFrom) p.date_from = fdExpenseFilterDateFrom;
    if (fdExpenseFilterDateTo) p.date_to = fdExpenseFilterDateTo;
    return p;
  }, [
    fdExpensePage,
    fdFinancePerPage,
    fdExpenseFilterQ,
    fdExpenseFilterTypeId,
    fdExpenseFilterDateFrom,
    fdExpenseFilterDateTo,
  ]);

  const fdPurchaseListParams = useMemo(() => {
    const p = { page: fdPurchasePage, per_page: normalizeFdPerPage(fdFinancePerPage, 100) };
    const q = fdPurchaseFilterQ.trim();
    if (q) p.q = q;
    if (fdPurchaseFilterTypeId) p.purchase_type_id = Number(fdPurchaseFilterTypeId);
    if (fdPurchaseFilterDateFrom) p.date_from = fdPurchaseFilterDateFrom;
    if (fdPurchaseFilterDateTo) p.date_to = fdPurchaseFilterDateTo;
    return p;
  }, [
    fdPurchasePage,
    fdFinancePerPage,
    fdPurchaseFilterQ,
    fdPurchaseFilterTypeId,
    fdPurchaseFilterDateFrom,
    fdPurchaseFilterDateTo,
  ]);

  useEffect(() => {
    const t = setTimeout(() => setFdPayrollFilterQ(fdPayrollFilterQDraft.trim()), 400);
    return () => clearTimeout(t);
  }, [fdPayrollFilterQDraft]);

  useEffect(() => {
    const t = setTimeout(() => setFdExpenseFilterQ(fdExpenseFilterQDraft.trim()), 400);
    return () => clearTimeout(t);
  }, [fdExpenseFilterQDraft]);

  useEffect(() => {
    const t = setTimeout(() => setFdPurchaseFilterQ(fdPurchaseFilterQDraft.trim()), 400);
    return () => clearTimeout(t);
  }, [fdPurchaseFilterQDraft]);

  useEffect(() => {
    setFdPayrollPage(1);
  }, [fdPayrollFilterQ, fdPayrollFilterYear, fdPayrollFilterMonth, fdPayrollFilterAssignmentId]);

  useEffect(() => {
    setFdExpensePage(1);
  }, [fdExpenseFilterQ, fdExpenseFilterTypeId, fdExpenseFilterDateFrom, fdExpenseFilterDateTo]);

  useEffect(() => {
    setFdPurchasePage(1);
  }, [fdPurchaseFilterQ, fdPurchaseFilterTypeId, fdPurchaseFilterDateFrom, fdPurchaseFilterDateTo]);

  useEffect(() => {
    if (view !== 'finance' || !propertyId) return undefined;
    let cancelled = false;
    (async () => {
      setFinanceLoading(true);
      setError('');
      try {
        if (financeTab === 'payroll') {
          const res = await frontDeskApi.payroll(propertyId, fdPayrollListParams);
          if (!cancelled) {
            setFdPayrollRows(Array.isArray(res.data) ? res.data : []);
            setFdPayrollMeta(res.meta || null);
          }
        } else if (financeTab === 'expenses') {
          const [typesRes, expRes] = await Promise.all([
            frontDeskApi.expenseTypes(),
            frontDeskApi.expenses(propertyId, fdExpenseListParams),
          ]);
          if (!cancelled) {
            setFdExpenseTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
            setFdExpenseRows(Array.isArray(expRes.data) ? expRes.data : []);
            setFdExpenseMeta(expRes.meta || null);
          }
        } else if (financeTab === 'purchases') {
          const [typesRes, purRes] = await Promise.all([
            frontDeskApi.purchaseTypes(),
            frontDeskApi.purchases(propertyId, fdPurchaseListParams),
          ]);
          if (!cancelled) {
            setFdPurchaseTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
            setFdPurchaseRows(Array.isArray(purRes.data) ? purRes.data : []);
            setFdPurchaseMeta(purRes.meta || null);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load finance data');
      } finally {
        if (!cancelled) setFinanceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    view,
    propertyId,
    financeTab,
    fdPayrollListParams,
    fdExpenseListParams,
    fdPurchaseListParams,
  ]);

  useEffect(() => {
    setFdPayrollPage(1);
    setFdExpensePage(1);
    setFdPurchasePage(1);
  }, [propertyId, financeTab]);

  useEffect(() => {
    setFdPayrollPage(1);
    setFdExpensePage(1);
    setFdPurchasePage(1);
  }, [fdFinancePerPage]);

  useEffect(() => {
    const t = setTimeout(() => {
      setBookingsQ(bookingsQDraft.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [bookingsQDraft]);

  useEffect(() => {
    if (!kycEditAssignment) return;
    const u = kycEditAssignment.user || {};
    setKycGovIdFileFd(null);
    setKycFormFd({
      kyc_status: kycEditAssignment.kyc_status || 'pending',
      kyc_notes: kycEditAssignment.kyc_notes ?? '',
      government_id_number: u.government_id_number ?? '',
      next_of_kin_name: u.next_of_kin_name ?? '',
      next_of_kin_phone: u.next_of_kin_phone ?? '',
      next_of_kin_relationship: u.next_of_kin_relationship ?? '',
      father_name: u.father_name ?? '',
      mother_name: u.mother_name ?? '',
    });
  }, [kycEditAssignment]);

  useEffect(() => {
    if (!headerProfileOpen) return undefined;
    const onDoc = (e) => {
      if (
        desktopHeaderToolsRef.current?.contains(e.target) ||
        mobileHeaderToolsRef.current?.contains(e.target)
      ) {
        return;
      }
      setHeaderProfileOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [headerProfileOpen]);

  useEffect(() => {
    setBookingsApartmentId('');
    setReportApartmentId('');
  }, [propertyId]);

  useEffect(() => {
    setBookingsPage(1);
  }, [propertyId, bookingsQ, bookingsStatusFilter, bookingsPaymentFilter, bookingsApartmentId, bookingsOnDate]);

  useEffect(() => {
    if (
      !propertyId ||
      (view !== 'bookings' &&
        view !== 'reports' &&
        view !== 'finance' &&
        view !== 'users' &&
        view !== 'catalog-discounts' &&
        view !== 'catalog-surcharges' &&
        view !== 'catalog-blocked-dates')
    ) {
      setBookingsApartmentOptions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await frontDeskApi.apartments(propertyId);
        const arr = Array.isArray(res.data) ? res.data : [];
        if (!cancelled) setBookingsApartmentOptions(arr);
      } catch {
        if (!cancelled) setBookingsApartmentOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [view, propertyId]);

  useEffect(() => {
    loadProperties().catch(() => {});
  }, [loadProperties]);

  /** Pick up permission / assignment changes from admin without requiring a full sign-out. */
  useEffect(() => {
    refreshUser().catch(() => {});
  }, [refreshUser]);

  /** Restricted Front Desk users: default property picker to first assigned building (still can choose “All properties” if multiple). */
  useEffect(() => {
    if (!propertiesReady) return;
    if (propertyId) return;
    const fd = user?.front_desk_properties;
    if (!Array.isArray(fd) || fd.length === 0) return;
    const want = String(fd[0].id);
    if (properties.some((p) => String(p.id) === want)) {
      setPropertyId(want);
    }
  }, [propertiesReady, properties, user?.front_desk_properties, propertyId, user]);

  useEffect(() => {
    const list = reportsMeta?.reports;
    if (!Array.isArray(list) || list.length === 0) return;
    const ids = list.map((r) => r.id);
    if (!ids.includes(reportType)) {
      setReportType(ids[0]);
    }
  }, [reportsMeta, reportType]);

  useEffect(() => {
    if (['overview', 'arrivals', 'departures', 'inhouse'].includes(view)) {
      loadOperational();
    } else if (view === 'rack') {
      loadRack();
    }
  }, [view, date, propertyId, loadOperational, loadRack]);

  useEffect(() => {
    if (view === 'bookings') {
      loadBookings();
    }
  }, [view, loadBookings]);

  useEffect(() => {
    if (view === 'reports' && (canReportsViewFd || canReportsExportFd)) {
      loadReportsMeta().catch(() => {});
    }
  }, [view, loadReportsMeta, canReportsViewFd, canReportsExportFd]);

  useEffect(() => {
    if (view === 'reports' && canReportsViewFd) {
      loadReportPreview();
    }
  }, [view, loadReportPreview, canReportsViewFd]);

  useEffect(() => {
    if (view === 'audit') {
      loadActivity();
    }
  }, [view, activityPage, loadActivity]);

  useEffect(() => {
    if (view !== 'audit') {
      setActivityPage(1);
    }
  }, [view]);

  useEffect(() => {
    if (view === 'rack' || view === 'bookings') {
      loadSummary();
    }
  }, [view, loadSummary]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const closeCreateModal = useCallback(() => {
    setCreateOpen(false);
    setError('');
    setCreateBookingPropertyId('');
    setCreateApartments([]);
    setCreateGuestIdFile(null);
    if (createIdFilePreview) {
      URL.revokeObjectURL(createIdFilePreview);
    }
    setCreateIdFilePreview(null);
    setCreateIdProfilePreview(null);
    setCreateDateWarning('');
    setCreateCheckoutWarning('');
    setGuestSuggestions([]);
    setGuestLinkedEmailNorm('');
    setCreateSelectedSurchargeIds([]);
    setCreateSurchargeOverrides({});
    setCreateForm({
      apartment_id: '',
      start_date: '',
      end_date: '',
      guests_adults: 2,
      guests_children: 0,
      guest_user_id: '',
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      guest_id_number: '',
      status: 'confirmed',
      payment_status: 'pending',
      notes: '',
      payment_method: 'cash',
      booking_platform_id: defaultBookingPlatformId,
      total_price_manual: '',
      discount_amount_manual: '',
    });
  }, [createIdFilePreview, defaultBookingPlatformId]);

  const openCreateModal = useCallback(() => {
    setMobileNavOpen(false);
    setError('');
    const initialProp =
      propertyId || (properties.length === 1 ? String(properties[0].id) : '');
    setCreateBookingPropertyId(initialProp);
    setCreateForm((prev) => ({
      ...prev,
      apartment_id: '',
      guest_id_number: '',
      status: 'confirmed',
      payment_status: 'pending',
      booking_platform_id: defaultBookingPlatformId || prev.booking_platform_id,
    }));
    setCreateOpen(true);
  }, [propertyId, properties, defaultBookingPlatformId]);

  const selectGuestSuggestion = useCallback((g) => {
    setCreateIdFilePreview((prevBlob) => {
      if (prevBlob) {
        URL.revokeObjectURL(prevBlob);
      }
      return null;
    });
    setCreateGuestIdFile(null);
    setCreateIdProfilePreview(g.government_id_document?.thumb || g.government_id_document?.url || null);
    setCreateForm((prev) => ({
      ...prev,
      guest_user_id: String(g.id),
      guest_name: g.name || '',
      guest_email: g.email || '',
      guest_phone: g.phone_number || '',
    }));
    setGuestLinkedEmailNorm((g.email || '').trim().toLowerCase());
    setGuestSuggestions([]);
  }, []);

  useEffect(() => {
    if (!createOpen) {
      setGuestSuggestions([]);
      return;
    }
    const email = (createForm.guest_email || '').trim();
    const name = (createForm.guest_name || '').trim();
    const phone = (createForm.guest_phone || '').trim();
    const q = email.length >= 2 ? email : phone.length >= 2 ? phone : name.length >= 2 ? name : '';

    if (guestSearchTimerRef.current) {
      clearTimeout(guestSearchTimerRef.current);
      guestSearchTimerRef.current = null;
    }

    if (!q) {
      setGuestSuggestions([]);
      return;
    }

    guestSearchTimerRef.current = setTimeout(() => {
      guestSearchAbortRef.current?.abort();
      const ac = new AbortController();
      guestSearchAbortRef.current = ac;
      frontDeskApi
        .searchGuests(q, ac.signal)
        .then((res) => {
          const rows = res?.data;
          setGuestSuggestions(Array.isArray(rows) ? rows : []);
        })
        .catch((e) => {
          if (e.name === 'AbortError') return;
          setGuestSuggestions([]);
        });
    }, 380);

    return () => {
      if (guestSearchTimerRef.current) {
        clearTimeout(guestSearchTimerRef.current);
      }
    };
  }, [createOpen, createForm.guest_email, createForm.guest_name, createForm.guest_phone]);

  useEffect(() => {
    if (!createOpen || properties.length !== 1 || createBookingPropertyId) return;
    setCreateBookingPropertyId(String(properties[0].id));
  }, [createOpen, properties, createBookingPropertyId]);

  useEffect(() => {
    if (!checkInOpen || !modal) return;
    const n =
      modal.registered_user?.government_id_number || modal.guest_id_number || '';
    setCheckInIdNumber(typeof n === 'string' ? n : '');
    setCheckInIdFile(null);
  }, [checkInOpen, modal]);

  useEffect(() => {
    if (!checkInOpen || !modal) return;
    if (!isArrivalDayForOps(modal, date)) {
      setCheckInOpen(false);
    }
  }, [date, checkInOpen, modal]);

  useEffect(() => {
    if (!checkOutOpen || !checkOutBooking) return;
    if (!isDepartureDayForOps(checkOutBooking, date)) {
      setCheckOutOpen(false);
      setCheckOutBooking(null);
      setCheckOutNote('');
    }
  }, [date, checkOutOpen, checkOutBooking]);

  useEffect(() => {
    if (!successMessage) return undefined;
    const t = setTimeout(() => setSuccessMessage(''), 10_000);
    return () => clearTimeout(t);
  }, [successMessage]);

  const dismissBookingModal = useCallback(() => {
    setModal(null);
    setModalEditing(false);
    setEditForm(null);
    setEditPriceQuote(null);
    setEditSelectedSurchargeIds([]);
    setEditSurchargeOverrides({});
    setCheckInOpen(false);
    setCheckInIdNumber('');
    setCheckInIdFile(null);
    setCheckOutOpen(false);
    setCheckOutBooking(null);
    setCheckOutNote('');
    setGuestRefundOpen(false);
    setGuestRefundAmount('');
    setGuestRefundNote('');
    setCautionModalBooking(null);
    setCautionModalKind('refund');
    setCautionModalAmount('');
    setCautionModalNote('');
  }, []);

  const refreshOperationalViews = useCallback(async () => {
    if (['overview', 'arrivals', 'departures', 'inhouse'].includes(view)) {
      await loadOperational();
    } else {
      await loadSummary();
    }
    if (view === 'rack') await loadRack();
    if (view === 'bookings') await loadBookings();
    if (view === 'reports' && canReportsViewFd) await loadReportPreview();
    if (view === 'audit') await loadActivity();
    if (view === 'users' || view === 'finance') await loadTeamList();
    if (
      view === 'catalog-discounts' ||
      view === 'catalog-surcharges' ||
      view === 'catalog-blocked-dates' ||
      view === 'staff-roles'
    ) {
      setCatalogRefreshTick((t) => t + 1);
    }
  }, [
    view,
    canReportsViewFd,
    loadOperational,
    loadSummary,
    loadRack,
    loadBookings,
    loadReportPreview,
    loadActivity,
    loadTeamList,
  ]);

  const handleRowMarkPaid = useCallback(
    async (b) => {
      if (b.payment_status?.value === 'paid' || b.status?.value === 'cancelled') return;
      const amt = formatNgn(b.total_price);
      const ok = await luxisttConfirm({
        title: 'Record payment?',
        text: `Record full payment of ${amt} for booking #${b.id} (${guestLabel(b)})? The guest will be able to download a PDF receipt.`,
        icon: 'question',
        confirmText: 'Record payment',
        cancelText: 'Not now',
      });
      if (!ok) return;
      setRowActionBusy(b.id);
      setError('');
      try {
        await frontDeskApi.markPaid(b.id);
        if (modal?.id === b.id) {
          dismissBookingModal();
        }
        setSuccessMessage(`Booking #${b.id} marked as paid.`);
        await refreshOperationalViews();
      } catch (e) {
        setError(e.message || 'Could not mark paid');
      } finally {
        setRowActionBusy(null);
      }
    },
    [refreshOperationalViews, modal?.id, dismissBookingModal],
  );

  const openCheckOutModal = useCallback((b) => {
    if (!['checked_in', 'confirmed'].includes(b?.status?.value)) return;
    if (!isDepartureDayForOps(b, date)) {
      setError(
        `Check-out is only available on the guest’s departure date (${formatLongDate(b.end_date)}). Adjust the operations date or use this action on that day.`,
      );
      return;
    }
    setCheckOutBooking(b);
    setCheckOutNote(typeof b.checkout_guest_note === 'string' ? b.checkout_guest_note : '');
    setCheckOutOpen(true);
  }, [date]);

  const handleRowConfirmBooking = useCallback(
    async (b) => {
      if (b.status?.value !== 'pending') return;
      const ok = await luxisttConfirm({
        title: 'Confirm booking?',
        text: `Confirm booking #${b.id} for ${guestLabel(b)}? The guest will receive a confirmation email.`,
        icon: 'question',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
      });
      if (!ok) return;
      setRowActionBusy(b.id);
      setError('');
      try {
        await frontDeskApi.approve(b.id);
        if (modal?.id === b.id) {
          dismissBookingModal();
        }
        setSuccessMessage(`Booking #${b.id} confirmed. Confirmation email sent to the guest.`);
        await refreshOperationalViews();
      } catch (e) {
        setError(e.message || 'Could not confirm booking');
      } finally {
        setRowActionBusy(null);
      }
    },
    [refreshOperationalViews, modal?.id, dismissBookingModal],
  );

  const completeCheckOut = useCallback(async () => {
    const b = checkOutBooking;
    if (!b?.id) return;
    const bookingId = b.id;
    setLoading(true);
    setError('');
    try {
      const trimmed = checkOutNote.trim();
      await frontDeskApi.checkOut(bookingId, trimmed ? { checkout_guest_note: trimmed } : {});
      setCheckOutOpen(false);
      setCheckOutBooking(null);
      setCheckOutNote('');
      if (modal?.id === bookingId) {
        dismissBookingModal();
      }
      setSuccessMessage(
        `Booking #${bookingId} checked out. If the guest has an account email, they will receive the thank-you and review request.`,
      );
      await refreshOperationalViews();
    } catch (e) {
      setError(e.message || 'Check-out failed');
    } finally {
      setLoading(false);
    }
  }, [checkOutBooking, checkOutNote, modal?.id, refreshOperationalViews, dismissBookingModal]);

  const openBookingForCheckIn = useCallback(async (id) => {
    setRowPrefetchBusy({ id, kind: 'checkin' });
    setError('');
    try {
      const res = await frontDeskApi.booking(id);
      const entity = unwrapEntity(res);
      if (!entity?.id) {
        setError('Could not load booking details.');
        return;
      }
      setModal(entity);
      if (!isArrivalDayForOps(entity, date)) {
        setError(
          `Check-in is only available on the guest’s arrival date (${formatLongDate(entity.start_date)}). Set the operations date to that day or use Details after the correct date.`,
        );
        setCheckInOpen(false);
        return;
      }
      if (!canShowCheckInOnOps(entity, date)) {
        if (entity.status?.value === 'pending') {
          setError('Confirm the booking first, then record payment before check-in.');
        } else if (entity.payment_status?.value !== 'paid') {
          setError('Record payment with Mark paid before check-in.');
        }
        setCheckInOpen(false);
        return;
      }
      setCheckInOpen(true);
    } catch (e) {
      setError(e.message || 'Could not load booking');
    } finally {
      setRowPrefetchBusy(null);
    }
  }, [date]);

  const handleRowReceiptDownload = useCallback(async (b) => {
    if (b.payment_status?.value !== 'paid') return;
    setRowActionBusy(b.id);
    setError('');
    try {
      const blob = await downloadFrontDeskReceipt(b.id);
      const how = openPdfBlobInNewTab(blob, { filename: `luxistt-receipt-${b.id}.pdf` });
      setSuccessMessage(
        how === 'viewer'
          ? `Receipt for booking #${b.id} opened in the PDF viewer.`
          : `Receipt saved for booking #${b.id}. Open the file to view or print.`,
      );
    } catch (e) {
      setError(e.message || 'Could not open receipt');
    } finally {
      setRowActionBusy(null);
    }
  }, []);

  const openBooking = useCallback(async (id) => {
    setRowPrefetchBusy({ id, kind: 'details' });
    setError('');
    try {
      const res = await frontDeskApi.booking(id);
      const entity = unwrapEntity(res);
      if (!entity?.id) {
        setError('Could not load booking details.');
        return;
      }
      setCheckInOpen(false);
      setModalEditing(false);
      setEditForm(null);
      setEditPriceQuote(null);
      setEditSelectedSurchargeIds([]);
      setEditSurchargeOverrides({});
      setModal(entity);
    } catch (e) {
      setError(e.message || 'Could not load booking');
    } finally {
      setRowPrefetchBusy(null);
    }
  }, []);

  const openBookingForEdit = useCallback(async (id) => {
    setRowPrefetchBusy({ id, kind: 'edit' });
    setError('');
    try {
      const res = await frontDeskApi.booking(id);
      const entity = unwrapEntity(res);
      if (!entity?.id) {
        setError('Could not load booking details.');
        return;
      }
      setCheckInOpen(false);
      setModal(entity);
      setEditPriceQuote(null);
      setEditSurchargeOverrides({});
      setEditSelectedSurchargeIds(
        Array.isArray(entity.selected_surcharge_ids)
          ? entity.selected_surcharge_ids
              .map((id) => Number(id))
              .filter((n) => Number.isFinite(n) && n > 0)
          : [],
      );
      setEditForm({
        start_date: entity.start_date,
        end_date: entity.end_date,
        guests_adults: entity.guests_adults,
        guests_children: entity.guests_children,
        guest_name: entity.guest_name || '',
        guest_email: entity.guest_email || '',
        guest_phone: entity.guest_phone || '',
        guest_id_number: entity.guest_id_number || '',
        notes: entity.notes || '',
        booking_platform_id: entity.booking_platform?.id ? String(entity.booking_platform.id) : '',
        discount_amount_manual: '',
        total_price_manual: '',
      });
      setModalEditing(true);
    } catch (e) {
      setError(e.message || 'Could not load booking');
    } finally {
      setRowPrefetchBusy(null);
    }
  }, []);

  const handleRowCancelBooking = useCallback(
    async (b) => {
      if (!canFdCancelBookingStaff(canStaffAbilityForFrontDesk, b, propertyId || null)) return;
      const ok = await luxisttConfirm({
        title: 'Cancel this booking?',
        text: `Cancel booking #${b.id} (${guestLabel(b)})? Only unpaid, not-yet-checked-in stays can be cancelled from the desk.`,
        icon: 'warning',
        confirmText: 'Yes, cancel',
        cancelText: 'Keep booking',
        danger: true,
      });
      if (!ok) return;
      setRowActionBusy(b.id);
      setError('');
      try {
        await frontDeskApi.deleteBooking(b.id);
        setSuccessMessage(`Booking #${b.id} cancelled.`);
        await refreshOperationalViews();
      } catch (e) {
        setError(e.message || 'Could not cancel booking');
      } finally {
        setRowActionBusy(null);
      }
    },
    [refreshOperationalViews, canStaffAbilityForFrontDesk, propertyId],
  );

  const openGuestRefundFlow = useCallback(async (id) => {
    setRowActionBusy(id);
    setError('');
    try {
      const res = await frontDeskApi.booking(id);
      const entity = unwrapEntity(res);
      if (!entity?.id) {
        setError('Could not load booking details.');
        return;
      }
      if (!canOfferGuestRefund(entity)) {
        setError('This booking is not eligible for a guest refund (must be confirmed, paid, and not already refunded).');
        return;
      }
      setCheckInOpen(false);
      setModalEditing(false);
      setEditForm(null);
      setModal(entity);
      setGuestRefundAmount('');
      setGuestRefundNote('');
      setGuestRefundOpen(true);
    } catch (e) {
      setError(e.message || 'Could not load booking');
    } finally {
      setRowActionBusy(null);
    }
  }, []);

  const submitGuestRefund = useCallback(async () => {
    const target = modal;
    if (!target?.id || !guestRefundOpen) return;
    const amt = parseWholeNairaToNumber(guestRefundAmount);
    if (!Number.isFinite(amt) || amt < 1) {
      setError('Enter a valid refund amount in naira (whole naira, at least ₦1).');
      return;
    }
    const max = Number(target.total_price);
    if (!Number.isFinite(max) || max < 1) {
      setError('This booking has no collectable total to refund against.');
      return;
    }
    if (amt > max) {
      setError(`Refund cannot exceed the booking total (${formatNgn(max)}).`);
      return;
    }
    const ok = await luxisttConfirm({
      title: 'Refund guest?',
      text: `Cancel booking #${target.id}, mark payment refunded, and reduce the recorded total by ${formatNgn(amt)}? The guest will be notified like a cancellation.`,
      confirmText: 'Yes, process refund',
      cancelText: 'Go back',
      danger: true,
      icon: 'warning',
    });
    if (!ok) return;
    setLoading(true);
    setError('');
    try {
      const body = {
        refund_amount: amt,
        ...(guestRefundNote.trim() ? { note: guestRefundNote.trim() } : {}),
      };
      const res = await frontDeskApi.guestRefund(target.id, body);
      const entity = unwrapEntity(res);
      setGuestRefundOpen(false);
      setGuestRefundAmount('');
      setGuestRefundNote('');
      if (entity?.id) {
        setModal(entity);
      }
      setSuccessMessage(`Guest refund recorded for booking #${target.id}.`);
      await refreshOperationalViews();
    } catch (e) {
      setError(formatApiErrorUserMessage(e) || e.message || 'Refund failed');
    } finally {
      setLoading(false);
    }
  }, [modal, guestRefundOpen, guestRefundAmount, guestRefundNote, refreshOperationalViews]);

  const submitCautionAdjustment = useCallback(async () => {
    const target = cautionModalBooking;
    if (!target?.id) return;
    const amt = parseWholeNairaToNumber(cautionModalAmount);
    if (!Number.isFinite(amt) || amt < 1) {
      setError('Enter a valid amount in naira (whole naira, at least ₦1).');
      return;
    }
    const pb = getPricingBreakdown(target);
    const summary =
      cautionModalKind === 'refund'
        ? `Refund ${formatNgn(amt)} from the caution hold to the guest? The booking total will decrease by the same amount.`
        : `Record forfeit / damage: ${formatNgn(amt)}? Up to ${formatNgn(pb.caution_remainder)} can come from the remaining caution pool; any excess is added to the booking total as an extra charge.`;
    const ok = await luxisttConfirm({
      title: cautionModalKind === 'refund' ? 'Refund caution fee?' : 'Record caution forfeit / charge?',
      text: summary,
      confirmText: 'Confirm',
      cancelText: 'Go back',
      danger: cautionModalKind !== 'refund',
      icon: 'warning',
    });
    if (!ok) return;
    setLoading(true);
    setError('');
    try {
      const res = await frontDeskApi.cautionAdjustment(target.id, {
        kind: cautionModalKind,
        amount: amt,
        ...(cautionModalNote.trim() ? { note: cautionModalNote.trim() } : {}),
      });
      const entity = unwrapEntity(res);
      setCautionModalBooking(null);
      setCautionModalAmount('');
      setCautionModalNote('');
      setCautionModalKind('refund');
      if (entity?.id && modal?.id === entity.id) {
        setModal(entity);
      }
      setSuccessMessage(`Caution fee updated for booking #${target.id}.`);
      await refreshOperationalViews();
    } catch (e) {
      setError(formatApiErrorUserMessage(e) || e.message || 'Could not update caution fee');
    } finally {
      setLoading(false);
    }
  }, [
    cautionModalBooking,
    cautionModalKind,
    cautionModalAmount,
    cautionModalNote,
    modal?.id,
    refreshOperationalViews,
  ]);

  const beginModalEdit = useCallback(() => {
    if (!modal?.id) return;
    setEditPriceQuote(null);
    setEditSurchargeOverrides({});
    setEditSelectedSurchargeIds(
      Array.isArray(modal.selected_surcharge_ids)
        ? modal.selected_surcharge_ids.map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0)
        : [],
    );
    setEditForm({
      start_date: modal.start_date,
      end_date: modal.end_date,
      guests_adults: modal.guests_adults,
      guests_children: modal.guests_children,
      guest_name: modal.guest_name || '',
      guest_email: modal.guest_email || '',
      guest_phone: modal.guest_phone || '',
      guest_id_number: modal.guest_id_number || '',
      notes: modal.notes || '',
      booking_platform_id: modal.booking_platform?.id ? String(modal.booking_platform.id) : '',
      discount_amount_manual: '',
      total_price_manual: '',
    });
    setModalEditing(true);
  }, [modal]);

  const saveModalEdit = useCallback(async () => {
    if (!modal?.id || !editForm) return;
    setLoading(true);
    setError('');
    try {
      const body = {
        start_date: editForm.start_date,
        end_date: editForm.end_date,
        guests_adults: Number(editForm.guests_adults),
        guests_children: Number(editForm.guests_children),
        guest_name: editForm.guest_name || undefined,
        guest_email: editForm.guest_email || undefined,
        guest_phone: editForm.guest_phone || undefined,
        guest_id_number: editForm.guest_id_number || undefined,
        notes: editForm.notes || undefined,
        ...(editForm.booking_platform_id
          ? { booking_platform_id: Number(editForm.booking_platform_id) }
          : { booking_platform_id: null }),
        surcharge_ids: editSelectedSurchargeIds,
      };
      if (editDiscountOverrideParsed !== null) {
        body.discount_amount_override = editDiscountOverrideParsed;
      }
      const surchargeOverridesPayload = Object.entries(editSurchargeOverrideAmountMap).map(([id, amount]) => ({
        id: Number(id),
        amount: Number(amount),
      }));
      if (surchargeOverridesPayload.length > 0) {
        body.surcharge_overrides = surchargeOverridesPayload;
      }
      const manualParsed = parseWholeNairaToNumber(editForm.total_price_manual);
      if (
        editForm.total_price_manual !== '' &&
        editForm.total_price_manual != null &&
        Number.isFinite(manualParsed) &&
        manualParsed >= 0
      ) {
        body.total_price = manualParsed;
      }
      const wasPaid = modal.payment_status?.value === 'paid';
      await frontDeskApi.updateBooking(modal.id, body);
      dismissBookingModal();
      setSuccessMessage(
        wasPaid
          ? 'Booking updated. If the total changed, payment was set to Pending until you record any balance with Mark paid.'
          : 'Booking updated.',
      );
      await refreshOperationalViews();
    } catch (e) {
      setError(formatApiErrorUserMessage(e, 'Could not update booking'));
    } finally {
      setLoading(false);
    }
  }, [
    modal,
    editForm,
    editSelectedSurchargeIds,
    editDiscountOverrideParsed,
    editSurchargeOverrideAmountMap,
    refreshOperationalViews,
    dismissBookingModal,
  ]);

  const handleModalCancelBooking = useCallback(async () => {
    if (!modal?.id) return;
    if (!canFdCancelBookingStaff(canStaffAbilityForFrontDesk, modal, propertyId || null)) return;
    const ok = await luxisttConfirm({
      title: 'Cancel this booking?',
      text: `Cancel booking #${modal.id} (${guestLabel(modal)})? Unpaid reservations can be cancelled here; paid stays need support.`,
      icon: 'warning',
      confirmText: 'Yes, cancel',
      cancelText: 'Keep booking',
      danger: true,
    });
    if (!ok) return;
    setLoading(true);
    setError('');
    try {
      await frontDeskApi.deleteBooking(modal.id);
      dismissBookingModal();
      setSuccessMessage('Booking cancelled.');
      await refreshOperationalViews();
    } catch (e) {
      setError(e.message || 'Could not cancel booking');
    } finally {
      setLoading(false);
    }
  }, [modal, refreshOperationalViews, dismissBookingModal, canStaffAbilityForFrontDesk, propertyId]);

  const postAction = async (pathFn, successToast) => {
    if (!modal?.id) return;
    setLoading(true);
    setError('');
    try {
      await pathFn(modal.id);
      if (successToast) {
        dismissBookingModal();
        setSuccessMessage(successToast);
      }
      await refreshOperationalViews();
    } catch (e) {
      setError(e.message || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const onUploadDocs = async (ev) => {
    const { files } = ev.target;
    if (!files?.length || !modal?.id) return;
    const fd = new FormData();
    for (let i = 0; i < files.length; i += 1) {
      fd.append('documents[]', files[i]);
    }
    setLoading(true);
    setError('');
    try {
      const res = await frontDeskApi.uploadGuestDocuments(modal.id, fd);
      const next = unwrapEntity(res) || res.data;
      if (next) setModal(next);
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setLoading(false);
      ev.target.value = '';
    }
  };

  const submitCreate = async (ev) => {
    ev.preventDefault();
    if (createDateWarning || createCheckoutWarning) return;
    setLoading(true);
    setError('');
    try {
      const base = {
        apartment_id: Number(createForm.apartment_id),
        start_date: createForm.start_date,
        end_date: createForm.end_date,
        guests_adults: Number(createForm.guests_adults),
        guests_children: Number(createForm.guests_children),
        guest_name: createForm.guest_name || undefined,
        guest_email: createForm.guest_email || undefined,
        guest_phone: createForm.guest_phone || undefined,
        guest_id_number: createForm.guest_id_number?.trim() || undefined,
        status: createForm.status || 'confirmed',
        payment_status: createForm.payment_status || 'pending',
        notes: createForm.notes || undefined,
        payment_method: createForm.payment_method || 'cash',
        ...(createForm.booking_platform_id
          ? { booking_platform_id: Number(createForm.booking_platform_id) }
          : {}),
        ...(createSelectedSurchargeIds.length > 0 ? { surcharge_ids: createSelectedSurchargeIds } : {}),
      };
      if (createForm.guest_user_id) {
        base.user_id = Number(createForm.guest_user_id);
      }
      const manualParsed = parseWholeNairaToNumber(createForm.total_price_manual);
      if (
        createForm.total_price_manual !== '' &&
        createForm.total_price_manual != null &&
        Number.isFinite(manualParsed) &&
        manualParsed >= 0
      ) {
        base.total_price = manualParsed;
      }
      if (createDiscountOverrideParsed !== null) {
        base.discount_amount_override = createDiscountOverrideParsed;
      }
      const surchargeOverridesPayload = Object.entries(createSurchargeOverrideAmountMap).map(([id, amount]) => ({
        id: Number(id),
        amount: Number(amount),
      }));
      if (surchargeOverridesPayload.length > 0) {
        base.surcharge_overrides = surchargeOverridesPayload;
      }

      if (createGuestIdFile) {
        const fd = new FormData();
        Object.entries(base).forEach(([k, v]) => {
          if (v === undefined || v === null || v === '') return;
          if (k === 'surcharge_ids' || k === 'surcharge_overrides') return;
          fd.append(k, String(v));
        });
        createSelectedSurchargeIds.forEach((id) => fd.append('surcharge_ids[]', String(id)));
        surchargeOverridesPayload.forEach((row, idx) => {
          fd.append(`surcharge_overrides[${idx}][id]`, String(row.id));
          fd.append(`surcharge_overrides[${idx}][amount]`, String(row.amount));
        });
        fd.append('guest_id_document', createGuestIdFile);
        await frontDeskApi.createBooking(fd);
      } else {
        await frontDeskApi.createBooking(base);
      }

      const guestEmail = (createForm.guest_email || '').trim();
      closeCreateModal();
      setSuccessMessage(
        guestEmail
          ? `Booking created. Confirmation email sent to ${guestEmail}. New guest accounts also receive a welcome email with a link to set their password.`
          : 'Booking created. Confirmation email sent to the guest when an email address is on file.',
      );
      setView('bookings');
      await loadOperational();
      await loadBookings();
    } catch (e) {
      setError(formatApiErrorUserMessage(e, 'Could not create booking'));
    } finally {
      setLoading(false);
    }
  };

  const counts = summary?.counts || {};

  function sidebarNavBadgeCount(navId) {
    const n = Number(counts.pending_checkins ?? 0);
    const o = Number(counts.pending_checkouts ?? 0);
    const h = Number(counts.in_house ?? 0);
    if (navId === 'arrivals') return n > 0 ? n : 0;
    if (navId === 'departures') return o > 0 ? o : 0;
    if (navId === 'inhouse') return h > 0 ? h : 0;
    return 0;
  }

  const viewLabel = FRONT_DESK_NAV.find((n) => n.id === view)?.label || 'Front desk';

  const bookingAbilityKey = (book) => fdBookingPropertyKey(book, propertyId || null);

  const rowPrefetch = (id, kind) => rowPrefetchBusy?.id === id && rowPrefetchBusy?.kind === kind;

  const renderBookingTable = (rows, emptyMessage = 'No reservations match this view for the selected date.') => (
    <div className="fd-table-wrap fd-table-wrap--scroll">
      <table className="fd-table fd-table--wide fd-table--nowrap-cells">
        <thead>
          <tr>
            <th>ID</th>
            <th>Guest</th>
            <th>Stay</th>
            <th className="fd-col-nowrap" title="Actual check-in time">
              Check-in
            </th>
            <th className="fd-col-nowrap" title="Actual check-out time">
              Check-out
            </th>
            <th className="fd-col-center">Status</th>
            <th className="fd-col-center">Payment</th>
            <th className="fd-col-center" title="Booking channel (OTA, walk-in, etc.)">
              Platform
            </th>
            <th className="fd-col-right fd-th-subtotal" title="Stay and fees excluding caution">
              Sub total
            </th>
            <th className="fd-col-right fd-th-caution" title="Caution / deposit (and adjustments)">
              Caution
            </th>
            <th className="fd-col-center fd-th-total" title="Total amount (sub total + caution column)">
              Total
            </th>
            <th className="fd-col-center fd-th-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <EmptyTableRow colSpan={13} message={emptyMessage} />
          ) : (
            rows.map((b) => (
              <tr key={b.id}>
                <td>#{b.id}</td>
                <td
                  className="fd-td-ellipsis"
                  title={`${guestLabel(b)} · ${unitLabel(b)}`}
                >
                  <span className="fd-booking-guest-line">
                    <strong style={{ fontWeight: 600 }}>{guestLabel(b)}</strong>
                    <span className="muted"> · {unitLabel(b)}</span>
                  </span>
                </td>
                <td
                  className="fd-col-nowrap"
                  title={`${formatLongDate(b.start_date)} → ${formatLongDate(b.end_date)}`}
                >
                  {formatLongDate(b.start_date)} → {formatLongDate(b.end_date)}
                </td>
                <td className="fd-col-nowrap muted">{formatDateTime(b.checked_in_at)}</td>
                <td className="fd-col-nowrap muted">{formatDateTime(b.checked_out_at)}</td>
                <td className="fd-col-center">
                  <StatusBadge ui={b.status} />
                </td>
                <td className="fd-col-center">
                  <StatusBadge ui={b.payment_status} />
                </td>
                <td className="fd-col-center">
                  <StatusBadge ui={b.booking_platform} />
                </td>
                {renderFdBookingPricingCells(b)}
                <td className="fd-col-center fd-td-actions">
                  <div className="fd-row-action-btns">
                    {b.status?.value === 'pending' && canStaffAbilityForFrontDesk(bookingAbilityKey(b), 'booking.approve') ? (
                      <button
                        type="button"
                        className="fd-table-action-pill"
                        disabled={rowActionBusy === b.id}
                        onClick={() => handleRowConfirmBooking(b)}
                      >
                        Confirm
                      </button>
                    ) : null}
                    {b.payment_status?.value !== 'paid' &&
                    b.status?.value !== 'cancelled' &&
                    canStaffAbilityForFrontDesk(bookingAbilityKey(b), 'booking.mark_paid') ? (
                      <button
                        type="button"
                        className="fd-table-action-pill"
                        disabled={rowActionBusy === b.id}
                        onClick={() => handleRowMarkPaid(b)}
                      >
                        Mark paid
                      </button>
                    ) : null}
                    {canOfferGuestRefund(b) && canStaffAbilityForFrontDesk(bookingAbilityKey(b), 'booking.mark_paid') ? (
                      <button
                        type="button"
                        className="fd-table-action-pill fd-table-action-pill--warn"
                        disabled={rowActionBusy === b.id}
                        onClick={() => openGuestRefundFlow(b.id)}
                      >
                        Refund
                      </button>
                    ) : null}
                    {bookingHasCautionLine(b) &&
                    canFrontDeskAdjustCautionFees(b) &&
                    canStaffMarkPaidOrCaution(bookingAbilityKey(b), canStaffAbilityForFrontDesk) ? (
                      <button
                        type="button"
                        className="fd-table-action-pill fd-table-action-pill--caution"
                        disabled={rowActionBusy === b.id}
                        onClick={() => {
                          setCautionModalBooking(b);
                          setCautionModalKind('refund');
                          setCautionModalAmount('');
                          setCautionModalNote('');
                        }}
                      >
                        Caution fees
                      </button>
                    ) : null}
                    {canShowCheckInOnOps(b, date) &&
                    canStaffAbilityForFrontDesk(bookingAbilityKey(b), 'booking.check_in') ? (
                      <button
                        type="button"
                        className="fd-table-action-pill"
                        disabled={rowActionBusy === b.id || rowPrefetch(b.id, 'checkin')}
                        onClick={() => openBookingForCheckIn(b.id)}
                      >
                        Check in
                      </button>
                    ) : null}
                    {canShowCheckOutOnOps(b, date) &&
                    canStaffAbilityForFrontDesk(bookingAbilityKey(b), 'booking.check_out') ? (
                      <button
                        type="button"
                        className="fd-table-action-pill"
                        disabled={loading && checkOutOpen && checkOutBooking?.id === b.id}
                        onClick={() => openCheckOutModal(b)}
                      >
                        Check out
                      </button>
                    ) : null}
                    {b.payment_status?.value === 'paid' &&
                    canStaffAbilityForFrontDesk(bookingAbilityKey(b), 'booking.view') ? (
                      <button
                        type="button"
                        className="fd-table-action-pill fd-table-action-pill--neutral"
                        disabled={rowActionBusy === b.id}
                        onClick={() => handleRowReceiptDownload(b)}
                        title="View PDF receipt"
                      >
                        <FiDownload aria-hidden />
                        PDF
                      </button>
                    ) : null}
                    {canFrontDeskEditBooking(b) && canStaffAbilityForFrontDesk(bookingAbilityKey(b), 'booking.update') ? (
                      <button
                        type="button"
                        className="fd-table-action-pill"
                        disabled={rowPrefetch(b.id, 'edit') || rowActionBusy === b.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openBookingForEdit(b.id);
                        }}
                      >
                        {rowPrefetch(b.id, 'edit') ? '…' : 'Edit'}
                      </button>
                    ) : null}
                    {canFdCancelBookingStaff(canStaffAbilityForFrontDesk, b, propertyId || null) ? (
                      <button
                        type="button"
                        className="fd-table-action-pill fd-table-action-pill--danger"
                        disabled={rowActionBusy === b.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRowCancelBooking(b);
                        }}
                      >
                        Cancel
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="fd-table-action-pill fd-table-action-pill--neutral"
                      disabled={rowPrefetch(b.id, 'details')}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openBooking(b.id);
                      }}
                    >
                      {rowPrefetch(b.id, 'details') ? '…' : 'Details'}
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="dashboard-layout front-desk-layout">
      <Helmet>
        <title>
          Front desk · {BRAND_NAME}
        </title>
        <meta
          name="description"
          content={`${BRAND_NAME} staff console — arrivals, departures, room rack, and guest services.`}
        />
      </Helmet>
      {mobileNavOpen ? (
        <div
          className="fd-mobile-nav-scrim"
          role="presentation"
          aria-hidden
          onClick={() => setMobileNavOpen(false)}
        />
      ) : null}
      <header className="fd-mobile-topbar">
        <button
          type="button"
          className="fd-mobile-menu-btn"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open menu"
        >
          <FiMenu aria-hidden />
        </button>
        <span className="fd-mobile-topbar-title">Luxistt</span>
        <span className="fd-mobile-topbar-spacer" aria-hidden />
        <div className="fd-mobile-topbar-tools" ref={mobileHeaderToolsRef}>
          <NotificationBell className="fd-notify-bell--on-dark" />
          <div className="fd-header-profile">
            <button
              type="button"
              className="fd-header-profile-trigger fd-header-profile-trigger--compact"
              aria-haspopup="menu"
              aria-expanded={headerProfileOpen}
              onClick={() => setHeaderProfileOpen((o) => !o)}
              aria-label="Account menu"
            >
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="fd-header-profile-avatar" width={32} height={32} />
              ) : (
                <span className="fd-header-profile-placeholder fd-header-profile-placeholder--compact" aria-hidden>
                  <FiUser />
                </span>
              )}
            </button>
            {headerProfileOpen ? (
              <div className="fd-header-profile-menu fd-header-profile-menu--dock-right" role="menu">
                {canAccessProfilePage ? (
                  <Link
                    to="/profile"
                    role="menuitem"
                    className="fd-header-profile-menu-item"
                    onClick={() => setHeaderProfileOpen(false)}
                  >
                    Profile
                  </Link>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  className="fd-header-profile-menu-item fd-header-profile-menu-item--danger"
                  onClick={() => {
                    setHeaderProfileOpen(false);
                    handleLogout();
                  }}
                >
                  Logout
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </header>
      <aside className={`dashboard-sidebar${mobileNavOpen ? ' fd-sidebar-open' : ''}`}>
        <div className="sidebar-brand">
          <Link to="/" onClick={() => setMobileNavOpen(false)}>
            Luxistt
          </Link>
          <span className="sidebar-brand-sub">Front desk</span>
        </div>
        <nav className="sidebar-nav" aria-label="Staff console sections">
          {fdNavItemsVisible.map((navItem) => {
            const { id, label, icon: Icon } = navItem;
            const badge = sidebarNavBadgeCount(id);
            const badgeLabel =
              id === 'arrivals'
                ? `${badge} arrival${badge === 1 ? '' : 's'} awaiting check-in`
                : id === 'departures'
                  ? `${badge} departure${badge === 1 ? '' : 's'} awaiting check-out`
                  : id === 'inhouse'
                    ? `${badge} guest${badge === 1 ? '' : 's'} in house tonight`
                    : '';
            return (
              <button
                key={id}
                type="button"
                className={`sidebar-nav-item${view === id ? ' active' : ''}`}
                onClick={() => {
                  setView(id);
                  setMobileNavOpen(false);
                }}
              >
                <Icon className="sidebar-nav-icon" />
                <span className="sidebar-nav-item-label">{label}</span>
                {badge > 0 ? (
                  <span className="sidebar-nav-badge" aria-label={badgeLabel}>
                    {badge > 99 ? '99+' : badge}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>
        {canAccessProfilePage ? (
          <div style={{ padding: '0 0.75rem 0.5rem' }}>
            <Link
              to="/profile"
              className="sidebar-nav-item"
              style={{ textDecoration: 'none', display: 'flex' }}
              onClick={() => setMobileNavOpen(false)}
            >
              <FiUser className="sidebar-nav-icon" />
              <span>My profile</span>
            </Link>
          </div>
        ) : null}
        <div className="sidebar-footer">
          <div className="sidebar-user">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="sidebar-user-avatar" width={40} height={40} />
            ) : null}
            <div className="sidebar-user-text">
              <span className="sidebar-user-name">{formatDisplayName(user?.name, 'Staff')}</span>
              <span className="sidebar-user-role">{user?.role}</span>
            </div>
          </div>
          <button type="button" className="btn-logout" onClick={handleLogout}>
            <FiLogOut />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="dashboard-main front-desk-main">
        <div className="dashboard-main-inner front-desk-inner">
          <header className="dashboard-header fd-page-header">
            <div className="dashboard-header-row">
              <div className="dashboard-header-text">
                <p className="dashboard-welcome">Staff console</p>
                <h1 className="dashboard-title">{viewLabel}</h1>
                <p className="dashboard-subtitle">
                  {TAGLINE} — manage today&apos;s movement, occupancy, and guest requests in one place.
                </p>
              </div>
              <div className="dashboard-header-tools fd-header-tools" ref={desktopHeaderToolsRef}>
                <NotificationBell />
                <div className="fd-header-profile">
                  <button
                    type="button"
                    className="fd-header-profile-trigger"
                    aria-haspopup="menu"
                    aria-expanded={headerProfileOpen}
                    onClick={() => setHeaderProfileOpen((o) => !o)}
                  >
                    {user?.avatar_url ? (
                      <img src={user.avatar_url} alt="" className="fd-header-profile-avatar" width={36} height={36} />
                    ) : (
                      <span className="fd-header-profile-placeholder" aria-hidden>
                        <FiUser />
                      </span>
                    )}
                    <FiChevronDown className="fd-header-profile-chevron" aria-hidden />
                  </button>
                  {headerProfileOpen ? (
                    <div className="fd-header-profile-menu fd-header-profile-menu--dock-right" role="menu">
                      {canAccessProfilePage ? (
                        <Link
                          to="/profile"
                          role="menuitem"
                          className="fd-header-profile-menu-item"
                          onClick={() => setHeaderProfileOpen(false)}
                        >
                          Profile
                        </Link>
                      ) : null}
                      <button
                        type="button"
                        role="menuitem"
                        className="fd-header-profile-menu-item fd-header-profile-menu-item--danger"
                        onClick={() => {
                          setHeaderProfileOpen(false);
                          handleLogout();
                        }}
                      >
                        Logout
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </header>

          <div className="fd-hero">
            <div className="fd-hero-date">
              <FiCalendar className="fd-hero-date-icon" aria-hidden />
              <div>
                <span className="fd-hero-date-label">Operations date</span>
                <span className="fd-hero-date-value">{formatLongDate(date)}</span>
              </div>
            </div>
            <p className="fd-hero-hint">
              Filter by property to focus one building; room rack requires a property. Use Refresh after changing dates.
            </p>
          </div>

          {error && !modal && !createOpen ? (
            <div className="dashboard-notification dashboard-notification--error" role="alert">
              {error}
              <button type="button" className="dashboard-notification-dismiss" onClick={() => setError('')}>
                ×
              </button>
            </div>
          ) : null}
          {successMessage ? (
            <div className="dashboard-notification dashboard-notification--success" role="status">
              {successMessage}
              <button
                type="button"
                className="dashboard-notification-dismiss"
                onClick={() => setSuccessMessage('')}
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          ) : null}

          <div className="front-desk-toolbar">
            <label>
              Property
              <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
                <option value="">All properties</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.city ? ` · ${p.city}` : ''}
                  </option>
                ))}
              </select>
              {propertiesReady && properties.length === 0 ? (
                <span className="fd-property-empty-hint">
                  {roleIdSetForUser(user).has(ROLE_ADMIN) || user?.role === 'Admin'
                    ? 'No properties in the database yet. Create one in the admin panel (Filament), then click Refresh.'
                    : 'No buildings in your access list. An administrator can assign properties to your Front Desk account (Filament admin → Users → Front Desk → property access).'}
                </span>
              ) : null}
            </label>
            <label>
              Date
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            <button
              type="button"
              className="btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
              onClick={() => {
                if (view === 'rack') {
                  loadSummary();
                  loadRack();
                } else if (view === 'bookings') {
                  loadSummary();
                  loadBookings();
                } else if (view === 'reports') {
                  if (canReportsViewFd || canReportsExportFd) loadReportsMeta().catch(() => {});
                  if (canReportsViewFd) loadReportPreview();
                } else if (view === 'audit') {
                  loadActivity();
                } else if (view === 'staff-roles') {
                  setCatalogRefreshTick((t) => t + 1);
                } else if (view === 'users' || view === 'finance') {
                  loadTeamList();
                  if (view === 'finance' && propertyId) {
                    setFinanceLoading(true);
                    (async () => {
                      try {
                        if (financeTab === 'payroll') {
                          const res = await frontDeskApi.payroll(propertyId, fdPayrollListParams);
                          setFdPayrollRows(Array.isArray(res.data) ? res.data : []);
                          setFdPayrollMeta(res.meta || null);
                        } else if (financeTab === 'expenses') {
                          const [typesRes, expRes] = await Promise.all([
                            frontDeskApi.expenseTypes(),
                            frontDeskApi.expenses(propertyId, fdExpenseListParams),
                          ]);
                          setFdExpenseTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
                          setFdExpenseRows(Array.isArray(expRes.data) ? expRes.data : []);
                          setFdExpenseMeta(expRes.meta || null);
                        } else if (financeTab === 'purchases') {
                          const [typesRes, purRes] = await Promise.all([
                            frontDeskApi.purchaseTypes(),
                            frontDeskApi.purchases(propertyId, fdPurchaseListParams),
                          ]);
                          setFdPurchaseTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
                          setFdPurchaseRows(Array.isArray(purRes.data) ? purRes.data : []);
                          setFdPurchaseMeta(purRes.meta || null);
                        }
                      } catch (e) {
                        setError(e.message || 'Could not refresh finance data');
                      } finally {
                        setFinanceLoading(false);
                      }
                    })();
                  }
                } else if (
                  view === 'catalog-discounts' ||
                  view === 'catalog-surcharges' ||
                  view === 'catalog-blocked-dates'
                ) {
                  setCatalogRefreshTick((t) => t + 1);
                } else {
                  loadOperational();
                }
              }}
              disabled={loading}
            >
              <FiRefreshCw /> Refresh
            </button>
            {canCreateBookingFd && showNewBookingInToolbar ? (
              <button
                type="button"
                className="btn-ghost btn-primary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                onClick={openCreateModal}
              >
                <FiPlus /> New booking
              </button>
            ) : null}
          </div>

          {fdNavItemsVisible.length === 0 ? (
            <div className="fd-no-modules-card glass-panel">
              <h2 className="fd-no-modules-title">No desk modules</h2>
              <p className="fd-no-modules-lead muted">
                Nothing in the staff console is enabled for your account with the current property filter. An administrator
                can grant abilities in Filament under Staff &amp; HR → Property staff.
              </p>
            </div>
          ) : (
            <>
              {loading && !modal && !createOpen && rowPrefetchBusy == null ? (
                <div className="dashboard-loading">
                  <div className="loading-spinner" />
                  <p>Loading…</p>
                </div>
              ) : null}

              {view === 'overview' && (
            <>
              <div className="fd-stat-grid fd-stat-grid--overview">
                <StatTile value={counts.arrivals} label="Arrivals today" icon={FiArrowDownCircle} />
                <StatTile value={counts.departures} label="Departures today" icon={FiArrowUpCircle} />
                <StatTile value={counts.in_house} label="In house" icon={FiUsers} />
                <StatTile
                  value={counts.pending_checkins}
                  label="Pending check-in"
                  icon={FiClipboard}
                  hint="Arrivals today not yet in house"
                />
                <StatTile
                  value={counts.pending_checkouts}
                  label="Pending check-out"
                  icon={FiCheckSquare}
                  hint="Departures today still in-house or confirmed"
                />
                {propertyId ? (
                  <StatTile
                    value={counts.team_active}
                    label="Active staff"
                    icon={FiUserCheck}
                    hint="Assignments on this property"
                  />
                ) : null}
              </div>
              <h2 className="front-desk-section-title">Arrivals snapshot</h2>
              <p className="front-desk-section-lead">First ten guests expected today — open a row for full details and actions.</p>
              {renderBookingTable(
                arrivals.slice(0, 10),
                'No arrivals scheduled for this date. Try another day or clear the property filter.',
              )}
            </>
          )}

          {view === 'arrivals' && (
            <>
              <div className="fd-bookings-section-head">
                <h2 className="front-desk-section-title">Arrivals</h2>
                {canCreateBookingFd ? (
                  <button
                    type="button"
                    className="btn-ghost btn-primary fd-btn-with-icon fd-bookings-section-head__btn"
                    onClick={openCreateModal}
                  >
                    <FiPlus aria-hidden />
                    New booking
                  </button>
                ) : null}
              </div>
              {renderBookingTable(arrivals)}
            </>
          )}

          {view === 'departures' && (
            <>
              <h2 className="front-desk-section-title">Departures</h2>
              {renderBookingTable(departures)}
            </>
          )}

          {view === 'inhouse' && (
            <>
              <h2 className="front-desk-section-title">In house</h2>
              <p className="front-desk-section-lead">
                Guests checked in for the operations date (confirmed arrivals who have not checked in yet appear under Arrivals).
              </p>
              {renderBookingTable(
                inHouse,
                'No in-house guests for this date. Confirmed arrivals who have not checked in yet are listed under Arrivals.',
              )}
            </>
          )}

          {view === 'rack' && rack && (
            <>
              <h2 className="front-desk-section-title">
                {rack.property?.name}
                <span className="muted" style={{ marginLeft: '0.75rem', fontWeight: 400 }}>
                  {rack.dates?.[0] ? formatLongDate(rack.dates[0]) : ''}
                  {' — '}
                  {rack.dates?.length ? formatLongDate(rack.dates[rack.dates.length - 1]) : ''}
                </span>
              </h2>
              <div className="fd-rack-scroll">
                <table className="fd-rack-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      {rack.dates?.map((d) => (
                        <th key={d} title={d} className="fd-rack-th-date">
                          {formatLongDate(d)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rack.apartments?.map((row) => (
                      <tr key={row.id}>
                        <td>{row.name}</td>
                        {row.cells?.map((c) => {
                          const rackLine = c.booking ? rackCheckInSummary(c.booking) : '';
                          return (
                          <td
                            key={c.date}
                            className={`fd-rack-cell ${c.booking ? 'fd-rack-cell--booked' : ''}`}
                            onClick={() => c.booking && openBooking(c.booking.id)}
                            onKeyDown={(e) => {
                              if ((e.key === 'Enter' || e.key === ' ') && c.booking) openBooking(c.booking.id);
                            }}
                            role={c.booking ? 'button' : undefined}
                            tabIndex={c.booking ? 0 : undefined}
                          >
                            {c.booking ? (
                              <>
                                <div style={{ fontWeight: 600 }}>{guestLabel(c.booking)}</div>
                                <StatusBadge ui={c.booking.status} />
                                {rackLine ? <div className="fd-rack-checkin">{rackLine}</div> : null}
                                <div className="fd-rack-cell-source">
                                  {c.booking.booking_source?.value != null
                                    ? c.booking.booking_source.label
                                    : '—'}
                                  {c.booking.booking_platform?.label && c.booking.booking_platform.label !== '—' ? (
                                    <span className="fd-rack-cell-platform"> · {c.booking.booking_platform.label}</span>
                                  ) : null}
                                </div>
                              </>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {view === 'bookings' && (
            <>
              <div className="fd-bookings-section-head">
                <h2 className="front-desk-section-title">
                  All bookings
                  {bookingsMeta?.total != null ? ` (${bookingsMeta.total})` : ''}
                </h2>
                {canCreateBookingFd ? (
                  <button
                    type="button"
                    className="btn-ghost btn-primary fd-btn-with-icon fd-bookings-section-head__btn"
                    onClick={openCreateModal}
                  >
                    <FiPlus aria-hidden />
                    New booking
                  </button>
                ) : null}
              </div>
              <p className="front-desk-section-lead">
                Select a property first, then search by guest name, email, phone, or booking ID. Use filters to narrow the list; results are paginated.
              </p>
              {!propertyId ? (
                <p className="muted">Choose a property above to load bookings for that building.</p>
              ) : null}
              <div className="fd-bookings-toolbar">
                <label className="fd-bookings-toolbar-field fd-bookings-toolbar-search">
                  <span className="fd-bookings-toolbar-label">Search</span>
                  <input
                    type="search"
                    placeholder="Name, email, phone, or #ID"
                    value={bookingsQDraft}
                    onChange={(e) => setBookingsQDraft(e.target.value)}
                    autoComplete="off"
                  />
                </label>
                <label className="fd-bookings-toolbar-field">
                  <span className="fd-bookings-toolbar-label">Status</span>
                  <select
                    value={bookingsStatusFilter}
                    onChange={(e) => setBookingsStatusFilter(e.target.value)}
                  >
                    {BOOKING_STATUS_FILTER_OPTIONS.map((o) => (
                      <option key={o.value || 'all'} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="fd-bookings-toolbar-field">
                  <span className="fd-bookings-toolbar-label">Payment</span>
                  <select
                    value={bookingsPaymentFilter}
                    onChange={(e) => setBookingsPaymentFilter(e.target.value)}
                  >
                    {PAYMENT_STATUS_FILTER_OPTIONS.map((o) => (
                      <option key={o.value || 'all'} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                {propertyId && bookingsApartmentOptions.length > 0 ? (
                  <label className="fd-bookings-toolbar-field">
                    <span className="fd-bookings-toolbar-label">Unit</span>
                    <select
                      value={bookingsApartmentId}
                      onChange={(e) => setBookingsApartmentId(e.target.value)}
                    >
                      <option value="">All units</option>
                      {bookingsApartmentOptions.map((a) => (
                        <option key={a.id} value={String(a.id)}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <label className="fd-bookings-toolbar-field">
                  <span className="fd-bookings-toolbar-label">Occupied on</span>
                  <input
                    type="date"
                    value={bookingsOnDate}
                    onChange={(e) => setBookingsOnDate(e.target.value)}
                  />
                </label>
                <FdPerPageField
                  id="fd-bookings-per-page"
                  value={bookingsPerPage}
                  onChange={setBookingsPerPage}
                  maxAllowed={100}
                />
                <div className="fd-bookings-toolbar-actions">
                  <button
                    type="button"
                    className="btn-link fd-bookings-clear"
                    onClick={() => {
                      setBookingsQDraft('');
                      setBookingsQ('');
                      setBookingsStatusFilter('');
                      setBookingsPaymentFilter('');
                      setBookingsApartmentId('');
                      setBookingsOnDate('');
                      setBookingsPage(1);
                    }}
                  >
                    Clear filters
                  </button>
                </div>
              </div>
              {renderBookingTable(bookings, 'No bookings match your filters. Try clearing search or widening the criteria.')}
              {bookingsMeta ? (
                <FdPaginationNav meta={bookingsMeta} page={bookingsPage} setPage={setBookingsPage} disabled={loading} />
              ) : null}
            </>
          )}

          {view === 'reports' && (
            <>
              <h2 className="front-desk-section-title">Reports</h2>
              <p className="front-desk-section-lead">
                Operations, HR, payroll, and finance summaries for your access (options depend on your permissions). In-app preview and PDF/Excel export are separate abilities in admin.
              </p>
              {!canReportsViewFd && canReportsExportFd ? (
                <p className="muted fd-reports-export-only-hint">
                  You can download reports below. Your assignment does not include in-app preview.
                </p>
              ) : null}
              <div className="fd-reports-toolbar">
                {(() => {
                  const reportSupportsApartmentFilter = propertyId
                    && ['revenue', 'ledger', 'movements', 'expenses_summary', 'purchases_summary', 'profit_and_loss'].includes(reportType);
                  return (
                    <>
                <label>
                  Report
                  <select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    aria-label="Report type"
                  >
                    {(reportsMeta?.reports || [
                      { id: 'occupancy', label: 'Daily occupancy' },
                      { id: 'revenue', label: 'Revenue by property' },
                      { id: 'ledger', label: 'Bookings ledger' },
                      { id: 'movements', label: 'Arrivals & departures' },
                      { id: 'payroll', label: 'Payroll register' },
                      { id: 'staff_roster', label: 'Staff roster & exits' },
                      { id: 'expenses_summary', label: 'Expenses summary' },
                      { id: 'purchases_summary', label: 'Purchases summary' },
                      { id: 'profit_and_loss', label: 'Profit & loss' },
                    ]).map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </label>
                {reportSupportsApartmentFilter && bookingsApartmentOptions.length > 0 ? (
                  <label>
                    Apartment
                    <select
                      value={reportApartmentId}
                      onChange={(e) => setReportApartmentId(e.target.value)}
                      aria-label="Report apartment"
                    >
                      <option value="">All apartments</option>
                      {bookingsApartmentOptions.map((a) => (
                        <option key={a.id} value={String(a.id)}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <label>
                  From
                  <input type="date" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} />
                </label>
                <label>
                  To
                  <input type="date" value={reportTo} onChange={(e) => setReportTo(e.target.value)} />
                </label>
                <div className="fd-reports-export-btns">
                  <button
                    type="button"
                    className="btn-ghost fd-btn-with-icon"
                    disabled={!!reportExportBusy || !canReportsExportFd}
                    title={!canReportsExportFd ? 'No permission to export reports' : undefined}
                    onClick={async () => {
                      setReportExportBusy('pdf');
                      setError('');
                      try {
                        const how = await downloadFrontDeskReport(reportType, {
                          from: reportFrom,
                          to: reportTo,
                          ...(propertyId ? { property_id: Number(propertyId) } : {}),
                          ...(reportApartmentId ? { apartment_id: Number(reportApartmentId) } : {}),
                        }, 'pdf');
                        setSuccessMessage(
                          how === 'download'
                            ? 'PDF report saved to your device. Open the file to view or print.'
                            : 'PDF report opened in the viewer.',
                        );
                      } catch (e) {
                        setError(e.message || 'PDF export failed');
                      } finally {
                        setReportExportBusy(null);
                      }
                    }}
                  >
                    <FiDownload aria-hidden />
                    {reportExportBusy === 'pdf' ? 'PDF…' : 'View PDF'}
                  </button>
                  <button
                    type="button"
                    className="btn-ghost fd-btn-with-icon"
                    disabled={!!reportExportBusy || !canReportsExportFd}
                    title={!canReportsExportFd ? 'No permission to export reports' : undefined}
                    onClick={async () => {
                      setReportExportBusy('xlsx');
                      setError('');
                      try {
                        await downloadFrontDeskReport(reportType, {
                          from: reportFrom,
                          to: reportTo,
                          ...(propertyId ? { property_id: Number(propertyId) } : {}),
                          ...(reportApartmentId ? { apartment_id: Number(reportApartmentId) } : {}),
                        }, 'xlsx');
                        setSuccessMessage('Excel report downloaded.');
                      } catch (e) {
                        setError(e.message || 'Excel export failed');
                      } finally {
                        setReportExportBusy(null);
                      }
                    }}
                  >
                    <FiDownload aria-hidden />
                    {reportExportBusy === 'xlsx' ? 'Excel…' : 'Download Excel'}
                  </button>
                </div>
                    </>
                  );
                })()}
              </div>
              {canReportsViewFd ? (
              <div className="fd-report-preview">
                {reportPreviewLoading ? (
                  <p className="muted">Loading preview…</p>
                ) : reportPreview && reportType === 'occupancy' && Array.isArray(reportPreview.rows) ? (
                  <div className="fd-table-wrap">
                    <table className="fd-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th className="fd-col-center">Occupied</th>
                          <th className="fd-col-center">Total units</th>
                          <th className="fd-col-center">Occupancy %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportPreview.rows.length === 0 ? (
                          <EmptyTableRow colSpan={4} message="No rows in this range." />
                        ) : (
                          reportPreview.rows.map((row) => (
                            <tr key={row.date}>
                              <td>{formatLongDate(row.date)}</td>
                              <td className="fd-col-center">{row.occupied_units}</td>
                              <td className="fd-col-center">{row.total_units}</td>
                              <td className="fd-col-center">{row.occupancy_percent}%</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : reportPreview && reportType === 'revenue' && Array.isArray(reportPreview.rows) ? (
                  <div className="fd-table-wrap">
                    <table className="fd-table">
                      <thead>
                        <tr>
                          <th>{reportPreview.scope === 'apartment' ? 'Apartment' : 'Property'}</th>
                          <th className="fd-col-center">Paid bookings</th>
                          <th className="fd-col-right">Revenue (₦)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportPreview.rows.length === 0 ? (
                          <EmptyTableRow colSpan={3} message="No paid stays with arrival in this range." />
                        ) : (
                          reportPreview.rows.map((row) => (
                            <tr key={row.apartment_id || row.property_id}>
                              <td>{reportPreview.scope === 'apartment' ? row.apartment_name : row.property_name}</td>
                              <td className="fd-col-center">{row.paid_bookings}</td>
                              <td className="fd-col-right">{formatNgn(row.revenue_ngn)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : reportPreview && reportType === 'ledger' && Array.isArray(reportPreview.rows) ? (
                  <div className="fd-table-wrap fd-table-wrap--scroll">
                    <table className="fd-table fd-table--wide">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Created</th>
                          <th>Guest</th>
                          <th>Property</th>
                          <th>Unit</th>
                          <th>Stay</th>
                          <th>Status</th>
                          <th>Payment</th>
                          <th className="fd-col-right">Sub total</th>
                          <th className="fd-col-right">Caution</th>
                          <th className="fd-col-right">Total</th>
                          <th>Platform</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportPreview.rows.length === 0 ? (
                          <EmptyTableRow colSpan={12} message="No bookings created in this range." />
                        ) : (
                          reportPreview.rows.slice(0, 100).map((row) => (
                            <tr key={row.id}>
                              <td>#{row.id}</td>
                              <td className="muted">{formatDateTime(row.created_at)}</td>
                              <td>{row.guest || '—'}</td>
                              <td>{row.property || '—'}</td>
                              <td>{row.apartment || '—'}</td>
                              <td>
                                {row.start_date} → {row.end_date}
                              </td>
                              <td>{row.status}</td>
                              <td>{row.payment_status}</td>
                              <td className="fd-col-right">{formatNgn(row.stay_subtotal)}</td>
                              <td
                                className={`fd-col-right ${cautionToneClassName(row.caution_tone || 'none', 'fd-booking-caution')}`}
                              >
                                {formatNgn(row.caution_display)}
                              </td>
                              <td className="fd-col-right">{formatNgn(row.total_price)}</td>
                              <td>{row.platform || '—'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    {reportPreview.rows.length > 100 ? (
                      <p className="muted fd-report-preview-note">Showing first 100 rows. Export for the full ledger (up to 5,000).</p>
                    ) : null}
                  </div>
                ) : reportPreview && reportType === 'movements' ? (
                  <div className="fd-movements-preview">
                    <h3 className="fd-subheading">Arrivals</h3>
                    <div className="fd-table-wrap">
                      <table className="fd-table fd-table--wide">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Guest</th>
                            <th>Property</th>
                            <th>Unit</th>
                            <th>Check-in</th>
                            <th>Check-out</th>
                            <th>Status</th>
                            <th>Payment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {!Array.isArray(reportPreview.arrivals) || reportPreview.arrivals.length === 0 ? (
                            <EmptyTableRow colSpan={8} message="No arrivals in this range." />
                          ) : (
                            reportPreview.arrivals.slice(0, 50).map((row) => (
                              <tr key={`a-${row.id}`}>
                                <td>#{row.id}</td>
                                <td>{row.guest || '—'}</td>
                                <td>{row.property || '—'}</td>
                                <td>{row.apartment || '—'}</td>
                                <td>{row.start_date}</td>
                                <td>{row.end_date}</td>
                                <td>{row.status}</td>
                                <td>{row.payment_status}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    <h3 className="fd-subheading">Departures</h3>
                    <div className="fd-table-wrap">
                      <table className="fd-table fd-table--wide">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Guest</th>
                            <th>Property</th>
                            <th>Unit</th>
                            <th>Check-in</th>
                            <th>Check-out</th>
                            <th>Status</th>
                            <th>Payment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {!Array.isArray(reportPreview.departures) || reportPreview.departures.length === 0 ? (
                            <EmptyTableRow colSpan={8} message="No departures in this range." />
                          ) : (
                            reportPreview.departures.slice(0, 50).map((row) => (
                              <tr key={`d-${row.id}`}>
                                <td>#{row.id}</td>
                                <td>{row.guest || '—'}</td>
                                <td>{row.property || '—'}</td>
                                <td>{row.apartment || '—'}</td>
                                <td>{row.start_date}</td>
                                <td>{row.end_date}</td>
                                <td>{row.status}</td>
                                <td>{row.payment_status}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : reportPreview && reportType === 'payroll' && Array.isArray(reportPreview.rows) ? (
                  <div className="fd-table-wrap fd-table-wrap--scroll">
                    <table className="fd-table fd-table--wide">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Property</th>
                          <th>Staff</th>
                          <th>Period</th>
                          <th className="fd-col-right">Gross ₦</th>
                          <th className="fd-col-right">Net ₦</th>
                          <th>Paid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportPreview.rows.length === 0 ? (
                          <EmptyTableRow colSpan={7} message="No payroll slips in this period range." />
                        ) : (
                          reportPreview.rows.slice(0, 100).map((row) => (
                            <tr key={row.id}>
                              <td>#{row.id}</td>
                              <td>{row.property || '—'}</td>
                              <td>{row.staff || '—'}</td>
                              <td>{row.period || '—'}</td>
                              <td className="fd-col-right">{formatNgn(row.gross_amount_ngn)}</td>
                              <td className="fd-col-right">{formatNgn(row.net_amount_ngn)}</td>
                              <td>{row.paid_at || '—'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    {reportPreview.rows.length > 100 ? (
                      <p className="muted fd-report-preview-note">Showing first 100 rows. Export for the full register.</p>
                    ) : null}
                  </div>
                ) : reportPreview && reportType === 'staff_roster' && Array.isArray(reportPreview.rows) ? (
                  <div className="fd-table-wrap fd-table-wrap--scroll">
                    <table className="fd-table fd-table--wide">
                      <thead>
                        <tr>
                          <th>Property</th>
                          <th>Name</th>
                          <th>Job title</th>
                          <th>Active</th>
                          <th>Hired</th>
                          <th>Exit</th>
                          <th>Exit type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportPreview.rows.length === 0 ? (
                          <EmptyTableRow colSpan={7} message="No staff match this range and filters." />
                        ) : (
                          reportPreview.rows.slice(0, 150).map((row) => (
                            <tr key={row.id}>
                              <td>{row.property || '—'}</td>
                              <td>{row.name || '—'}</td>
                              <td>{row.job_title || '—'}</td>
                              <td>{row.active || '—'}</td>
                              <td>{row.hired_at || '—'}</td>
                              <td>{row.separated_at || '—'}</td>
                              <td>{row.separation_type || '—'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    {reportPreview.rows.length > 150 ? (
                      <p className="muted fd-report-preview-note">Showing first 150 rows. Export for the full roster.</p>
                    ) : null}
                  </div>
                ) : reportPreview && reportType === 'profit_and_loss' && reportPreview.revenue_ngn != null ? (
                  <div className="fd-pl-preview">
                    <p className="muted fd-pl-basis">{reportPreview.basis}</p>
                    <div className="fd-pl-summary" role="table" aria-label="Profit and loss summary">
                      <div className="fd-pl-row fd-pl-row--head">
                        <span>Line</span>
                        <span className="fd-pl-amt">Amount (₦)</span>
                      </div>
                      <div className="fd-pl-row">
                        <span>Revenue (paid bookings)</span>
                        <span className="fd-pl-amt">{formatNgn(reportPreview.revenue_ngn)}</span>
                      </div>
                      <div className="fd-pl-row fd-pl-row--muted">
                        <span>Paid bookings (count)</span>
                        <span className="fd-pl-amt">{Number(reportPreview.paid_bookings_count ?? 0).toLocaleString()}</span>
                      </div>
                      <div className="fd-pl-row">
                        <span>Less: expenses</span>
                        <span className="fd-pl-amt">−{formatNgn(reportPreview.expenses_total_ngn)}</span>
                      </div>
                      <div className="fd-pl-row">
                        <span>Less: purchases</span>
                        <span className="fd-pl-amt">−{formatNgn(reportPreview.purchases_total_ngn)}</span>
                      </div>
                      <div className="fd-pl-row fd-pl-row--subtotal">
                        <span>Total costs</span>
                        <span className="fd-pl-amt">{formatNgn(reportPreview.total_costs_ngn)}</span>
                      </div>
                      <div
                        className={`fd-pl-row fd-pl-row--net ${
                          Number(reportPreview.net_income_ngn) < 0 ? 'fd-pl-row--net-neg' : ''
                        }`}
                      >
                        <span>Net income</span>
                        <span className="fd-pl-amt">{formatNgn(reportPreview.net_income_ngn)}</span>
                      </div>
                    </div>
                    {Array.isArray(reportPreview.revenue_breakdown) && reportPreview.revenue_breakdown.length > 0 ? (
                      <>
                        <h3 className="fd-subheading">{reportPreview.revenue_breakdown_label || 'Revenue by property'}</h3>
                        <div className="fd-table-wrap">
                          <table className="fd-table">
                            <thead>
                              <tr>
                                <th>{reportPreview.revenue_breakdown_scope === 'apartment' ? 'Apartment' : 'Property'}</th>
                                <th className="fd-col-center">Paid bookings</th>
                                <th className="fd-col-right">Revenue (₦)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reportPreview.revenue_breakdown.map((row) => (
                                <tr key={row.apartment_id || row.property_id}>
                                  <td>{reportPreview.revenue_breakdown_scope === 'apartment' ? row.apartment_name : row.property_name}</td>
                                  <td className="fd-col-center">{row.paid_bookings}</td>
                                  <td className="fd-col-right">{formatNgn(row.revenue_ngn)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : null}
                    {Array.isArray(reportPreview.expenses_by_type) && reportPreview.expenses_by_type.length > 0 ? (
                      <>
                        <h3 className="fd-subheading">Expenses by type</h3>
                        <div className="fd-table-wrap">
                          <table className="fd-table">
                            <thead>
                              <tr>
                                <th>Type</th>
                                <th className="fd-col-center">Entries</th>
                                <th className="fd-col-right">Total ₦</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reportPreview.expenses_by_type.map((row, idx) => (
                                <tr key={`exp-${idx}`}>
                                  <td>{row.expense_type}</td>
                                  <td className="fd-col-center">{row.entry_count}</td>
                                  <td className="fd-col-right">{formatNgn(row.total_amount_ngn)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : null}
                    {Array.isArray(reportPreview.purchases_by_type) && reportPreview.purchases_by_type.length > 0 ? (
                      <>
                        <h3 className="fd-subheading">Purchases by type</h3>
                        <div className="fd-table-wrap">
                          <table className="fd-table">
                            <thead>
                              <tr>
                                <th>Type</th>
                                <th className="fd-col-center">Entries</th>
                                <th className="fd-col-right">Total ₦</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reportPreview.purchases_by_type.map((row, idx) => (
                                <tr key={`pur-${idx}`}>
                                  <td>{row.purchase_type}</td>
                                  <td className="fd-col-center">{row.entry_count}</td>
                                  <td className="fd-col-right">{formatNgn(row.total_amount_ngn)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : null}
                  </div>
                ) : reportPreview &&
                  (reportType === 'expenses_summary' || reportType === 'purchases_summary') &&
                  Array.isArray(reportPreview.rows) ? (
                  <div className="fd-table-wrap">
                    <table className="fd-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th className="fd-col-center">Entries</th>
                          <th className="fd-col-right">Total ₦</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportPreview.rows.length === 0 ? (
                          <EmptyTableRow colSpan={3} message="No rows in this range." />
                        ) : (
                          reportPreview.rows.map((row, idx) => (
                            <tr key={`${reportType}-${idx}`}>
                              <td>{row.expense_type || row.purchase_type || '—'}</td>
                              <td className="fd-col-center">{row.entry_count}</td>
                              <td className="fd-col-right">{formatNgn(row.total_amount_ngn)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="muted">Adjust filters above; preview refreshes when dates or report type change.</p>
                )}
              </div>
              ) : null}
            </>
          )}

          {view === 'staff-roles' && (
            <FrontDeskPermissionRolesPanel
              propertyId={propertyId}
              propertyOptions={properties}
              isPlatformAdmin={isPlatformAdminUser}
              canManage={canManagePermissionTemplates}
              setError={setError}
              setSuccessMessage={setSuccessMessage}
              refreshTick={catalogRefreshTick}
            />
          )}

          {view === 'users' && (
            <FrontDeskUsersPanel
              propertyId={propertyId}
              teamRows={teamRows}
              teamLoading={teamLoading}
              staffMeta={staffMeta}
              staffPage={staffPage}
              setStaffPage={setStaffPage}
              staffPerPage={staffPerPage}
              setStaffPerPage={setStaffPerPage}
              staffQDraft={staffQDraft}
              setStaffQDraft={setStaffQDraft}
              staffKycFilter={staffKycFilter}
              setStaffKycFilter={setStaffKycFilter}
              staffIncludeInactive={staffIncludeInactive}
              setStaffIncludeInactive={setStaffIncludeInactive}
              loadTeamList={loadTeamList}
              canStaffCreateFd={canStaffCreateFd}
              canStaffUserCreateFd={canStaffUserCreateFd}
              canStaffRecordCreateFd={canStaffRecordCreateFd}
              canStaffUpdateFd={canStaffUpdateFd}
              canStaffDeleteFd={canStaffDeleteFd}
              canPropertyOwnerAssignFd={canPropertyOwnerAssignFd}
              showKycReviewFd={showKycReviewFd}
              setKycEditAssignment={setKycEditAssignment}
              setError={setError}
              setSuccessMessage={setSuccessMessage}
              apartmentOptions={bookingsApartmentOptions}
            />
          )}

          {view === 'catalog-discounts' && (
            <FrontDeskCatalogPanel
              kind="discounts"
              propertyId={propertyId}
              apartmentOptions={bookingsApartmentOptions}
              canView={CATALOG_DISCOUNT_NAV_ABILITIES.some((a) =>
                canStaffAbilityForFrontDesk(propertyId || null, a),
              )}
              canManage={
                !!propertyId && canStaffAbilityForFrontDesk(propertyId, 'catalog.discounts_manage')
              }
              setError={setError}
              setSuccessMessage={setSuccessMessage}
              refreshTick={catalogRefreshTick}
            />
          )}

          {view === 'catalog-surcharges' && (
            <FrontDeskCatalogPanel
              kind="surcharges"
              propertyId={propertyId}
              apartmentOptions={bookingsApartmentOptions}
              canView={CATALOG_SURCHARGE_NAV_ABILITIES.some((a) =>
                canStaffAbilityForFrontDesk(propertyId || null, a),
              )}
              canManage={
                !!propertyId && canStaffAbilityForFrontDesk(propertyId, 'catalog.surcharges_manage')
              }
              setError={setError}
              setSuccessMessage={setSuccessMessage}
              refreshTick={catalogRefreshTick}
            />
          )}

          {view === 'catalog-blocked-dates' && (
            <FrontDeskCatalogPanel
              kind="blocked-dates"
              propertyId={propertyId}
              apartmentOptions={bookingsApartmentOptions}
              canView={CATALOG_BLOCKED_NAV_ABILITIES.some((a) =>
                canStaffAbilityForFrontDesk(propertyId || null, a),
              )}
              canManage={
                !!propertyId && canStaffAbilityForFrontDesk(propertyId, 'catalog.blocked_dates_manage')
              }
              setError={setError}
              setSuccessMessage={setSuccessMessage}
              refreshTick={catalogRefreshTick}
            />
          )}

          {view === 'finance' && (
            <>
              <h2 className="front-desk-section-title">Finance &amp; HR</h2>
              <p className="front-desk-section-lead">
                Payroll, expenses, and purchases for the selected property. What you can do here follows granular abilities from Filament (view, create, edit, and delete are separate for payroll, expenses, and purchases).
              </p>
              {!propertyId ? (
                <p className="muted">Select a property above.</p>
              ) : allowedFinanceTabs.length === 0 ? (
                <p className="muted">You do not have payroll or finance permissions for this property.</p>
              ) : (
                <>
                  <div className="fd-finance-tabs" role="tablist" aria-label="Finance sections">
                    {allowedFinanceTabs.includes('payroll') ? (
                      <button
                        type="button"
                        role="tab"
                        aria-selected={financeTab === 'payroll'}
                        className={`fd-finance-tab${financeTab === 'payroll' ? ' fd-finance-tab--active' : ''}`}
                        onClick={() => setFinanceTab('payroll')}
                      >
                        Payroll
                      </button>
                    ) : null}
                    {allowedFinanceTabs.includes('expenses') ? (
                      <button
                        type="button"
                        role="tab"
                        aria-selected={financeTab === 'expenses'}
                        className={`fd-finance-tab${financeTab === 'expenses' ? ' fd-finance-tab--active' : ''}`}
                        onClick={() => setFinanceTab('expenses')}
                      >
                        Expenses
                      </button>
                    ) : null}
                    {allowedFinanceTabs.includes('purchases') ? (
                      <button
                        type="button"
                        role="tab"
                        aria-selected={financeTab === 'purchases'}
                        className={`fd-finance-tab${financeTab === 'purchases' ? ' fd-finance-tab--active' : ''}`}
                        onClick={() => setFinanceTab('purchases')}
                      >
                        Purchases
                      </button>
                    ) : null}
                  </div>
                  {financeLoading ? (
                    <p className="muted">Loading…</p>
                  ) : financeTab === 'payroll' ? (
                    <>
                      {canPayrollCreateFd ? (
                        <section className="fd-finance-card" aria-labelledby="fd-payroll-form-title">
                          <header className="fd-finance-card__head">
                            <h3 id="fd-payroll-form-title" className="fd-finance-card__title">
                              Record payroll slip
                            </h3>
                            <p className="fd-finance-card__lede">
                              One row per staff member per calendar month. Amounts in Nigerian naira (₦).
                            </p>
                          </header>
                          <form
                            className="fd-finance-form"
                            onSubmit={async (e) => {
                              e.preventDefault();
                              setFdSaving(true);
                              setError('');
                              try {
                                const grossParsed = parseWholeNairaToNumber(payrollFormFd.gross_amount_ngn);
                                const dedParsed = parseWholeNairaToNumber(payrollFormFd.deductions_ngn);
                                await frontDeskApi.createPayroll(propertyId, {
                                  property_staff_assignment_id: Number(payrollFormFd.property_staff_assignment_id),
                                  period_year: Number(payrollFormFd.period_year),
                                  period_month: Number(payrollFormFd.period_month),
                                  gross_amount_ngn: grossParsed,
                                  deductions_ngn: Number.isFinite(dedParsed) ? dedParsed : 0,
                                  paid_at: payrollFormFd.paid_at || null,
                                  payment_reference: payrollFormFd.payment_reference || null,
                                  slip_notes: payrollFormFd.slip_notes || null,
                                });
                                setSuccessMessage('Payroll slip recorded.');
                                const res = await frontDeskApi.payroll(propertyId, fdPayrollListParams);
                                setFdPayrollRows(Array.isArray(res.data) ? res.data : []);
                                setFdPayrollMeta(res.meta || null);
                              } catch (err) {
                                setError(err.message || 'Could not record payroll');
                              } finally {
                                setFdSaving(false);
                              }
                            }}
                          >
                            <div className="fd-finance-form__grid">
                              <label className="fd-finance-field fd-finance-field--span2">
                                <span className="fd-finance-field__label">Staff member</span>
                                <select
                                  className="fd-finance-input"
                                  required
                                  value={payrollFormFd.property_staff_assignment_id}
                                  onChange={(ev) => {
                                    const id = ev.target.value;
                                    const row = payrollStaffPickerRows.find((r) => String(r.id) === id);
                                    const g = row?.suggested_payroll_gross_ngn;
                                    const useGross =
                                      id &&
                                      row &&
                                      g != null &&
                                      !Number.isNaN(Number(g));
                                    setPayrollFormFd((f) => ({
                                      ...f,
                                      property_staff_assignment_id: id,
                                      gross_amount_ngn: useGross ? formatWholeNairaFromNumber(g) : id ? '' : '',
                                    }));
                                  }}
                                >
                                  <option value="">Select assignment…</option>
                                  {payrollStaffPickerRows.map((row) => (
                                    <option key={row.id} value={row.id}>
                                      {row.user?.name || row.user?.email || `Assignment #${row.id}`}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <label className="fd-finance-field">
                                <span className="fd-finance-field__label">Year</span>
                                <input
                                  className="fd-finance-input"
                                  type="number"
                                  required
                                  min={2000}
                                  max={2100}
                                  value={payrollFormFd.period_year}
                                  onChange={(ev) =>
                                    setPayrollFormFd((f) => ({ ...f, period_year: Number(ev.target.value) }))
                                  }
                                />
                              </label>
                              <label className="fd-finance-field">
                                <span className="fd-finance-field__label">Month</span>
                                <select
                                  className="fd-finance-input"
                                  required
                                  value={payrollFormFd.period_month}
                                  onChange={(ev) =>
                                    setPayrollFormFd((f) => ({
                                      ...f,
                                      period_month: Number(ev.target.value),
                                    }))
                                  }
                                >
                                  {PAYROLL_MONTH_OPTIONS.map((mo) => (
                                    <option key={mo.value} value={mo.value}>
                                      {mo.label}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <label className="fd-finance-field">
                                <span className="fd-finance-field__label">Gross amount (₦)</span>
                                <input
                                  className="fd-finance-input luxistt-money-input"
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="off"
                                  required
                                  value={payrollFormFd.gross_amount_ngn}
                                  onChange={(ev) =>
                                    setPayrollFormFd((f) => ({
                                      ...f,
                                      gross_amount_ngn: formatWholeNairaInputChange(ev.target.value),
                                    }))
                                  }
                                />
                              </label>
                              <label className="fd-finance-field">
                                <span className="fd-finance-field__label">Deductions (₦)</span>
                                <input
                                  className="fd-finance-input luxistt-money-input"
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="off"
                                  value={payrollFormFd.deductions_ngn}
                                  onChange={(ev) =>
                                    setPayrollFormFd((f) => ({
                                      ...f,
                                      deductions_ngn: formatWholeNairaInputChange(ev.target.value),
                                    }))
                                  }
                                />
                              </label>
                              <label className="fd-finance-field">
                                <span className="fd-finance-field__label">Paid date</span>
                                <input
                                  className="fd-finance-input"
                                  type="date"
                                  value={payrollFormFd.paid_at}
                                  onChange={(ev) => setPayrollFormFd((f) => ({ ...f, paid_at: ev.target.value }))}
                                />
                              </label>
                              <label className="fd-finance-field">
                                <span className="fd-finance-field__label">Payment reference</span>
                                <input
                                  className="fd-finance-input"
                                  value={payrollFormFd.payment_reference}
                                  onChange={(ev) =>
                                    setPayrollFormFd((f) => ({ ...f, payment_reference: ev.target.value }))
                                  }
                                />
                              </label>
                              <label className="fd-finance-field fd-finance-field--span2">
                                <span className="fd-finance-field__label">Internal notes</span>
                                <textarea
                                  className="fd-finance-input fd-finance-input--textarea"
                                  value={payrollFormFd.slip_notes}
                                  onChange={(ev) => setPayrollFormFd((f) => ({ ...f, slip_notes: ev.target.value }))}
                                  rows={3}
                                  placeholder="Optional context for this slip…"
                                />
                              </label>
                            </div>
                            <div className="fd-finance-form__actions">
                              <button type="submit" className="btn-primary" disabled={fdSaving}>
                                Record payroll slip
                              </button>
                            </div>
                          </form>
                        </section>
                      ) : null}
                      <section className="fd-finance-card fd-finance-card--table" aria-label="Payroll history">
                        <header className="fd-finance-card__head fd-finance-card__head--row">
                          <h3 className="fd-finance-card__title">Payroll history</h3>
                        </header>
                        <div
                          className="fd-bookings-toolbar fd-finance-ledger-toolbar"
                          style={{ marginBottom: '1rem', flexWrap: 'wrap' }}
                        >
                          <label className="fd-bookings-toolbar-field">
                            <span className="fd-bookings-toolbar-label">Search</span>
                            <input
                              type="search"
                              value={fdPayrollFilterQDraft}
                              onChange={(ev) => setFdPayrollFilterQDraft(ev.target.value)}
                              placeholder="Staff, reference, notes…"
                            />
                          </label>
                          <label className="fd-bookings-toolbar-field">
                            <span className="fd-bookings-toolbar-label">Staff</span>
                            <select
                              value={fdPayrollFilterAssignmentId}
                              onChange={(ev) => setFdPayrollFilterAssignmentId(ev.target.value)}
                              className="fd-finance-input"
                              style={{ minWidth: 160 }}
                            >
                              <option value="">All staff</option>
                              {payrollStaffPickerRows.map((row) => (
                                <option key={row.id} value={String(row.id)}>
                                  {row.user?.name || row.user?.email || `Assignment #${row.id}`}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="fd-bookings-toolbar-field">
                            <span className="fd-bookings-toolbar-label">Year</span>
                            <input
                              type="number"
                              min={2000}
                              max={2100}
                              placeholder="Any"
                              value={fdPayrollFilterYear}
                              onChange={(ev) => setFdPayrollFilterYear(ev.target.value)}
                              style={{ width: 88 }}
                            />
                          </label>
                          <label className="fd-bookings-toolbar-field">
                            <span className="fd-bookings-toolbar-label">Month</span>
                            <select
                              value={fdPayrollFilterMonth}
                              onChange={(ev) => setFdPayrollFilterMonth(ev.target.value)}
                              className="fd-finance-input"
                              style={{ minWidth: 120 }}
                            >
                              <option value="">Any</option>
                              {PAYROLL_MONTH_OPTIONS.map((mo) => (
                                <option key={mo.value} value={String(mo.value)}>
                                  {mo.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <div className="fd-bookings-toolbar-actions">
                            <button
                              type="button"
                              className="btn-ghost"
                              onClick={() => {
                                setFdPayrollFilterQDraft('');
                                setFdPayrollFilterQ('');
                                setFdPayrollFilterYear('');
                                setFdPayrollFilterMonth('');
                                setFdPayrollFilterAssignmentId('');
                              }}
                            >
                              Clear filters
                            </button>
                          </div>
                          <FdPerPageField
                            id="fd-payroll-per-page"
                            value={fdFinancePerPage}
                            onChange={setFdFinancePerPage}
                            maxAllowed={100}
                          />
                        </div>
                        <div className="fd-table-wrap fd-finance-table-wrap">
                          <table className="fd-table fd-table--wide">
                            <thead>
                              <tr>
                                <th>Staff</th>
                                <th>Period</th>
                                <th>Gross</th>
                                <th>Net</th>
                                <th>Paid</th>
                                {showPayrollRowActionsFd ? (
                                  <th className="fd-col-nowrap fd-actions-col-end">Actions</th>
                                ) : null}
                              </tr>
                            </thead>
                            <tbody>
                              {fdPayrollRows.length === 0 ? (
                                <EmptyTableRow
                                  colSpan={showPayrollRowActionsFd ? 6 : 5}
                                  message="No payroll slips for this property."
                                />
                              ) : (
                                fdPayrollRows.map((row) => (
                                  <tr key={row.id}>
                                    <td>{row.staff_name || '—'}</td>
                                    <td className="fd-col-nowrap">{formatPayrollPeriodLabel(row)}</td>
                                    <td>{formatNgn(row.gross_amount_ngn)}</td>
                                    <td>{formatNgn(row.net_amount_ngn)}</td>
                                    <td>{row.paid_at || '—'}</td>
                                    {showPayrollRowActionsFd ? (
                                      <td className="fd-actions-col-end">
                                        <div className="fd-finance-row-actions">
                                          {canPayrollSlipFd ? (
                                            <button
                                              type="button"
                                              className="btn-ghost fd-finance-row-action"
                                              disabled={fdSaving || fdPayrollSlipLoadingId === row.id}
                                              title="View PDF payslip"
                                              onClick={async () => {
                                                setFdPayrollSlipLoadingId(row.id);
                                                setError('');
                                                try {
                                                  const blob = await downloadFrontDeskPayrollSlip(row.id);
                                                  const how = openPdfBlobInNewTab(blob, { filename: `luxistt-payslip-${row.id}.pdf` });
                                                  setSuccessMessage(
                                                    how === 'viewer'
                                                      ? 'Payslip opened in the PDF viewer.'
                                                      : 'Payslip saved to your device. Open the file to view or print.',
                                                  );
                                                } catch (err) {
                                                  setError(err.message || 'Could not open payslip');
                                                } finally {
                                                  setFdPayrollSlipLoadingId(null);
                                                }
                                              }}
                                            >
                                              <FiDownload className="fd-payroll-pdf-icon" size={14} aria-hidden />
                                              PDF
                                            </button>
                                          ) : null}
                                          {canPayrollDeleteFd ? (
                                            <button
                                              type="button"
                                              className="btn-ghost fd-finance-row-action"
                                              disabled={fdSaving}
                                              onClick={async () => {
                                                const ok = await luxisttConfirm({
                                                  title: 'Delete payroll slip?',
                                                  text: 'This cannot be undone.',
                                                  icon: 'warning',
                                                  confirmText: 'Delete',
                                                  danger: true,
                                                });
                                                if (!ok) return;
                                                setFdSaving(true);
                                                setError('');
                                                try {
                                                  await frontDeskApi.deletePayroll(row.id);
                                                  const res = await frontDeskApi.payroll(propertyId, fdPayrollListParams);
                                                  setFdPayrollRows(Array.isArray(res.data) ? res.data : []);
                                                  setFdPayrollMeta(res.meta || null);
                                                  setSuccessMessage('Payroll slip removed.');
                                                } catch (err) {
                                                  setError(err.message || 'Could not delete');
                                                } finally {
                                                  setFdSaving(false);
                                                }
                                              }}
                                            >
                                              Delete
                                            </button>
                                          ) : null}
                                        </div>
                                      </td>
                                    ) : null}
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                        {fdPayrollMeta ? (
                          <FdPaginationNav
                            meta={fdPayrollMeta}
                            page={fdPayrollPage}
                            setPage={setFdPayrollPage}
                            disabled={financeLoading}
                          />
                        ) : null}
                      </section>
                    </>
                  ) : financeTab === 'expenses' ? (
                    <>
                      {canExpenseCreateFd ? (
                        <section className="fd-finance-card" aria-labelledby="fd-expense-form-title">
                          <header className="fd-finance-card__head">
                            <h3 id="fd-expense-form-title" className="fd-finance-card__title">
                              Add expense
                            </h3>
                            <p className="fd-finance-card__lede">
                              Log property or unit costs; owners are notified when applicable.
                            </p>
                          </header>
                          <form
                            className="fd-finance-form"
                            onSubmit={async (e) => {
                              e.preventDefault();
                              setFdSaving(true);
                              setError('');
                              try {
                                await frontDeskApi.createExpense(propertyId, {
                                  amount: parseNairaDecimalToNumber(expenseFormFd.amount),
                                  expense_type_id: Number(expenseFormFd.expense_type_id),
                                  expense_date: expenseFormFd.expense_date,
                                  description: expenseFormFd.description || null,
                                  apartment_id: expenseFormFd.apartment_id ? Number(expenseFormFd.apartment_id) : null,
                                });
                                setSuccessMessage('Expense recorded.');
                                const res = await frontDeskApi.expenses(propertyId, fdExpenseListParams);
                                setFdExpenseRows(Array.isArray(res.data) ? res.data : []);
                                setFdExpenseMeta(res.meta || null);
                                if (canReportsViewFd) await loadReportPreview();
                                setExpenseFormFd((f) => ({
                                  ...f,
                                  amount: '',
                                  description: '',
                                  apartment_id: '',
                                }));
                              } catch (err) {
                                setError(err.message || 'Could not add expense');
                              } finally {
                                setFdSaving(false);
                              }
                            }}
                          >
                            <div className="fd-finance-form__grid">
                              <label className="fd-finance-field fd-finance-field--span2">
                                <span className="fd-finance-field__label">Category</span>
                                <select
                                  className="fd-finance-input"
                                  required
                                  value={expenseFormFd.expense_type_id}
                                  onChange={(ev) =>
                                    setExpenseFormFd((f) => ({ ...f, expense_type_id: ev.target.value }))
                                  }
                                >
                                  <option value="">Select type…</option>
                                  {fdExpenseTypes.map((t) => (
                                    <option key={t.id} value={t.id}>
                                      {t.name}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <label className="fd-finance-field">
                                <span className="fd-finance-field__label">Amount (₦)</span>
                                <input
                                  className="fd-finance-input luxistt-money-input"
                                  type="text"
                                  inputMode="decimal"
                                  autoComplete="off"
                                  required
                                  value={expenseFormFd.amount}
                                  onChange={(ev) =>
                                    setExpenseFormFd((f) => ({
                                      ...f,
                                      amount: formatNairaDecimalInputChange(ev.target.value, 2),
                                    }))
                                  }
                                />
                              </label>
                              <label className="fd-finance-field">
                                <span className="fd-finance-field__label">Date incurred</span>
                                <input
                                  className="fd-finance-input"
                                  type="date"
                                  required
                                  value={expenseFormFd.expense_date}
                                  onChange={(ev) => setExpenseFormFd((f) => ({ ...f, expense_date: ev.target.value }))}
                                />
                              </label>
                              <label className="fd-finance-field fd-finance-field--span2">
                                <span className="fd-finance-field__label">Apartment (optional)</span>
                                <select
                                  className="fd-finance-input"
                                  value={expenseFormFd.apartment_id}
                                  onChange={(ev) => setExpenseFormFd((f) => ({ ...f, apartment_id: ev.target.value }))}
                                >
                                  <option value="">Whole property</option>
                                  {bookingsApartmentOptions.map((a) => (
                                    <option key={a.id} value={a.id}>
                                      {a.name}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <label className="fd-finance-field fd-finance-field--span2">
                                <span className="fd-finance-field__label">Description</span>
                                <textarea
                                  className="fd-finance-input fd-finance-input--textarea"
                                  value={expenseFormFd.description}
                                  onChange={(ev) => setExpenseFormFd((f) => ({ ...f, description: ev.target.value }))}
                                  rows={3}
                                  placeholder="What was purchased or paid for…"
                                />
                              </label>
                            </div>
                            <div className="fd-finance-form__actions">
                              <button type="submit" className="btn-primary" disabled={fdSaving}>
                                Save expense
                              </button>
                            </div>
                          </form>
                        </section>
                      ) : null}
                      <section className="fd-finance-card fd-finance-card--table" aria-label="Expense ledger">
                        <header className="fd-finance-card__head fd-finance-card__head--row">
                          <h3 className="fd-finance-card__title">Expense ledger</h3>
                        </header>
                        <div
                          className="fd-bookings-toolbar fd-finance-ledger-toolbar"
                          style={{ marginBottom: '1rem', flexWrap: 'wrap' }}
                        >
                          <label className="fd-bookings-toolbar-field">
                            <span className="fd-bookings-toolbar-label">Search</span>
                            <input
                              type="search"
                              value={fdExpenseFilterQDraft}
                              onChange={(ev) => setFdExpenseFilterQDraft(ev.target.value)}
                              placeholder="Description or ID…"
                            />
                          </label>
                          <label className="fd-bookings-toolbar-field">
                            <span className="fd-bookings-toolbar-label">Type</span>
                            <select
                              value={fdExpenseFilterTypeId}
                              onChange={(ev) => setFdExpenseFilterTypeId(ev.target.value)}
                              className="fd-finance-input"
                              style={{ minWidth: 140 }}
                            >
                              <option value="">All types</option>
                              {fdExpenseTypes.map((t) => (
                                <option key={t.id} value={String(t.id)}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="fd-bookings-toolbar-field">
                            <span className="fd-bookings-toolbar-label">From</span>
                            <input
                              type="date"
                              value={fdExpenseFilterDateFrom}
                              onChange={(ev) => setFdExpenseFilterDateFrom(ev.target.value)}
                            />
                          </label>
                          <label className="fd-bookings-toolbar-field">
                            <span className="fd-bookings-toolbar-label">To</span>
                            <input
                              type="date"
                              value={fdExpenseFilterDateTo}
                              onChange={(ev) => setFdExpenseFilterDateTo(ev.target.value)}
                            />
                          </label>
                          <div className="fd-bookings-toolbar-actions">
                            <button
                              type="button"
                              className="btn-ghost"
                              onClick={() => {
                                setFdExpenseFilterQDraft('');
                                setFdExpenseFilterQ('');
                                setFdExpenseFilterTypeId('');
                                setFdExpenseFilterDateFrom('');
                                setFdExpenseFilterDateTo('');
                              }}
                            >
                              Clear filters
                            </button>
                          </div>
                          <FdPerPageField
                            id="fd-expense-per-page"
                            value={fdFinancePerPage}
                            onChange={setFdFinancePerPage}
                            maxAllowed={100}
                          />
                        </div>
                        <div className="fd-table-wrap fd-finance-table-wrap">
                          <table className="fd-table fd-table--wide">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Amount</th>
                                <th>Apartment</th>
                                <th>Notes</th>
                                {showExpenseRowActionsFd ? <th className="fd-col-nowrap fd-actions-col-end">Actions</th> : null}
                              </tr>
                            </thead>
                            <tbody>
                              {fdExpenseRows.length === 0 ? (
                                <EmptyTableRow
                                  colSpan={showExpenseRowActionsFd ? 6 : 5}
                                  message="No expenses for this property."
                                />
                              ) : (
                                fdExpenseRows.map((ex) => (
                                  <tr key={ex.id}>
                                    <td className="fd-col-nowrap">{(ex.expense_date || '').slice(0, 10)}</td>
                                    <td>{ex.expense_type?.name || ex.category || '—'}</td>
                                    <td>{formatNgn(ex.amount)}</td>
                                    <td>{ex.apartment?.name || '—'}</td>
                                    <td style={{ maxWidth: 220 }} className="muted">
                                      {ex.description || '—'}
                                    </td>
                                    {showExpenseRowActionsFd ? (
                                      <td className="fd-actions-col-end">
                                        <div className="fd-finance-row-actions">
                                          {canExpenseUpdateFd ? (
                                            <button
                                              type="button"
                                              className="btn-ghost fd-finance-row-action"
                                              disabled={fdSaving}
                                              onClick={() =>
                                                setExpenseEditDraft({
                                                  id: ex.id,
                                                  amount: formatNairaDecimalFromNumber(ex.amount ?? '', 2),
                                                  expense_type_id: String(
                                                    ex.expense_type_id ?? ex.expense_type?.id ?? '',
                                                  ),
                                                  expense_date: (ex.expense_date || '').slice(0, 10),
                                                  description: ex.description || '',
                                                  apartment_id: ex.apartment_id ? String(ex.apartment_id) : '',
                                                })
                                              }
                                            >
                                              Edit
                                            </button>
                                          ) : null}
                                          {canExpenseDeleteFd ? (
                                            <button
                                              type="button"
                                              className="btn-ghost fd-finance-row-action"
                                              disabled={fdSaving}
                                              onClick={async () => {
                                                const ok = await luxisttConfirm({
                                                  title: 'Delete expense?',
                                                  text: 'Remove this expense from records?',
                                                  icon: 'warning',
                                                  danger: true,
                                                });
                                                if (!ok) return;
                                                setFdSaving(true);
                                                setError('');
                                                try {
                                                  await frontDeskApi.deleteExpense(ex.id);
                                                  const res = await frontDeskApi.expenses(propertyId, fdExpenseListParams);
                                                  setFdExpenseRows(Array.isArray(res.data) ? res.data : []);
                                                  setFdExpenseMeta(res.meta || null);
                                                  if (canReportsViewFd) await loadReportPreview();
                                                  setSuccessMessage('Expense deleted.');
                                                } catch (err) {
                                                  setError(err.message || 'Could not delete');
                                                } finally {
                                                  setFdSaving(false);
                                                }
                                              }}
                                            >
                                              Delete
                                            </button>
                                          ) : null}
                                        </div>
                                      </td>
                                    ) : null}
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                        {fdExpenseMeta ? (
                          <FdPaginationNav
                            meta={fdExpenseMeta}
                            page={fdExpensePage}
                            setPage={setFdExpensePage}
                            disabled={financeLoading}
                          />
                        ) : null}
                      </section>
                      {expenseEditDraft ? (
                        <div
                          className="modal-backdrop fd-finance-edit-overlay"
                          role="presentation"
                          onClick={() => {
                            if (!fdSaving) setExpenseEditDraft(null);
                          }}
                        >
                          <div
                            className="fd-finance-edit-modal"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="fd-expense-edit-title"
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            <h3 id="fd-expense-edit-title" className="fd-finance-edit-modal__title">
                              Edit expense
                            </h3>
                            <form
                              className="fd-finance-form"
                              onSubmit={async (e) => {
                                e.preventDefault();
                                setFdSaving(true);
                                setError('');
                                try {
                                  await frontDeskApi.updateExpense(expenseEditDraft.id, {
                                    amount: parseNairaDecimalToNumber(expenseEditDraft.amount),
                                    expense_type_id: Number(expenseEditDraft.expense_type_id),
                                    expense_date: expenseEditDraft.expense_date,
                                    description: expenseEditDraft.description || null,
                                    apartment_id: expenseEditDraft.apartment_id
                                      ? Number(expenseEditDraft.apartment_id)
                                      : null,
                                  });
                                  setSuccessMessage('Expense updated.');
                                  const res = await frontDeskApi.expenses(propertyId, fdExpenseListParams);
                                  setFdExpenseRows(Array.isArray(res.data) ? res.data : []);
                                  setFdExpenseMeta(res.meta || null);
                                  if (canReportsViewFd) await loadReportPreview();
                                  setExpenseEditDraft(null);
                                } catch (err) {
                                  setError(err.message || 'Could not update expense');
                                } finally {
                                  setFdSaving(false);
                                }
                              }}
                            >
                              <div className="fd-finance-form__grid">
                                <label className="fd-finance-field fd-finance-field--span2">
                                  <span className="fd-finance-field__label">Category</span>
                                  <select
                                    className="fd-finance-input"
                                    required
                                    value={expenseEditDraft.expense_type_id}
                                    onChange={(ev) =>
                                      setExpenseEditDraft((d) => ({ ...d, expense_type_id: ev.target.value }))
                                    }
                                  >
                                    {fdExpenseTypes.map((t) => (
                                      <option key={t.id} value={t.id}>
                                        {t.name}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label className="fd-finance-field">
                                  <span className="fd-finance-field__label">Amount (₦)</span>
                                  <input
                                    className="fd-finance-input luxistt-money-input"
                                    type="text"
                                    inputMode="decimal"
                                    autoComplete="off"
                                    required
                                    value={expenseEditDraft.amount}
                                    onChange={(ev) =>
                                      setExpenseEditDraft((d) => ({
                                        ...d,
                                        amount: formatNairaDecimalInputChange(ev.target.value, 2),
                                      }))
                                    }
                                  />
                                </label>
                                <label className="fd-finance-field">
                                  <span className="fd-finance-field__label">Date</span>
                                  <input
                                    className="fd-finance-input"
                                    type="date"
                                    required
                                    value={expenseEditDraft.expense_date}
                                    onChange={(ev) =>
                                      setExpenseEditDraft((d) => ({ ...d, expense_date: ev.target.value }))
                                    }
                                  />
                                </label>
                                <label className="fd-finance-field fd-finance-field--span2">
                                  <span className="fd-finance-field__label">Apartment</span>
                                  <select
                                    className="fd-finance-input"
                                    value={expenseEditDraft.apartment_id}
                                    onChange={(ev) =>
                                      setExpenseEditDraft((d) => ({ ...d, apartment_id: ev.target.value }))
                                    }
                                  >
                                    <option value="">Whole property</option>
                                    {bookingsApartmentOptions.map((a) => (
                                      <option key={a.id} value={a.id}>
                                        {a.name}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label className="fd-finance-field fd-finance-field--span2">
                                  <span className="fd-finance-field__label">Description</span>
                                  <textarea
                                    className="fd-finance-input fd-finance-input--textarea"
                                    value={expenseEditDraft.description}
                                    onChange={(ev) =>
                                      setExpenseEditDraft((d) => ({ ...d, description: ev.target.value }))
                                    }
                                    rows={3}
                                  />
                                </label>
                              </div>
                              <div className="fd-finance-form__actions fd-finance-form__actions--split">
                                <button
                                  type="button"
                                  className="btn-ghost"
                                  disabled={fdSaving}
                                  onClick={() => setExpenseEditDraft(null)}
                                >
                                  Cancel
                                </button>
                                <button type="submit" className="btn-primary" disabled={fdSaving}>
                                  Save changes
                                </button>
                              </div>
                            </form>
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <>
                      {canPurchaseCreateFd ? (
                        <section className="fd-finance-card" aria-labelledby="fd-purchase-form-title">
                          <header className="fd-finance-card__head">
                            <h3 id="fd-purchase-form-title" className="fd-finance-card__title">
                              Add purchase
                            </h3>
                            <p className="fd-finance-card__lede">
                              Stock, supplies, and capex with unit price × quantity. Total updates in the ledger.
                            </p>
                          </header>
                          <form
                            className="fd-finance-form"
                            onSubmit={async (e) => {
                              e.preventDefault();
                              setFdSaving(true);
                              setError('');
                              try {
                                await frontDeskApi.createPurchase(propertyId, {
                                  item_name: purchaseFormFd.item_name,
                                  amount: parseNairaDecimalToNumber(purchaseFormFd.amount),
                                  quantity: parseWholeNairaToNumber(purchaseFormFd.quantity) || 1,
                                  purchase_date: purchaseFormFd.purchase_date,
                                  purchase_type_id: Number(purchaseFormFd.purchase_type_id),
                                  vendor: purchaseFormFd.vendor || null,
                                  description: purchaseFormFd.description || null,
                                  apartment_id: purchaseFormFd.apartment_id ? Number(purchaseFormFd.apartment_id) : null,
                                });
                                setSuccessMessage('Purchase recorded.');
                                const res = await frontDeskApi.purchases(propertyId, fdPurchaseListParams);
                                setFdPurchaseRows(Array.isArray(res.data) ? res.data : []);
                                setFdPurchaseMeta(res.meta || null);
                                if (canReportsViewFd) await loadReportPreview();
                                setPurchaseFormFd({
                                  item_name: '',
                                  amount: '',
                                  quantity: '1',
                                  purchase_date: todayISO(),
                                  purchase_type_id: '',
                                  vendor: '',
                                  description: '',
                                  apartment_id: '',
                                });
                              } catch (err) {
                                setError(err.message || 'Could not add purchase');
                              } finally {
                                setFdSaving(false);
                              }
                            }}
                          >
                            <div className="fd-finance-form__grid">
                              <label className="fd-finance-field fd-finance-field--span2">
                                <span className="fd-finance-field__label">Item / description</span>
                                <input
                                  className="fd-finance-input"
                                  required
                                  value={purchaseFormFd.item_name}
                                  onChange={(ev) =>
                                    setPurchaseFormFd((f) => ({ ...f, item_name: ev.target.value }))
                                  }
                                  placeholder="e.g. Linens — queen set"
                                />
                              </label>
                              <label className="fd-finance-field fd-finance-field--span2">
                                <span className="fd-finance-field__label">Purchase type</span>
                                <select
                                  className="fd-finance-input"
                                  required
                                  value={purchaseFormFd.purchase_type_id}
                                  onChange={(ev) =>
                                    setPurchaseFormFd((f) => ({ ...f, purchase_type_id: ev.target.value }))
                                  }
                                >
                                  <option value="">Select type…</option>
                                  {fdPurchaseTypes.map((t) => (
                                    <option key={t.id} value={t.id}>
                                      {t.name}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <label className="fd-finance-field">
                                <span className="fd-finance-field__label">Unit price (₦)</span>
                                <input
                                  className="fd-finance-input luxistt-money-input"
                                  type="text"
                                  inputMode="decimal"
                                  autoComplete="off"
                                  required
                                  value={purchaseFormFd.amount}
                                  onChange={(ev) =>
                                    setPurchaseFormFd((f) => ({
                                      ...f,
                                      amount: formatNairaDecimalInputChange(ev.target.value, 2),
                                    }))
                                  }
                                />
                              </label>
                              <label className="fd-finance-field">
                                <span className="fd-finance-field__label">Quantity</span>
                                <input
                                  className="fd-finance-input luxistt-money-input"
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="off"
                                  required
                                  value={purchaseFormFd.quantity}
                                  onChange={(ev) =>
                                    setPurchaseFormFd((f) => ({
                                      ...f,
                                      quantity: formatWholeNairaInputChange(ev.target.value),
                                    }))
                                  }
                                />
                              </label>
                              <label className="fd-finance-field">
                                <span className="fd-finance-field__label">Purchase date</span>
                                <input
                                  className="fd-finance-input"
                                  type="date"
                                  required
                                  value={purchaseFormFd.purchase_date}
                                  onChange={(ev) =>
                                    setPurchaseFormFd((f) => ({ ...f, purchase_date: ev.target.value }))
                                  }
                                />
                              </label>
                              <label className="fd-finance-field">
                                <span className="fd-finance-field__label">Vendor</span>
                                <input
                                  className="fd-finance-input"
                                  value={purchaseFormFd.vendor}
                                  onChange={(ev) => setPurchaseFormFd((f) => ({ ...f, vendor: ev.target.value }))}
                                  placeholder="Optional"
                                />
                              </label>
                              <label className="fd-finance-field fd-finance-field--span2">
                                <span className="fd-finance-field__label">Apartment (optional)</span>
                                <select
                                  className="fd-finance-input"
                                  value={purchaseFormFd.apartment_id}
                                  onChange={(ev) =>
                                    setPurchaseFormFd((f) => ({ ...f, apartment_id: ev.target.value }))
                                  }
                                >
                                  <option value="">Whole property</option>
                                  {bookingsApartmentOptions.map((a) => (
                                    <option key={a.id} value={a.id}>
                                      {a.name}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <label className="fd-finance-field fd-finance-field--span2">
                                <span className="fd-finance-field__label">Notes</span>
                                <textarea
                                  className="fd-finance-input fd-finance-input--textarea"
                                  value={purchaseFormFd.description}
                                  onChange={(ev) =>
                                    setPurchaseFormFd((f) => ({ ...f, description: ev.target.value }))
                                  }
                                  rows={3}
                                  placeholder="Invoice ref, delivery details…"
                                />
                              </label>
                            </div>
                            <div className="fd-finance-form__actions">
                              <button type="submit" className="btn-primary" disabled={fdSaving}>
                                Save purchase
                              </button>
                            </div>
                          </form>
                        </section>
                      ) : null}
                      <section className="fd-finance-card fd-finance-card--table" aria-label="Purchase ledger">
                        <header className="fd-finance-card__head fd-finance-card__head--row">
                          <h3 className="fd-finance-card__title">Purchase ledger</h3>
                        </header>
                        <div
                          className="fd-bookings-toolbar fd-finance-ledger-toolbar"
                          style={{ marginBottom: '1rem', flexWrap: 'wrap' }}
                        >
                          <label className="fd-bookings-toolbar-field">
                            <span className="fd-bookings-toolbar-label">Search</span>
                            <input
                              type="search"
                              value={fdPurchaseFilterQDraft}
                              onChange={(ev) => setFdPurchaseFilterQDraft(ev.target.value)}
                              placeholder="Item, vendor, notes…"
                            />
                          </label>
                          <label className="fd-bookings-toolbar-field">
                            <span className="fd-bookings-toolbar-label">Type</span>
                            <select
                              value={fdPurchaseFilterTypeId}
                              onChange={(ev) => setFdPurchaseFilterTypeId(ev.target.value)}
                              className="fd-finance-input"
                              style={{ minWidth: 140 }}
                            >
                              <option value="">All types</option>
                              {fdPurchaseTypes.map((t) => (
                                <option key={t.id} value={String(t.id)}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="fd-bookings-toolbar-field">
                            <span className="fd-bookings-toolbar-label">From</span>
                            <input
                              type="date"
                              value={fdPurchaseFilterDateFrom}
                              onChange={(ev) => setFdPurchaseFilterDateFrom(ev.target.value)}
                            />
                          </label>
                          <label className="fd-bookings-toolbar-field">
                            <span className="fd-bookings-toolbar-label">To</span>
                            <input
                              type="date"
                              value={fdPurchaseFilterDateTo}
                              onChange={(ev) => setFdPurchaseFilterDateTo(ev.target.value)}
                            />
                          </label>
                          <div className="fd-bookings-toolbar-actions">
                            <button
                              type="button"
                              className="btn-ghost"
                              onClick={() => {
                                setFdPurchaseFilterQDraft('');
                                setFdPurchaseFilterQ('');
                                setFdPurchaseFilterTypeId('');
                                setFdPurchaseFilterDateFrom('');
                                setFdPurchaseFilterDateTo('');
                              }}
                            >
                              Clear filters
                            </button>
                          </div>
                          <FdPerPageField
                            id="fd-purchase-per-page"
                            value={fdFinancePerPage}
                            onChange={setFdFinancePerPage}
                            maxAllowed={100}
                          />
                        </div>
                        <div className="fd-table-wrap fd-finance-table-wrap">
                          <table className="fd-table fd-table--wide">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Item</th>
                                <th>Type</th>
                                <th>Total</th>
                                <th>Vendor</th>
                                {showPurchaseRowActionsFd ? <th className="fd-col-nowrap fd-actions-col-end">Actions</th> : null}
                              </tr>
                            </thead>
                            <tbody>
                              {fdPurchaseRows.length === 0 ? (
                                <EmptyTableRow
                                  colSpan={showPurchaseRowActionsFd ? 6 : 5}
                                  message="No purchases for this property."
                                />
                              ) : (
                                fdPurchaseRows.map((p) => (
                                  <tr key={p.id}>
                                    <td className="fd-col-nowrap">{(p.purchase_date || '').slice(0, 10)}</td>
                                    <td>{p.item_name}</td>
                                    <td>{p.purchase_type?.name || '—'}</td>
                                    <td>{formatNgn(Number(p.amount) * Number(p.quantity))}</td>
                                    <td>{p.vendor || '—'}</td>
                                    {showPurchaseRowActionsFd ? (
                                      <td className="fd-actions-col-end">
                                        <div className="fd-finance-row-actions">
                                          {canPurchaseUpdateFd ? (
                                            <button
                                              type="button"
                                              className="btn-ghost fd-finance-row-action"
                                              disabled={fdSaving}
                                              onClick={() =>
                                                setPurchaseEditDraft({
                                                  id: p.id,
                                                  item_name: p.item_name || '',
                                                  amount: formatNairaDecimalFromNumber(p.amount ?? '', 2),
                                                  quantity: formatWholeNairaFromNumber(p.quantity ?? 1) || '1',
                                                  purchase_date: (p.purchase_date || '').slice(0, 10),
                                                  purchase_type_id: String(
                                                    p.purchase_type_id ?? p.purchase_type?.id ?? '',
                                                  ),
                                                  vendor: p.vendor || '',
                                                  description: p.description || '',
                                                  apartment_id: p.apartment_id ? String(p.apartment_id) : '',
                                                })
                                              }
                                            >
                                              Edit
                                            </button>
                                          ) : null}
                                          {canPurchaseDeleteFd ? (
                                            <button
                                              type="button"
                                              className="btn-ghost fd-finance-row-action"
                                              disabled={fdSaving}
                                              onClick={async () => {
                                                const ok = await luxisttConfirm({
                                                  title: 'Delete purchase?',
                                                  text: 'Remove this purchase from records?',
                                                  icon: 'warning',
                                                  danger: true,
                                                });
                                                if (!ok) return;
                                                setFdSaving(true);
                                                setError('');
                                                try {
                                                  await frontDeskApi.deletePurchase(p.id);
                                                  const res = await frontDeskApi.purchases(propertyId, fdPurchaseListParams);
                                                  setFdPurchaseRows(Array.isArray(res.data) ? res.data : []);
                                                  setFdPurchaseMeta(res.meta || null);
                                                  if (canReportsViewFd) await loadReportPreview();
                                                  setSuccessMessage('Purchase deleted.');
                                                } catch (err) {
                                                  setError(err.message || 'Could not delete');
                                                } finally {
                                                  setFdSaving(false);
                                                }
                                              }}
                                            >
                                              Delete
                                            </button>
                                          ) : null}
                                        </div>
                                      </td>
                                    ) : null}
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                        {fdPurchaseMeta ? (
                          <FdPaginationNav
                            meta={fdPurchaseMeta}
                            page={fdPurchasePage}
                            setPage={setFdPurchasePage}
                            disabled={financeLoading}
                          />
                        ) : null}
                      </section>
                      {purchaseEditDraft ? (
                        <div
                          className="modal-backdrop fd-finance-edit-overlay"
                          role="presentation"
                          onClick={() => {
                            if (!fdSaving) setPurchaseEditDraft(null);
                          }}
                        >
                          <div
                            className="fd-finance-edit-modal"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="fd-purchase-edit-title"
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            <h3 id="fd-purchase-edit-title" className="fd-finance-edit-modal__title">
                              Edit purchase
                            </h3>
                            <form
                              className="fd-finance-form"
                              onSubmit={async (e) => {
                                e.preventDefault();
                                setFdSaving(true);
                                setError('');
                                try {
                                  await frontDeskApi.updatePurchase(purchaseEditDraft.id, {
                                    item_name: purchaseEditDraft.item_name,
                                    amount: parseNairaDecimalToNumber(purchaseEditDraft.amount),
                                    quantity: parseWholeNairaToNumber(purchaseEditDraft.quantity) || 1,
                                    purchase_date: purchaseEditDraft.purchase_date,
                                    purchase_type_id: Number(purchaseEditDraft.purchase_type_id),
                                    vendor: purchaseEditDraft.vendor || null,
                                    description: purchaseEditDraft.description || null,
                                    apartment_id: purchaseEditDraft.apartment_id
                                      ? Number(purchaseEditDraft.apartment_id)
                                      : null,
                                  });
                                  setSuccessMessage('Purchase updated.');
                                  const res = await frontDeskApi.purchases(propertyId, fdPurchaseListParams);
                                  setFdPurchaseRows(Array.isArray(res.data) ? res.data : []);
                                  setFdPurchaseMeta(res.meta || null);
                                  if (canReportsViewFd) await loadReportPreview();
                                  setPurchaseEditDraft(null);
                                } catch (err) {
                                  setError(err.message || 'Could not update purchase');
                                } finally {
                                  setFdSaving(false);
                                }
                              }}
                            >
                              <div className="fd-finance-form__grid">
                                <label className="fd-finance-field fd-finance-field--span2">
                                  <span className="fd-finance-field__label">Item</span>
                                  <input
                                    className="fd-finance-input"
                                    required
                                    value={purchaseEditDraft.item_name}
                                    onChange={(ev) =>
                                      setPurchaseEditDraft((d) => ({ ...d, item_name: ev.target.value }))
                                    }
                                  />
                                </label>
                                <label className="fd-finance-field fd-finance-field--span2">
                                  <span className="fd-finance-field__label">Type</span>
                                  <select
                                    className="fd-finance-input"
                                    required
                                    value={purchaseEditDraft.purchase_type_id}
                                    onChange={(ev) =>
                                      setPurchaseEditDraft((d) => ({ ...d, purchase_type_id: ev.target.value }))
                                    }
                                  >
                                    {fdPurchaseTypes.map((t) => (
                                      <option key={t.id} value={t.id}>
                                        {t.name}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label className="fd-finance-field">
                                  <span className="fd-finance-field__label">Unit price (₦)</span>
                                  <input
                                    className="fd-finance-input luxistt-money-input"
                                    type="text"
                                    inputMode="decimal"
                                    autoComplete="off"
                                    required
                                    value={purchaseEditDraft.amount}
                                    onChange={(ev) =>
                                      setPurchaseEditDraft((d) => ({
                                        ...d,
                                        amount: formatNairaDecimalInputChange(ev.target.value, 2),
                                      }))
                                    }
                                  />
                                </label>
                                <label className="fd-finance-field">
                                  <span className="fd-finance-field__label">Quantity</span>
                                  <input
                                    className="fd-finance-input luxistt-money-input"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    required
                                    value={purchaseEditDraft.quantity}
                                    onChange={(ev) =>
                                      setPurchaseEditDraft((d) => ({
                                        ...d,
                                        quantity: formatWholeNairaInputChange(ev.target.value),
                                      }))
                                    }
                                  />
                                </label>
                                <label className="fd-finance-field">
                                  <span className="fd-finance-field__label">Date</span>
                                  <input
                                    className="fd-finance-input"
                                    type="date"
                                    required
                                    value={purchaseEditDraft.purchase_date}
                                    onChange={(ev) =>
                                      setPurchaseEditDraft((d) => ({ ...d, purchase_date: ev.target.value }))
                                    }
                                  />
                                </label>
                                <label className="fd-finance-field">
                                  <span className="fd-finance-field__label">Vendor</span>
                                  <input
                                    className="fd-finance-input"
                                    value={purchaseEditDraft.vendor}
                                    onChange={(ev) =>
                                      setPurchaseEditDraft((d) => ({ ...d, vendor: ev.target.value }))
                                    }
                                  />
                                </label>
                                <label className="fd-finance-field fd-finance-field--span2">
                                  <span className="fd-finance-field__label">Apartment</span>
                                  <select
                                    className="fd-finance-input"
                                    value={purchaseEditDraft.apartment_id}
                                    onChange={(ev) =>
                                      setPurchaseEditDraft((d) => ({ ...d, apartment_id: ev.target.value }))
                                    }
                                  >
                                    <option value="">Whole property</option>
                                    {bookingsApartmentOptions.map((a) => (
                                      <option key={a.id} value={a.id}>
                                        {a.name}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label className="fd-finance-field fd-finance-field--span2">
                                  <span className="fd-finance-field__label">Notes</span>
                                  <textarea
                                    className="fd-finance-input fd-finance-input--textarea"
                                    value={purchaseEditDraft.description}
                                    onChange={(ev) =>
                                      setPurchaseEditDraft((d) => ({ ...d, description: ev.target.value }))
                                    }
                                    rows={3}
                                  />
                                </label>
                              </div>
                              <div className="fd-finance-form__actions fd-finance-form__actions--split">
                                <button
                                  type="button"
                                  className="btn-ghost"
                                  disabled={fdSaving}
                                  onClick={() => setPurchaseEditDraft(null)}
                                >
                                  Cancel
                                </button>
                                <button type="submit" className="btn-primary" disabled={fdSaving}>
                                  Save changes
                                </button>
                              </div>
                            </form>
                          </div>
                        </div>
                      ) : null}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {view === 'audit' && (
            <>
              <h2 className="front-desk-section-title">Audit log</h2>
              <p className="front-desk-section-lead">
                Recent changes and actions across the system (same underlying log as the admin panel), in plain language.
              </p>
              <div
                className="fd-bookings-toolbar fd-audit-toolbar"
                style={{ marginBottom: '1rem', flexWrap: 'wrap' }}
              >
                <label className="fd-bookings-toolbar-field fd-bookings-toolbar-search">
                  <span className="fd-bookings-toolbar-label">Search</span>
                  <input
                    type="search"
                    placeholder="Description, log name, subject…"
                    value={activityQDraft}
                    onChange={(e) => setActivityQDraft(e.target.value)}
                    autoComplete="off"
                  />
                </label>
                <label className="fd-bookings-toolbar-field">
                  <span className="fd-bookings-toolbar-label">Actor</span>
                  <input
                    type="search"
                    placeholder="User name"
                    value={activityActorDraft}
                    onChange={(e) => setActivityActorDraft(e.target.value)}
                    autoComplete="off"
                  />
                </label>
                <label className="fd-bookings-toolbar-field">
                  <span className="fd-bookings-toolbar-label">Subject type</span>
                  <input
                    type="search"
                    placeholder="e.g. Booking"
                    value={activitySubjectType}
                    onChange={(e) => setActivitySubjectType(e.target.value)}
                    autoComplete="off"
                  />
                </label>
                <label className="fd-bookings-toolbar-field">
                  <span className="fd-bookings-toolbar-label">Subject ID</span>
                  <input
                    type="number"
                    min={1}
                    placeholder="Numeric id"
                    value={activitySubjectId}
                    onChange={(e) => setActivitySubjectId(e.target.value)}
                  />
                </label>
                <label className="fd-bookings-toolbar-field">
                  <span className="fd-bookings-toolbar-label">From</span>
                  <input
                    type="date"
                    value={activityDateFrom}
                    onChange={(e) => setActivityDateFrom(e.target.value)}
                  />
                </label>
                <label className="fd-bookings-toolbar-field">
                  <span className="fd-bookings-toolbar-label">To</span>
                  <input
                    type="date"
                    value={activityDateTo}
                    onChange={(e) => setActivityDateTo(e.target.value)}
                  />
                </label>
                <FdPerPageField
                  id="fd-audit-per-page"
                  value={activityPerPage}
                  onChange={setActivityPerPage}
                  maxAllowed={100}
                />
                <div className="fd-bookings-toolbar-actions">
                  <button
                    type="button"
                    className="btn-link fd-bookings-clear"
                    onClick={() => {
                      setActivityQDraft('');
                      setActivityQ('');
                      setActivityActorDraft('');
                      setActivityActor('');
                      setActivitySubjectType('');
                      setActivitySubjectId('');
                      setActivityDateFrom('');
                      setActivityDateTo('');
                      setActivityPage(1);
                    }}
                  >
                    Clear filters
                  </button>
                </div>
              </div>
              {activityLoading ? (
                <p className="muted">Loading activity…</p>
              ) : (
                <div className="fd-table-wrap fd-audit-wrap">
                  <table className="fd-table fd-table--wide fd-audit-table">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Summary</th>
                        <th>Actor</th>
                        <th>Subject</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityRows.length === 0 ? (
                        <EmptyTableRow colSpan={5} message="No activity rows yet, or you may lack permission to view the log." />
                      ) : (
                        activityRows.map((row) => (
                          <tr key={row.id}>
                            <td className="fd-col-nowrap muted">{formatDateTime(row.created_at)}</td>
                            <td>{row.headline || '—'}</td>
                            <td>{row.actor || '—'}</td>
                            <td className="muted">
                              {row.subject_type ? `${row.subject_type}${row.subject_id != null ? ` #${row.subject_id}` : ''}` : '—'}
                            </td>
                            <td className="fd-audit-details">
                              {Array.isArray(row.details) && row.details.length > 0 ? (
                                <ul className="fd-audit-detail-list">
                                  {row.details.map((line, i) => (
                                    <li key={i}>{line}</li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="muted">—</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              {activityMeta ? (
                <FdPaginationNav
                  meta={activityMeta}
                  page={activityPage}
                  setPage={setActivityPage}
                  disabled={activityLoading}
                />
              ) : null}
            </>
          )}
            </>
          )}
        </div>
        <footer className="fd-app-footer" role="contentinfo">
          <p className="fd-app-footer__credit">
            Built with <span className="fd-app-footer__heart" aria-label="love">&#10084;</span> by{' '}
            <a
              href="https://www.newistt.com"
              target="_blank"
              rel="noopener noreferrer"
              className="fd-app-footer__link"
            >
              newistt
            </a>
          </p>
        </footer>
      </main>

      {kycEditAssignment ? (
        <div
          className="modal-backdrop fd-modal-backdrop"
          role="presentation"
          onClick={() => {
            if (!kycSaveBusy) setKycEditAssignment(null);
          }}
        >
          <div
            className="modal-box modal-wide fd-shift-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="fd-kyc-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="fd-kyc-title">Review KYC</h3>
            <p className="muted fd-shift-modal-lede">
              {kycEditAssignment.user?.name || 'Staff member'} · {kycEditAssignment.user?.email || '—'}
            </p>
            {kycEditAssignment.government_id_url ? (
              <p className="fd-kyc-id-link-wrap">
                <a
                  href={kycEditAssignment.government_id_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fd-kyc-id-link"
                >
                  Open current government ID (new tab)
                </a>
              </p>
            ) : kycEditAssignment.has_government_id_uploaded ? (
              <p className="muted">ID is marked uploaded but no URL is available. Refresh the user list or contact support.</p>
            ) : (
              <p className="muted">No government ID image on file yet — you can upload one below.</p>
            )}
            <div className="fd-kyc-form">
              <label className="fd-finance-field">
                <span className="fd-finance-field__label">KYC status</span>
                <select
                  className="fd-finance-input"
                  value={kycFormFd.kyc_status}
                  onChange={(ev) => setKycFormFd((f) => ({ ...f, kyc_status: ev.target.value }))}
                >
                  {FD_KYC_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="fd-finance-field fd-finance-field--span2">
                <span className="fd-finance-field__label">Government ID / passport number</span>
                <input
                  className="fd-finance-input"
                  value={kycFormFd.government_id_number}
                  onChange={(ev) => setKycFormFd((f) => ({ ...f, government_id_number: ev.target.value }))}
                  autoComplete="off"
                />
              </label>
              <label className="fd-finance-field fd-finance-field--span2">
                <span className="fd-finance-field__label">Government ID photo (JPEG, PNG, WebP)</span>
                <input
                  type="file"
                  className="fd-booking-upload-input"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  onChange={(ev) => setKycGovIdFileFd(ev.target.files?.[0] || null)}
                />
                {kycGovIdFileFd ? <span className="muted">{kycGovIdFileFd.name}</span> : null}
              </label>
              <label className="fd-finance-field">
                <span className="fd-finance-field__label">Next of kin — name</span>
                <input
                  className="fd-finance-input"
                  value={kycFormFd.next_of_kin_name}
                  onChange={(ev) => setKycFormFd((f) => ({ ...f, next_of_kin_name: ev.target.value }))}
                  autoComplete="off"
                />
              </label>
              <label className="fd-finance-field">
                <span className="fd-finance-field__label">Next of kin — phone</span>
                <input
                  className="fd-finance-input"
                  value={kycFormFd.next_of_kin_phone}
                  onChange={(ev) => setKycFormFd((f) => ({ ...f, next_of_kin_phone: ev.target.value }))}
                  placeholder="+234…"
                  autoComplete="off"
                />
              </label>
              <label className="fd-finance-field fd-finance-field--span2">
                <span className="fd-finance-field__label">Next of kin — relationship</span>
                <input
                  className="fd-finance-input"
                  value={kycFormFd.next_of_kin_relationship}
                  onChange={(ev) => setKycFormFd((f) => ({ ...f, next_of_kin_relationship: ev.target.value }))}
                  placeholder="e.g. Spouse, sibling"
                  autoComplete="off"
                />
              </label>
              <label className="fd-finance-field">
                <span className="fd-finance-field__label">Father&apos;s name</span>
                <input
                  className="fd-finance-input"
                  value={kycFormFd.father_name}
                  onChange={(ev) => setKycFormFd((f) => ({ ...f, father_name: ev.target.value }))}
                  autoComplete="off"
                />
              </label>
              <label className="fd-finance-field">
                <span className="fd-finance-field__label">Mother&apos;s name</span>
                <input
                  className="fd-finance-input"
                  value={kycFormFd.mother_name}
                  onChange={(ev) => setKycFormFd((f) => ({ ...f, mother_name: ev.target.value }))}
                  autoComplete="off"
                />
              </label>
              <label className="fd-finance-field fd-finance-field--span2">
                <span className="fd-finance-field__label">Reviewer notes (internal)</span>
                <textarea
                  className="fd-finance-input fd-finance-input--textarea"
                  rows={4}
                  value={kycFormFd.kyc_notes}
                  onChange={(ev) => setKycFormFd((f) => ({ ...f, kyc_notes: ev.target.value }))}
                  placeholder="Optional notes visible to other reviewers…"
                />
              </label>
            </div>
            <div className="fd-shift-modal-actions">
              <button type="button" className="btn-ghost" disabled={kycSaveBusy} onClick={() => setKycEditAssignment(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={kycSaveBusy}
                onClick={async () => {
                  setKycSaveBusy(true);
                  setError('');
                  try {
                    const fd = new FormData();
                    fd.append('kyc_status', kycFormFd.kyc_status);
                    const notes = kycFormFd.kyc_notes.trim();
                    fd.append('kyc_notes', notes);
                    fd.append('government_id_number', (kycFormFd.government_id_number || '').trim());
                    fd.append('next_of_kin_name', (kycFormFd.next_of_kin_name || '').trim());
                    fd.append('next_of_kin_phone', (kycFormFd.next_of_kin_phone || '').trim());
                    fd.append('next_of_kin_relationship', (kycFormFd.next_of_kin_relationship || '').trim());
                    fd.append('father_name', (kycFormFd.father_name || '').trim());
                    fd.append('mother_name', (kycFormFd.mother_name || '').trim());
                    if (kycGovIdFileFd) {
                      fd.append('government_id', kycGovIdFileFd);
                    }
                    const res = await frontDeskApi.patchStaffKyc(kycEditAssignment.id, fd);
                    const entity = unwrapEntity(res);
                    setKycEditAssignment(null);
                    setKycGovIdFileFd(null);
                    setSuccessMessage('KYC updated.');
                    if (entity && typeof entity.id === 'number') {
                      setTeamRows((prev) => prev.map((r) => (r.id === entity.id ? { ...r, ...entity } : r)));
                    } else {
                      await loadTeamList();
                    }
                  } catch (err) {
                    setError(err.message || 'Could not save KYC');
                  } finally {
                    setKycSaveBusy(false);
                  }
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {modal && (
        <div
          className="modal-backdrop fd-modal-backdrop"
          role="presentation"
          onClick={() => {
            if (guestRefundOpen) {
              setGuestRefundOpen(false);
            } else if (checkOutOpen) {
              setCheckOutOpen(false);
              setCheckOutBooking(null);
              setCheckOutNote('');
            } else if (checkInOpen) {
              setCheckInOpen(false);
            } else {
              setModalEditing(false);
              setEditForm(null);
              setEditPriceQuote(null);
              setEditSelectedSurchargeIds([]);
              setEditSurchargeOverrides({});
              setModal(null);
            }
          }}
        >
          <div className="modal-box modal-wide fd-booking-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="fd-booking-modal-head">
              <h3>Booking #{modal.id}</h3>
              <div className="fd-booking-modal-badges">
                <StatusBadge ui={modal.status} />
                <StatusBadge ui={modal.payment_status} />
                <StatusBadge ui={modal.booking_source} />
                <StatusBadge ui={modal.booking_platform} />
              </div>
            </div>
            {error ? (
              <div className="fd-create-warn" role="alert" style={{ margin: '0 0 0.75rem' }}>
                {error}
                <button
                  type="button"
                  className="btn-ghost"
                  style={{ marginLeft: '0.5rem', fontSize: '0.85rem' }}
                  onClick={() => setError('')}
                >
                  Dismiss
                </button>
              </div>
            ) : null}
            <div className="fd-booking-modal-section">
              <span className="fd-booking-modal-k">Property &amp; unit</span>
              <p className="fd-booking-modal-v">{unitLabel(modal)}</p>
            </div>
            {modalEditing && editForm ? (
              <div className="fd-booking-modal-section fd-booking-edit-block">
                <span className="fd-booking-modal-k">Edit reservation</span>
                <p className="muted fd-create-field-full" style={{ marginTop: 0 }}>
                  Changing dates or pricing updates totals using current rates. If payment was already recorded and the
                  new total differs, status switches to Pending until you collect any balance (Mark paid).
                </p>
                <div className="fd-booking-edit-grid">
                  <label className="fd-create-field-full">
                    Check-in
                    <input
                      type="date"
                      value={toCalendarIso(editForm.start_date)}
                      onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                    />
                  </label>
                  <label className="fd-create-field-full">
                    Check-out
                    <input
                      type="date"
                      value={toCalendarIso(editForm.end_date)}
                      onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                    />
                  </label>
                  <label>
                    Adults
                    <input
                      type="number"
                      min={1}
                      max={editMaxAdultsInput}
                      value={editForm.guests_adults}
                      onChange={(e) => setEditForm({ ...editForm, guests_adults: e.target.value })}
                    />
                  </label>
                  <label>
                    Children
                    <input
                      type="number"
                      min={0}
                      max={editMaxChildrenInput}
                      value={editForm.guests_children}
                      onChange={(e) => setEditForm({ ...editForm, guests_children: e.target.value })}
                    />
                  </label>
                  {editMandatorySurcharges.length > 0 || editOptionalSurcharges.length > 0 ? (
                    <div className="fd-create-field-full fd-create-surcharges" style={{ gridColumn: '1 / -1' }}>
                      {editMandatorySurcharges.length > 0 ? (
                        <div className="party-surcharge-mandatory-block">
                          <p className="party-surcharge-section-title">Included with this stay</p>
                          <ul className="party-surcharge-included-list">
                            {editMandatorySurcharges.map((s) => (
                              <li key={s.id} className="party-surcharge-included-row">
                                <div className="party-surcharge-included-copy">
                                  <span className="party-surcharge-main">{s.name}</span>
                                  {s.type_name ? <span className="party-surcharge-type">{s.type_name}</span> : null}
                                  {s.description ? <span className="party-surcharge-desc">{s.description}</span> : null}
                                </div>
                                <span className="party-surcharge-fee-pill">{formatSurchargeFeeLabel(s)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {editOptionalSurcharges.length > 0 ? (
                        <>
                          {editOptionalSurcharges.length > 1 ? (
                            <p className="party-surcharge-intro">Optional add-ons — select any that apply.</p>
                          ) : null}
                          {editOptionalSurcharges.length === 1 ? (
                            (() => {
                              const s = editOptionalSurcharges[0];
                              const fee = formatSurchargeFeeLabel(s);
                              return (
                                <label className="party-surcharge-row party-surcharge-row--single">
                                  <input
                                    type="checkbox"
                                    className="party-surcharge-checkbox"
                                    checked={editSelectedSurchargeIds.includes(s.id)}
                                    onChange={() => toggleEditSurcharge(s.id)}
                                  />
                                  <span className="party-surcharge-label party-surcharge-label--party-option">
                                    <span className="party-surcharge-main">Include optional surcharge.</span>
                                    {fee ? (
                                      <span className="party-surcharge-fee-note party-surcharge-fee-note--party">
                                        Adds <strong>{fee}</strong> when ticked.
                                      </span>
                                    ) : null}
                                  </span>
                                </label>
                              );
                            })()
                          ) : (
                            <ul className="party-surcharge-multi-list">
                              {editOptionalSurcharges.map((s) => (
                                <li key={s.id}>
                                  <label className="party-surcharge-row party-surcharge-row--multi">
                                    <input
                                      type="checkbox"
                                      className="party-surcharge-checkbox"
                                      checked={editSelectedSurchargeIds.includes(s.id)}
                                      onChange={() => toggleEditSurcharge(s.id)}
                                    />
                                    <span className="party-surcharge-label">
                                      <span className="party-surcharge-main">{s.name}</span>
                                      {s.type_name ? (
                                        <span className="party-surcharge-type">{s.type_name}</span>
                                      ) : null}
                                    </span>
                                    <span className="party-surcharge-fee-pill">{formatSurchargeFeeLabel(s)}</span>
                                  </label>
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      ) : null}
                    </div>
                  ) : null}
                  <label className="fd-create-field-full">
                    Discount (₦) — optional override
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      className="luxistt-money-input"
                      placeholder={
                        editPriceQuote?.discount_amount != null && Number(editPriceQuote.discount_amount) > 0
                          ? `Calculated: ₦${Number(editPriceQuote.discount_amount).toLocaleString()}`
                          : 'Leave blank for calculated discount from rules'
                      }
                      value={editForm.discount_amount_manual}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          discount_amount_manual: formatWholeNairaInputChange(e.target.value),
                        })
                      }
                    />
                  </label>
                  {editDisplaySurchargeLines.length > 0 ? (
                    <div className="fd-create-field-full">
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                        <p className="muted" style={{ margin: 0, flex: '1 1 auto' }}>
                          Surcharge amount overrides (optional)
                        </p>
                        <button
                          type="button"
                          className="btn-ghost"
                          style={{ fontSize: '0.85rem' }}
                          onClick={() => setEditSurchargeOverrides({})}
                        >
                          Reset all surcharge overrides
                        </button>
                      </div>
                      <div className="fd-create-total-meta">
                        {editDisplaySurchargeLines.map((line) => {
                          const baselineRow = (
                            Array.isArray(editPriceQuote?.surcharge_lines) ? editPriceQuote.surcharge_lines : []
                          ).find((x) => Number(x.id) === Number(line.id));
                          const baseline = Number(baselineRow?.amount ?? line.amount ?? 0);
                          return (
                            <div
                              key={line.id}
                              className="fd-create-field-full"
                              style={{ marginBottom: '0.45rem', display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}
                            >
                              <label style={{ flex: '1 1 180px', marginBottom: 0 }}>
                                {line.name} (₦)
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="off"
                                  className="luxistt-money-input"
                                  placeholder={`Calculated: ₦${baseline.toLocaleString()}`}
                                  value={editSurchargeOverrides[String(line.id)] || ''}
                                  onChange={(e) =>
                                    setEditSurchargeOverrides((prev) => ({
                                      ...prev,
                                      [String(line.id)]: formatWholeNairaInputChange(e.target.value),
                                    }))
                                  }
                                />
                              </label>
                              <button
                                type="button"
                                className="btn-ghost"
                                style={{ fontSize: '0.85rem', marginBottom: '0.15rem' }}
                                onClick={() =>
                                  setEditSurchargeOverrides((prev) => {
                                    const next = { ...prev };
                                    delete next[String(line.id)];
                                    return next;
                                  })
                                }
                              >
                                Reset line
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                  {editOverridePreviewInvalid && editPricingOverridePreview?.message ? (
                    <p className="muted fd-create-field-full" role="alert">
                      {editPricingOverridePreview.message}
                    </p>
                  ) : null}
                  <label className="fd-create-field-full">
                    Total amount (₦) — optional override
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      className="luxistt-money-input"
                      placeholder="Leave blank to use calculated total from rates"
                      value={editForm.total_price_manual}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          total_price_manual: formatWholeNairaInputChange(e.target.value),
                        })
                      }
                    />
                  </label>
                  <div className="fd-create-total fd-create-field-full">
                    <strong>New total (preview)</strong>
                    {editDisplayTotal > 0 ? (
                      <div className="fd-create-total-amount">
                        {editHasDiscount && editDisplaySubtotal != null ? (
                          <>
                            <span className="book-total-strike">₦{editDisplaySubtotal.toLocaleString()}</span>{' '}
                            <span className="fd-create-total-final">₦{editDisplayTotal.toLocaleString()}</span>
                            {editAppliedDiscountLabel ? (
                              <span className="muted"> ({editAppliedDiscountLabel})</span>
                            ) : null}
                          </>
                        ) : (
                          <span className="fd-create-total-final">₦{editDisplayTotal.toLocaleString()}</span>
                        )}
                        {editDisplaySurchargeLines.length > 0 ? (
                          <div className="muted fd-create-total-meta" style={{ marginTop: '0.35rem' }}>
                            {editDisplaySurchargeLines.map((line) => (
                              <div key={line.id}>
                                {line.name}: ₦{Number(line.amount).toLocaleString()}
                              </div>
                            ))}
                          </div>
                        ) : null}
                        <div className="muted fd-create-total-meta" style={{ marginTop: '0.35rem' }}>
                          Previously saved total: ₦{Number(modal.total_price ?? 0).toLocaleString()}
                        </div>
                      </div>
                    ) : (
                      <p className="muted">Select valid dates to load a quote preview.</p>
                    )}
                  </div>
                  <label className="fd-create-field-full">
                    Guest name
                    <input
                      type="text"
                      value={editForm.guest_name}
                      onChange={(e) => setEditForm({ ...editForm, guest_name: e.target.value })}
                    />
                  </label>
                  <label className="fd-create-field-full">
                    Email
                    <input
                      type="email"
                      value={editForm.guest_email}
                      onChange={(e) => setEditForm({ ...editForm, guest_email: e.target.value })}
                    />
                  </label>
                  <label className="fd-create-field-full">
                    Phone
                    <input
                      type="tel"
                      value={editForm.guest_phone}
                      onChange={(e) => setEditForm({ ...editForm, guest_phone: e.target.value })}
                    />
                  </label>
                  <label className="fd-create-field-full">
                    Government ID / passport no.
                    <input
                      type="text"
                      value={editForm.guest_id_number}
                      onChange={(e) => setEditForm({ ...editForm, guest_id_number: e.target.value })}
                    />
                  </label>
                  <label className="fd-create-field-full">
                    Platform
                    <select
                      value={editForm.booking_platform_id}
                      onChange={(e) => setEditForm({ ...editForm, booking_platform_id: e.target.value })}
                      aria-label="Booking platform"
                    >
                      <option value="">—</option>
                      {bookingPlatforms.map((p) => (
                        <option key={p.id} value={String(p.id)}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="fd-create-field-full">
                    Notes
                    <textarea
                      value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      rows={3}
                      className="fd-checkout-textarea"
                    />
                  </label>
                </div>
                <div className="fd-booking-edit-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={loading || editOverridePreviewInvalid}
                    onClick={saveModalEdit}
                  >
                    {loading ? 'Saving…' : 'Save changes'}
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={loading}
                    onClick={() => {
                      setModalEditing(false);
                      setEditForm(null);
                      setEditPriceQuote(null);
                      setEditSelectedSurchargeIds([]);
                      setEditSurchargeOverrides({});
                    }}
                  >
                    Discard
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="fd-booking-modal-section">
                  <span className="fd-booking-modal-k">Stay &amp; pricing</span>
                  <p className="fd-booking-modal-v">
                    {formatLongDate(modal.start_date)} → {formatLongDate(modal.end_date)}
                  </p>
                  {(() => {
                    const mpb = getPricingBreakdown(modal);
                    const mcn = cautionToneClassName(mpb.tone, 'fd-booking-caution');
                    return (
                      <dl className="fd-pricing-breakdown-dl">
                        <div>
                          <dt>Sub total</dt>
                          <dd>{formatNgn(mpb.stay_subtotal)}</dd>
                        </div>
                        <div>
                          <dt>Caution</dt>
                          <dd className={mcn}>{formatNgn(mpb.caution_display)}</dd>
                        </div>
                        <div>
                          <dt>Total</dt>
                          <dd>
                            <strong>{formatNgn(mpb.total)}</strong>
                          </dd>
                        </div>
                      </dl>
                    );
                  })()}
                </div>
                <div className="fd-booking-modal-section">
                  <span className="fd-booking-modal-k">Guest</span>
                  <p className="fd-booking-modal-v">{guestLabel(modal)}</p>
                  <p className="muted fd-booking-modal-contact">
                    {[modal.guest_email, modal.guest_phone].filter(Boolean).join(' · ') || '—'}
                  </p>
                  {modal.registered_user ? (
                    <dl className="fd-detail-dl">
                      <dt>Registered account</dt>
                      <dd>
                        {modal.registered_user.name} · {modal.registered_user.email}
                      </dd>
                    </dl>
                  ) : null}
                </div>
                <div className="fd-booking-modal-section fd-booking-modal-grid">
                  <div>
                    <span className="fd-booking-modal-k">Party</span>
                    <p className="fd-booking-modal-v">
                      {modal.guests_adults} adult{modal.guests_adults === 1 ? '' : 's'}
                      {', '}
                      {modal.guests_children} child
                      {modal.guests_children === 1 ? '' : 'ren'}
                    </p>
                  </div>
                  <div>
                    <span className="fd-booking-modal-k">Payment method</span>
                    <p className="fd-booking-modal-v">{modal.payment_method || '—'}</p>
                  </div>
                  {modal.payment_reference ? (
                    <div>
                      <span className="fd-booking-modal-k">Payment reference</span>
                      <p className="fd-booking-modal-v fd-mono">{modal.payment_reference}</p>
                    </div>
                  ) : null}
                </div>
                {modal.notes ? (
                  <div className="fd-booking-modal-section">
                    <span className="fd-booking-modal-k">Notes</span>
                    <p className="fd-booking-modal-v">{modal.notes}</p>
                  </div>
                ) : null}
              </>
            )}
            {modal.checkout_guest_note ? (
              <div className="fd-booking-modal-section">
                <span className="fd-booking-modal-k">Checkout note</span>
                <p className="fd-booking-modal-v">{modal.checkout_guest_note}</p>
              </div>
            ) : null}
            {modal.guest_refunded_at ? (
              <div className="fd-booking-modal-section">
                <span className="fd-booking-modal-k">Guest refund</span>
                <p className="fd-booking-modal-v">
                  Refunded <strong>{formatNgn(modal.guest_refund_amount)}</strong> on {formatDateTime(modal.guest_refunded_at)}.
                  Amount collected before refund: {formatNgn(modal.total_price_before_guest_refund)}. Retained total after
                  refund: <strong>{formatNgn(modal.total_price)}</strong>.
                </p>
                {modal.guest_refund_note ? <p className="muted">{modal.guest_refund_note}</p> : null}
              </div>
            ) : null}
            {(modal.checked_in_at || modal.checked_out_at) ? (
              <div className="fd-booking-modal-section fd-booking-modal-grid">
                {modal.checked_in_at ? (
                  <div>
                    <span className="fd-booking-modal-k">Check-in time</span>
                    <p className="fd-booking-modal-v">{formatDateTime(modal.checked_in_at)}</p>
                  </div>
                ) : null}
                {modal.checked_out_at ? (
                  <div>
                    <span className="fd-booking-modal-k">Check-out time</span>
                    <p className="fd-booking-modal-v">{formatDateTime(modal.checked_out_at)}</p>
                  </div>
                ) : null}
              </div>
            ) : null}
            {Array.isArray(modal.guest_id_documents) && modal.guest_id_documents.length > 0 ? (
              <div className="fd-booking-modal-section">
                <span className="fd-booking-modal-k">ID documents on this booking</span>
                <div className="fd-booking-docs-grid">
                  {modal.guest_id_documents.map((doc) =>
                    doc.mime_type?.startsWith('image/') ? (
                      <LightboxImage
                        key={doc.id}
                        src={doc.thumb || doc.url}
                        fullSrc={doc.url}
                        alt="Guest ID document"
                        className="fd-booking-doc-thumb"
                        imgClassName="fd-booking-doc-thumb-img"
                      />
                    ) : (
                      <a
                        key={doc.id}
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fd-booking-doc-thumb"
                      >
                        <span className="fd-booking-doc-fallback">{doc.name}</span>
                      </a>
                    ),
                  )}
                </div>
              </div>
            ) : null}
            {(modal.guest_id_number || modal.registered_user?.government_id_number) ? (
              <div className="fd-booking-modal-section">
                <span className="fd-booking-modal-k">Government ID / passport number</span>
                <p className="fd-booking-modal-v">
                  {modal.guest_id_number || modal.registered_user?.government_id_number}
                </p>
              </div>
            ) : null}
            {modal.registered_user?.government_id_document?.url ? (
              <div className="fd-booking-modal-section">
                <span className="fd-booking-modal-k">ID on guest profile</span>
                <LightboxImage
                  src={
                    modal.registered_user.government_id_document.thumb ||
                    modal.registered_user.government_id_document.url
                  }
                  fullSrc={modal.registered_user.government_id_document.url}
                  alt="Government ID on file"
                  className="fd-gov-id-preview-link"
                  imgClassName="fd-gov-id-preview"
                />
              </div>
            ) : null}
            <div className="modal-actions fd-booking-modal-actions">
              {!modalEditing ? (
                <>
                  {canFrontDeskEditBooking(modal) &&
                  canStaffAbilityForFrontDesk(bookingAbilityKey(modal), 'booking.update') ? (
                    <button type="button" className="btn-ghost" onClick={beginModalEdit}>
                      Edit booking
                    </button>
                  ) : null}
                  {canFdCancelBookingStaff(canStaffAbilityForFrontDesk, modal, propertyId || null) ? (
                    <button type="button" className="btn-ghost fd-row-action--danger" onClick={handleModalCancelBooking}>
                      Cancel booking
                    </button>
                  ) : null}
                  {modal.status?.value === 'pending' &&
                  canStaffAbilityForFrontDesk(bookingAbilityKey(modal), 'booking.approve') ? (
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() =>
                        postAction(frontDeskApi.approve, 'Booking confirmed. Confirmation email sent to the guest.')
                      }
                    >
                      Confirm booking
                    </button>
                  ) : null}
                  {modal.payment_status?.value !== 'paid' &&
                  modal.status?.value !== 'cancelled' &&
                  canStaffAbilityForFrontDesk(bookingAbilityKey(modal), 'booking.mark_paid') ? (
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => postAction(frontDeskApi.markPaid, 'Payment recorded. Guest can download a PDF receipt.')}
                    >
                      Mark paid
                    </button>
                  ) : null}
                  {canOfferGuestRefund(modal) &&
                  canStaffAbilityForFrontDesk(bookingAbilityKey(modal), 'booking.mark_paid') ? (
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => {
                        setGuestRefundAmount('');
                        setGuestRefundNote('');
                        setGuestRefundOpen(true);
                      }}
                    >
                      Refund guest
                    </button>
                  ) : null}
                  {bookingHasCautionLine(modal) &&
                  canFrontDeskAdjustCautionFees(modal) &&
                  canStaffMarkPaidOrCaution(bookingAbilityKey(modal), canStaffAbilityForFrontDesk) ? (
                    <button
                      type="button"
                      className="btn-ghost fd-table-action-pill--caution"
                      onClick={() => {
                        setCautionModalBooking(modal);
                        setCautionModalKind('refund');
                        setCautionModalAmount('');
                        setCautionModalNote('');
                      }}
                    >
                      Caution fees
                    </button>
                  ) : null}
                  {modal.payment_status?.value === 'paid' &&
                  canStaffAbilityForFrontDesk(bookingAbilityKey(modal), 'booking.view') ? (
                    <button
                      type="button"
                      className="btn-ghost fd-btn-with-icon"
                      onClick={async () => {
                        if (!modal?.id) return;
                        setLoading(true);
                        setError('');
                        try {
                          const blob = await downloadFrontDeskReceipt(modal.id);
                          const how = openPdfBlobInNewTab(blob, { filename: `luxistt-receipt-${modal.id}.pdf` });
                          setSuccessMessage(
                            how === 'viewer'
                              ? `Receipt for booking #${modal.id} opened in the PDF viewer.`
                              : `Receipt saved for booking #${modal.id}. Open the file to view or print.`,
                          );
                        } catch (e) {
                          setError(e.message || 'Could not open receipt');
                        } finally {
                          setLoading(false);
                        }
                      }}
                    >
                      <FiDownload aria-hidden />
                      PDF receipt
                    </button>
                  ) : null}
                  {canShowCheckInOnOps(modal, date) &&
                  canStaffAbilityForFrontDesk(bookingAbilityKey(modal), 'booking.check_in') ? (
                    <button type="button" className="btn-ghost" onClick={() => setCheckInOpen(true)}>
                      Check in
                    </button>
                  ) : null}
                  {canShowCheckOutOnOps(modal, date) &&
                  canStaffAbilityForFrontDesk(bookingAbilityKey(modal), 'booking.check_out') ? (
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => {
                        if (!modal) return;
                        setCheckOutBooking(modal);
                        setCheckOutNote(
                          typeof modal.checkout_guest_note === 'string' ? modal.checkout_guest_note : '',
                        );
                        setCheckOutOpen(true);
                      }}
                    >
                      Check out
                    </button>
                  ) : null}
                </>
              ) : null}
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  setGuestRefundOpen(false);
                  setGuestRefundAmount('');
                  setGuestRefundNote('');
                  setCheckInOpen(false);
                  setCheckOutOpen(false);
                  setCheckOutBooking(null);
                  setCheckOutNote('');
                  setModalEditing(false);
                  setEditForm(null);
                  setModal(null);
                }}
              >
                Close
              </button>
            </div>
            {canStaffAbilityForFrontDesk(bookingAbilityKey(modal), 'booking.guest_documents') ? (
              <div className="fd-booking-upload">
                <label className="fd-booking-upload-label">Upload ID documents (images or PDF)</label>
                <input
                  type="file"
                  className="fd-booking-upload-input"
                  multiple
                  accept="image/*,application/pdf"
                  capture="environment"
                  onChange={onUploadDocs}
                />
              </div>
            ) : null}
          </div>
        </div>
      )}

      {modal && checkInOpen ? (
        <div
          className="modal-backdrop fd-checkin-root-overlay"
          role="presentation"
          onClick={() => setCheckInOpen(false)}
        >
          <div
            className="modal-box fd-checkin-dialog fd-checkin-dialog--standalone"
            role="dialog"
            aria-modal="true"
            aria-labelledby="fd-checkin-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="fd-checkin-title" className="fd-checkin-title">
              Verify ID · check in
            </h4>
            <p className="muted fd-checkin-lead">
              Confirm or update the guest&apos;s government ID on their profile, then complete check-in.
            </p>
            {modal.registered_user?.government_id_document?.url ? (
              <div className="fd-checkin-profile-block">
                <span className="fd-booking-modal-k">Current ID on profile</span>
                <LightboxImage
                  src={
                    modal.registered_user.government_id_document.thumb ||
                    modal.registered_user.government_id_document.url
                  }
                  fullSrc={modal.registered_user.government_id_document.url}
                  alt="Government ID on profile"
                  className="fd-gov-id-preview-link"
                  imgClassName="fd-gov-id-preview"
                />
              </div>
            ) : (
              <p className="muted fd-checkin-no-id">No ID photo saved for this guest yet.</p>
            )}
            <label className="fd-create-field-full">
              ID / passport number
              <input
                type="text"
                autoComplete="off"
                value={checkInIdNumber}
                onChange={(e) => setCheckInIdNumber(e.target.value)}
                placeholder="Optional — e.g. passport number"
              />
            </label>
            <label className="fd-create-field-full">
              New ID photo (optional)
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                className="fd-booking-upload-input"
                onChange={(e) => setCheckInIdFile(e.target.files?.[0] || null)}
              />
              {checkInIdFile ? <span className="muted">{checkInIdFile.name}</span> : null}
            </label>
            <div className="fd-checkin-actions">
              <button type="button" className="btn-ghost" onClick={() => setCheckInOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={loading}
                onClick={async () => {
                  if (!modal?.id) return;
                  setLoading(true);
                  setError('');
                  try {
                    if (checkInIdFile || checkInIdNumber.trim()) {
                      const fd = new FormData();
                      if (checkInIdNumber.trim()) {
                        fd.append('guest_id_number', checkInIdNumber.trim());
                      }
                      if (checkInIdFile) {
                        fd.append('guest_id_document', checkInIdFile);
                      }
                      await frontDeskApi.checkIn(modal.id, fd);
                    } else {
                      await frontDeskApi.checkIn(modal.id, {});
                    }
                    setCheckInOpen(false);
                    dismissBookingModal();
                    setSuccessMessage(`Booking #${modal.id} checked in.`);
                    await refreshOperationalViews();
                  } catch (e) {
                    setError(e.message || 'Check-in failed');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Complete check-in
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {modal && guestRefundOpen ? (
        <div
          className="modal-backdrop fd-checkin-root-overlay"
          role="presentation"
          onClick={() => {
            if (!loading) {
              setGuestRefundOpen(false);
            }
          }}
        >
          <div
            className="modal-box fd-checkin-dialog fd-checkin-dialog--standalone"
            role="dialog"
            aria-modal="true"
            aria-labelledby="fd-guest-refund-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="fd-guest-refund-title" className="fd-checkin-title">
              Refund guest
            </h4>
            <p className="muted fd-checkin-lead">
              Booking #{modal.id} · {guestLabel(modal)} · collected {formatNgn(modal.total_price)}. This cancels the stay,
              marks payment as refunded, and updates the booking total to the amount you retain.
            </p>
            <label className="fd-create-field-full">
              Refund amount (₦, whole naira)
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                className="luxistt-money-input"
                value={guestRefundAmount}
                onChange={(e) => setGuestRefundAmount(formatWholeNairaInputChange(e.target.value))}
                placeholder={
                  modal.total_price != null && Number.isFinite(Number(modal.total_price))
                    ? `Max ${Number(modal.total_price).toLocaleString('en-NG')}`
                    : 'Amount'
                }
              />
            </label>
            <label className="fd-create-field-full">
              Note (optional)
              <textarea
                className="fd-checkout-textarea"
                value={guestRefundNote}
                onChange={(e) => setGuestRefundNote(e.target.value)}
                placeholder="e.g. partial refund agreed with guest…"
                maxLength={5000}
              />
            </label>
            <div className="fd-checkin-actions">
              <button type="button" className="btn-ghost" disabled={loading} onClick={() => setGuestRefundOpen(false)}>
                Cancel
              </button>
              <button type="button" className="btn-primary" disabled={loading} onClick={submitGuestRefund}>
                {loading ? 'Processing…' : 'Submit refund'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {cautionModalBooking ? (
        <div
          className="modal-backdrop fd-checkin-root-overlay"
          role="presentation"
          onClick={() => {
            if (!loading) {
              setCautionModalBooking(null);
              setCautionModalKind('refund');
              setCautionModalAmount('');
              setCautionModalNote('');
            }
          }}
        >
          <div
            className="modal-box fd-checkin-dialog fd-checkin-dialog--standalone"
            role="dialog"
            aria-modal="true"
            aria-labelledby="fd-caution-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="fd-caution-title" className="fd-checkin-title">
              Caution fees
            </h4>
            <p className="muted fd-checkin-lead">
              Booking #{cautionModalBooking.id} · {guestLabel(cautionModalBooking)}. Sub total{' '}
              {formatNgn(getPricingBreakdown(cautionModalBooking).stay_subtotal)}, caution column{' '}
              {formatNgn(getPricingBreakdown(cautionModalBooking).caution_display)}, total{' '}
              {formatNgn(getPricingBreakdown(cautionModalBooking).total)}.
            </p>
            <div className="fd-caution-kind-row">
              <label className="fd-caution-radio">
                <input
                  type="radio"
                  name="fd-caution-kind"
                  checked={cautionModalKind === 'refund'}
                  onChange={() => setCautionModalKind('refund')}
                />
                Refund to guest (reduces total; max remaining pool{' '}
                {formatNgn(getPricingBreakdown(cautionModalBooking).caution_remainder)})
              </label>
              <label className="fd-caution-radio">
                <input
                  type="radio"
                  name="fd-caution-kind"
                  checked={cautionModalKind === 'forfeit'}
                  onChange={() => setCautionModalKind('forfeit')}
                />
                Forfeit / damage charge (retain from pool first; amount can exceed pool to add to total)
              </label>
            </div>
            <label className="fd-create-field-full">
              Amount (₦, whole naira)
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                className="luxistt-money-input"
                value={cautionModalAmount}
                onChange={(e) => setCautionModalAmount(formatWholeNairaInputChange(e.target.value))}
                placeholder={cautionModalKind === 'refund' ? 'Amount to refund' : 'Amount to forfeit / charge'}
              />
            </label>
            <label className="fd-create-field-full">
              Note (optional)
              <textarea
                className="fd-checkout-textarea"
                value={cautionModalNote}
                onChange={(e) => setCautionModalNote(e.target.value)}
                placeholder="e.g. No damage — full caution refunded…"
                maxLength={5000}
              />
            </label>
            <div className="fd-checkin-actions">
              <button
                type="button"
                className="btn-ghost"
                disabled={loading}
                onClick={() => {
                  setCautionModalBooking(null);
                  setCautionModalKind('refund');
                  setCautionModalAmount('');
                  setCautionModalNote('');
                }}
              >
                Cancel
              </button>
              <button type="button" className="btn-primary" disabled={loading} onClick={submitCautionAdjustment}>
                {loading ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {checkOutOpen && checkOutBooking ? (
        <div
          className="modal-backdrop fd-checkout-root-overlay"
          role="presentation"
          onClick={() => {
            if (!loading) {
              setCheckOutOpen(false);
              setCheckOutBooking(null);
              setCheckOutNote('');
            }
          }}
        >
          <div
            className="modal-box fd-checkin-dialog fd-checkin-dialog--standalone"
            role="dialog"
            aria-modal="true"
            aria-labelledby="fd-checkout-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="fd-checkout-title" className="fd-checkin-title">
              Complete check-out
            </h4>
            <p className="muted fd-checkin-lead">
              {guestLabel(checkOutBooking)} · #{checkOutBooking.id} · departure{' '}
              {formatLongDate(checkOutBooking.end_date)}
            </p>
            <p className="muted fd-checkin-lead fd-checkout-lead-tight">
              Confirm below to mark the stay as checked out. If the guest has an account email, we send the thank-you and
              review request after you confirm.
            </p>
            <label className="fd-create-field-full">
              Checkout note (optional, for staff records)
              <textarea
                className="fd-checkout-textarea"
                value={checkOutNote}
                onChange={(e) => setCheckOutNote(e.target.value)}
                placeholder="e.g. key returned, incident notes…"
                maxLength={5000}
              />
            </label>
            <div className="fd-checkin-actions">
              <button
                type="button"
                className="btn-ghost"
                disabled={loading}
                onClick={() => {
                  setCheckOutOpen(false);
                  setCheckOutBooking(null);
                  setCheckOutNote('');
                }}
              >
                Cancel
              </button>
              <button type="button" className="btn-primary" disabled={loading} onClick={completeCheckOut}>
                {loading ? 'Processing…' : 'Confirm check-out'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {createOpen && (
        <div className="modal-backdrop fd-create-backdrop" role="presentation" onClick={closeCreateModal}>
          <div className="modal-box modal-wide fd-create-booking-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h3>New booking</h3>
            <form id="fd-create-form" onSubmit={submitCreate} className="form-grid fd-create-form">
              {error ? (
                <div className="fd-create-warn" role="alert" style={{ gridColumn: '1 / -1' }}>
                  {error}
                  <button
                    type="button"
                    className="btn-ghost"
                    style={{ marginLeft: '0.5rem', fontSize: '0.85rem' }}
                    onClick={() => setError('')}
                  >
                    Dismiss
                  </button>
                </div>
              ) : null}
              {properties.length > 1 ? (
                <label className="fd-create-field fd-create-field-full">
                  Property
                  <select
                    value={createBookingPropertyId}
                    required
                    onChange={(e) => {
                      const v = e.target.value;
                      setCreateBookingPropertyId(v);
                      setCreateForm((prev) => ({ ...prev, apartment_id: '' }));
                    }}
                  >
                    <option value="">Select property…</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {p.city ? ` · ${p.city}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <label className="fd-create-field fd-create-field-full">
                Apartment
                <select
                  value={createForm.apartment_id}
                  required
                  disabled={!createBookingPropertyId}
                  onChange={(e) => setCreateForm({ ...createForm, apartment_id: e.target.value })}
                >
                  <option value="">
                    {!createBookingPropertyId ? 'Select a property first…' : 'Select unit…'}
                  </option>
                  {createApartments.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="fd-create-field">
                Check-in
                <DatePicker
                  selected={fromISODate(createForm.start_date)}
                  onChange={handleCreateStartChange}
                  onKeyDown={(e) => e.preventDefault()}
                  minDate={todayStart}
                  filterDate={canSelectStartCreate}
                  excludeDateIntervals={createUnavailableIntervals}
                  dayClassName={(d) => (isDateBlockedCreate(d) ? 'book-day-blocked' : undefined)}
                  renderDayContents={renderDayContentsCreate}
                  placeholderText="Select check-in"
                  dateFormat="d MMMM yyyy"
                  required
                  className="book-datepicker-input"
                  disabled={!createForm.apartment_id}
                  portalId="luxistt-fd-datepicker-root"
                  popperClassName="fd-create-datepicker-popper"
                  popperPlacement="bottom-start"
                />
                {createForm.start_date ? (
                  <span className="fd-create-date-long">{formatLongDate(createForm.start_date)}</span>
                ) : null}
              </label>
              <label className="fd-create-field">
                Check-out
                <DatePicker
                  selected={fromISODate(createForm.end_date)}
                  onChange={handleCreateEndChange}
                  onKeyDown={(e) => e.preventDefault()}
                  minDate={minCheckoutCreate}
                  filterDate={canSelectEndCreate}
                  excludeDateIntervals={createUnavailableIntervals}
                  dayClassName={(d) => (isDateBlockedCreate(d) ? 'book-day-blocked' : undefined)}
                  renderDayContents={renderDayContentsCreate}
                  placeholderText="Select check-out"
                  dateFormat="d MMMM yyyy"
                  required
                  className="book-datepicker-input"
                  disabled={!createForm.start_date}
                  portalId="luxistt-fd-datepicker-root"
                  popperClassName="fd-create-datepicker-popper"
                  popperPlacement="bottom-start"
                />
                {createForm.end_date ? (
                  <span className="fd-create-date-long">{formatLongDate(createForm.end_date)}</span>
                ) : null}
              </label>
              {createDateWarning || createCheckoutWarning ? (
                <div className="fd-create-warn" role="alert" style={{ gridColumn: '1 / -1' }}>
                  {createDateWarning || createCheckoutWarning}
                </div>
              ) : null}
              {createMandatorySurcharges.length > 0 || createOptionalSurcharges.length > 0 ? (
                <div className="fd-create-field-full fd-create-surcharges" style={{ gridColumn: '1 / -1' }}>
                  {createMandatorySurcharges.length > 0 ? (
                    <div className="party-surcharge-mandatory-block">
                      <p className="party-surcharge-section-title">Included with this stay</p>
                      <ul className="party-surcharge-included-list">
                        {createMandatorySurcharges.map((s) => (
                          <li key={s.id} className="party-surcharge-included-row">
                            <div className="party-surcharge-included-copy">
                              <span className="party-surcharge-main">{s.name}</span>
                              {s.type_name ? <span className="party-surcharge-type">{s.type_name}</span> : null}
                              {s.description ? <span className="party-surcharge-desc">{s.description}</span> : null}
                            </div>
                            <span className="party-surcharge-fee-pill">{formatSurchargeFeeLabel(s)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {createOptionalSurcharges.length > 0 ? (
                    <>
                      {createOptionalSurcharges.length > 1 ? (
                        <p className="party-surcharge-intro">Optional add-ons — select any that apply.</p>
                      ) : null}
                      {createOptionalSurcharges.length === 1 ? (
                        (() => {
                          const s = createOptionalSurcharges[0];
                          const fee = formatSurchargeFeeLabel(s);
                          return (
                            <label className="party-surcharge-row party-surcharge-row--single">
                              <input
                                type="checkbox"
                                className="party-surcharge-checkbox"
                                checked={createSelectedSurchargeIds.includes(s.id)}
                                onChange={() => toggleCreateSurcharge(s.id)}
                              />
                              <span className="party-surcharge-label party-surcharge-label--party-option">
                                <span className="party-surcharge-main">Tick if booking for a party.</span>
                                {fee ? (
                                  <span className="party-surcharge-fee-note party-surcharge-fee-note--party">
                                    Adds an extra <strong>{fee}</strong> to your booking total when ticked.
                                  </span>
                                ) : null}
                              </span>
                            </label>
                          );
                        })()
                      ) : (
                        <ul className="party-surcharge-multi-list">
                          {createOptionalSurcharges.map((s) => (
                            <li key={s.id}>
                              <label className="party-surcharge-row party-surcharge-row--multi">
                                <input
                                  type="checkbox"
                                  className="party-surcharge-checkbox"
                                  checked={createSelectedSurchargeIds.includes(s.id)}
                                  onChange={() => toggleCreateSurcharge(s.id)}
                                />
                                <span className="party-surcharge-label">
                                  <span className="party-surcharge-main">{s.name}</span>
                                  {s.type_name ? (
                                    <span className="party-surcharge-type">{s.type_name}</span>
                                  ) : null}
                                </span>
                                <span className="party-surcharge-fee-pill">{formatSurchargeFeeLabel(s)}</span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : null}
                </div>
              ) : null}
              <label>
                Adults
                <input
                  type="number"
                  min={1}
                  max={createMaxAdultsInput}
                  required
                  value={createForm.guests_adults}
                  onChange={(e) => setCreateForm({ ...createForm, guests_adults: e.target.value })}
                />
              </label>
              <label>
                Children
                <input
                  type="number"
                  min={0}
                  max={createMaxChildrenInput}
                  required
                  value={createForm.guests_children}
                  onChange={(e) => setCreateForm({ ...createForm, guests_children: e.target.value })}
                />
              </label>
              <div className="fd-create-field-full fd-create-guest-hint muted" style={{ gridColumn: '1 / -1' }}>
                Guest account: we match by email (and search as you type). Pick a suggestion to reuse an existing
                guest, or enter email + name to create a new guest user automatically.
              </div>
              <label className="fd-create-field-full">
                Guest name
                <input
                  type="text"
                  autoComplete="name"
                  value={createForm.guest_name}
                  onChange={(e) => setCreateForm({ ...createForm, guest_name: e.target.value })}
                />
              </label>
              <label className="fd-create-guest-email-wrap">
                Email
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={createForm.guest_email}
                  onChange={(e) => {
                    const v = e.target.value;
                    const norm = v.trim().toLowerCase();
                    setCreateForm((prev) => {
                      const next = { ...prev, guest_email: v };
                      if (prev.guest_user_id && guestLinkedEmailNorm && norm !== guestLinkedEmailNorm) {
                        next.guest_user_id = '';
                      }
                      return next;
                    });
                    if (guestLinkedEmailNorm && norm !== guestLinkedEmailNorm) {
                      setGuestLinkedEmailNorm('');
                      setCreateIdProfilePreview(null);
                      setCreateIdFilePreview((blob) => {
                        if (blob) {
                          URL.revokeObjectURL(blob);
                        }
                        return null;
                      });
                      setCreateGuestIdFile(null);
                    }
                  }}
                />
                {guestSuggestions.length > 0 ? (
                  <ul className="fd-guest-suggestions" role="listbox">
                    {guestSuggestions.map((g) => (
                      <li key={g.id}>
                        <button
                          type="button"
                          className="fd-guest-suggestion-row"
                          onClick={() => selectGuestSuggestion(g)}
                        >
                          <span className="fd-guest-suggestion-name">{g.name}</span>
                          <span className="fd-guest-suggestion-meta muted">
                            {g.email}
                            {g.phone_number ? ` · ${g.phone_number}` : ''}
                            {g.government_id_document ? ' · ID on file' : ''}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </label>
              <label>
                Phone
                <input
                  type="tel"
                  autoComplete="tel"
                  value={createForm.guest_phone}
                  onChange={(e) => setCreateForm({ ...createForm, guest_phone: e.target.value })}
                />
              </label>
              <label className="fd-create-field-full">
                Government ID / passport (optional)
                <input
                  type="text"
                  autoComplete="off"
                  value={createForm.guest_id_number}
                  onChange={(e) => setCreateForm({ ...createForm, guest_id_number: e.target.value })}
                  maxLength={128}
                />
              </label>
              <label>
                Booking status
                <select
                  value={createForm.status}
                  onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })}
                  aria-label="Booking status"
                >
                  {CREATE_BOOKING_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Payment status
                <select
                  value={createForm.payment_status}
                  onChange={(e) => setCreateForm({ ...createForm, payment_status: e.target.value })}
                  aria-label="Payment status"
                >
                  {CREATE_PAYMENT_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="fd-create-field-full">
                Platform
                <select
                  value={createForm.booking_platform_id}
                  onChange={(e) => setCreateForm({ ...createForm, booking_platform_id: e.target.value })}
                  aria-label="Booking platform or channel"
                >
                  {bookingPlatforms.length === 0 ? (
                    <option value="">Loading platforms…</option>
                  ) : (
                    bookingPlatforms.map((p) => (
                      <option key={p.id} value={String(p.id)}>{p.name}</option>
                    ))
                  )}
                </select>
              </label>
              <label className="fd-create-field-full">
                ID photo (optional)
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  className="fd-booking-upload-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setCreateGuestIdFile(f);
                    setCreateIdProfilePreview(null);
                    setCreateIdFilePreview((prevBlob) => {
                      if (prevBlob) {
                        URL.revokeObjectURL(prevBlob);
                      }
                      return f ? URL.createObjectURL(f) : null;
                    });
                  }}
                />
                {createGuestIdFile ? <span className="muted">{createGuestIdFile.name}</span> : null}
                {createIdFilePreview || createIdProfilePreview ? (
                  <div className="fd-create-id-preview-wrap">
                    <img
                      src={createIdFilePreview || createIdProfilePreview}
                      alt=""
                      className="fd-gov-id-preview fd-create-id-preview"
                    />
                    <span className="muted fd-create-id-preview-caption">
                      {createIdFilePreview ? 'New upload' : 'From guest profile'}
                    </span>
                  </div>
                ) : null}
              </label>
              <label className="fd-create-field-full">
                Payment method
                <select
                  value={createForm.payment_method}
                  onChange={(e) => setCreateForm({ ...createForm, payment_method: e.target.value })}
                >
                  <option value="cash">Cash</option>
                  <option value="paystack">Paystack (online)</option>
                  <option value="bank_transfer">Bank transfer</option>
                  <option value="pos">POS / card terminal</option>
                </select>
              </label>
              <label className="fd-create-field-full">
                Discount (₦) — optional override
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="luxistt-money-input"
                  placeholder={
                    createPriceQuote?.discount_amount != null && Number(createPriceQuote.discount_amount) > 0
                      ? `Calculated: ₦${Number(createPriceQuote.discount_amount).toLocaleString()}`
                      : 'Leave blank to use calculated discount from rules'
                  }
                  value={createForm.discount_amount_manual}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      discount_amount_manual: formatWholeNairaInputChange(e.target.value),
                    })
                  }
                />
              </label>
              {createDisplaySurchargeLines.length > 0 ? (
                <div className="fd-create-field-full">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                    <p className="muted" style={{ margin: 0, flex: '1 1 auto' }}>
                      Surcharge amount overrides (optional)
                    </p>
                    <button
                      type="button"
                      className="btn-ghost"
                      style={{ fontSize: '0.85rem' }}
                      onClick={() => setCreateSurchargeOverrides({})}
                    >
                      Reset all surcharge overrides
                    </button>
                  </div>
                  <div className="fd-create-total-meta">
                    {createDisplaySurchargeLines.map((line) => {
                      const baselineRow = (
                        Array.isArray(createPriceQuote?.surcharge_lines) ? createPriceQuote.surcharge_lines : []
                      ).find((x) => Number(x.id) === Number(line.id));
                      const baseline = Number(baselineRow?.amount ?? line.amount ?? 0);
                      return (
                        <div
                          key={line.id}
                          className="fd-create-field-full"
                          style={{
                            marginBottom: '0.45rem',
                            display: 'flex',
                            gap: '0.5rem',
                            alignItems: 'flex-end',
                            flexWrap: 'wrap',
                          }}
                        >
                          <label style={{ flex: '1 1 180px', marginBottom: 0 }}>
                            {line.name} (₦)
                            <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              className="luxistt-money-input"
                              placeholder={`Calculated: ₦${baseline.toLocaleString()}`}
                              value={createSurchargeOverrides[String(line.id)] || ''}
                              onChange={(e) =>
                                setCreateSurchargeOverrides((prev) => ({
                                  ...prev,
                                  [String(line.id)]: formatWholeNairaInputChange(e.target.value),
                                }))
                              }
                            />
                          </label>
                          <button
                            type="button"
                            className="btn-ghost"
                            style={{ fontSize: '0.85rem', marginBottom: '0.15rem' }}
                            onClick={() =>
                              setCreateSurchargeOverrides((prev) => {
                                const next = { ...prev };
                                delete next[String(line.id)];
                                return next;
                              })
                            }
                          >
                            Reset line
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              {createOverridePreviewInvalid && createPricingOverridePreview?.message ? (
                <p className="muted fd-create-field-full" role="alert">
                  {createPricingOverridePreview.message}
                </p>
              ) : null}
              <label className="fd-create-field-full">
                Total amount (₦) — optional override
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="luxistt-money-input"
                  placeholder="Leave blank to use calculated total from rates"
                  value={createForm.total_price_manual}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      total_price_manual: formatWholeNairaInputChange(e.target.value),
                    })
                  }
                />
              </label>
              <div className="fd-create-total fd-create-field-full">
                <strong>Total amount to receive</strong>
                {createDisplayTotal > 0 ? (
                  <div className="fd-create-total-amount">
                    {createHasDiscount && createDisplaySubtotal != null ? (
                      <>
                        <span className="book-total-strike">₦{createDisplaySubtotal.toLocaleString()}</span>{' '}
                        <span className="fd-create-total-final">₦{createDisplayTotal.toLocaleString()}</span>
                        {createAppliedDiscountLabel ? (
                          <span className="muted"> ({createAppliedDiscountLabel})</span>
                        ) : null}
                      </>
                    ) : (
                      <span className="fd-create-total-final">₦{createDisplayTotal.toLocaleString()}</span>
                    )}
                    {createDisplaySurchargeLines.length > 0 ? (
                      <div className="muted fd-create-total-meta" style={{ marginTop: '0.35rem' }}>
                        {createDisplaySurchargeLines.map((line) => (
                          <div key={line.id}>
                            {line.name}: ₦{Number(line.amount).toLocaleString()}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {createNightCount > 0 && !createForm.total_price_manual ? (
                      <div className="muted fd-create-total-meta">
                        {createNightCount} night{createNightCount === 1 ? '' : 's'} · from configured nightly rates
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <p className="muted">Select unit and dates to calculate total.</p>
                )}
              </div>
              <label className="fd-create-field-full">
                Notes
                <input
                  type="text"
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                />
              </label>
              <p className="muted fd-create-field-full">
                {properties.length > 1
                  ? 'Choose a property in this form to load units (the toolbar filter is separate). Unavailable nights are marked in the calendar.'
                  : 'Unavailable nights are marked in the calendar.'}
              </p>
            </form>
            <div className="fd-create-footer">
              <button
                type="submit"
                form="fd-create-form"
                className="btn-primary"
                disabled={
                  loading || !!createDateWarning || !!createCheckoutWarning || createOverridePreviewInvalid
                }
              >
                Create
              </button>
              <button type="button" className="btn-ghost" onClick={closeCreateModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
